import { blockFirestoreForTradesPage } from '../../lib/trades/blockFirestoreForTradesPage';
blockFirestoreForTradesPage();
import { useState, useEffect, useRef, useMemo, useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    type DragStartEvent,
    type DragMoveEvent,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Papa from 'papaparse';
import { Listbox } from '@headlessui/react';
import { createPortal } from 'react-dom';
import CreateCustomEntryModal from '../ui/CreateCustomEntryModal';
import type { ManualEntryPayload } from '../ui/CreateCustomEntryModal';
import StandardPageHeader from './StandardPageHeader';
import TableColumnsManager from '../ui/TableColumnsManager';
import RemovableTag from '../ui/RemovableTag';
import { getDemoTradeDisplayId, getVisibleTradeTags, renameTradeTag } from '../../lib/tradeTags';
export { getDemoTradeDisplayId, getVisibleTradeTags } from '../../lib/tradeTags';
import { ChevronDown, X, Settings, RefreshCcw, Cloud, Info, Plus, ArrowRight, AlertCircle, Tag, MessageSquarePlus, ArrowUpRight, ArrowDownRight, Check, XCircle, Layers, Trash2, Wallet } from 'lucide-react';
import type { Trade } from '../../types';
import { fmtMoney, formatNyDateTime } from '../../utils/formatters';
import { formatDateTimeForMode, type ChartTzMode } from '../../lib/chart/displayOptions';
import { loadTradesTimeMode, saveTradesTimeMode } from '../../lib/time/tradesTimeMode';
// time‑mode helpers were previously imported here but are no longer used
import { filterTradesForSelectedTradingAccount, saveImportedTrades, IMPORTED_TRADES_UPDATED_EVENT, loadImportedTrades } from '../../utils/importedTradesStorage';
import { privateTradingScopeChangedEventName, readPrivateTradingJson, readPrivateTradingValue, removePrivateTradingValue, writePrivateTradingJson, writePrivateTradingValue } from '../../lib/storage/privateTradingStorage';
import {
    collectExecutionDedupeKeys as sharedCollectExecutionDedupeKeys,
    collectExistingExecutionKeys,
    filterDuplicateExecutionsFromImportedTrade,
} from '../../lib/import-engine/csvExecutionDedupe';
import { applyImportOverlapDiagnostics, buildCsvImportSummary, buildCsvRejectionDiagnostics, formatCsvImportSummary, formatCsvImportToast, getTradeSourceRowOrdinals, isGenuineCsvIssueDiagnostic, reconcileCsvImportSummaryCounts } from '../../lib/import-engine/csvRejectionDiagnostics';
import type { CsvImportSummary } from '../../lib/import-engine/csvRejectionDiagnostics';
import { buildCsvImportAuthHeaders, buildCsvImportPersistRequest, buildCsvSourceAccountResolutionInput, isCsvImportVerificationRetryable, listCsvSourceAccounts, persistCsvImportAfterLocalSave, requireResolvedCsvSourceAccount, resolveCsvSourceAccount, retryCsvImportVerification, verifyPersistedCsvImport } from '../../lib/import-engine/csvImportPersistenceSync';
import type { CsvSourceAccountOption } from '../../lib/import-engine/csvImportPersistenceSync';
import { createManualTrade, deleteSelectedPersistedTrades } from '../../lib/trades/manualTradeApi';
import { brokerOptions } from '../../lib/import-engine/csvBrokers';
import { allocateVisibleTradeIds } from '../../lib/trades/visibleTradeIds';
import { compareTradeIdValues } from './tradeIdSort';
import { TopFilters, type SideFilterValue, type TimeframeValue } from '../filters/TopFilters';
import { emptyJournalFilters, type JournalFilters } from '../filters/JournalFiltersDropdown';
import { matchesTradeSearch, matchesTimeframeFilter, getTradeTimestampMs } from './tradesFilters';
import ActiveFilterChips from './ActiveFilterChips';
import { computeInitialRiskFromStop, type InitialRiskMethod, type StopRiskAssetClass } from './initialRisk';
import { readTradingSettings } from '../../config/tradingSettingsDefaults';
import { loadTrades } from '../../lib/trades/tradeRepository';
import { isIndexOrCfdInstrument } from '../../constants/indexInstruments';
import {
    getPersistedTradeHistoryStatus,
    subscribePersistedTradeHistoryStatus,
    PERSISTED_TRADE_HISTORY_RETRY_EVENT,
} from '../../lib/import-engine/persistedCsvTradeRead';
import { CUSTOM_TRADES_UPDATED_EVENT, getTradePnl, loadCustomTrades, resolveTradeRealizedPnl, saveCustomTrades } from '../../lib/trades/authoritativeTradeData';
import { filterCanonicalDisplayTrades } from '../../lib/trades/dashboardTradeSource';
import { normalizeTimestampToMs } from '../../lib/time/normalizeTimestampToMs';
import { resolveCsvSourceTimezone } from '../../lib/time/sourceTimestamp';
import { formatQuantityDisplayValue, toDisplayForexLots } from '../../lib/forex/quantity';
import { calculateFifoGrossPnl } from '../../lib/trades/fifoPnl';
import { hasManualEntryTrade, isManualEntryTrade } from '../../lib/trades/manualEntrySource';
import { fetchCanonicalTradeExcursionSummary, readPersistedTradePositionExcursionValue, type TradeExcursionSummary } from '../../lib/trades/tradeExcursions';
import { normalizeTradeAssetClassification } from '../../lib/trades/canonicalAssetClass';
import { buildCsvImportToastDetail } from '../../lib/import-engine/csvImportToast';
import { getContractMultiplier, resolveFuturesTickSize, computeDirectionalTicks, extractFuturesRoot, CME_FUTURES_MULTIPLIERS } from '../../constants/contractMultipliers';
import { resolveTradeInitialRiskValue } from '../../lib/trades/tradeInitialRisk';
import { useDemoModeState, useDemoTradesOverride } from '../../demo/demoMode';
import { getCurrentSession } from '../../lib/auth.ts';
import { applyTenantRequestHeaders } from '../../lib/tenantRequestContext';
import { coerceMarketTypeToVisible, isAssetClassEnabled, useAssetClassVisibility } from '../../lib/assetClassVisibility';
import { isTradeWithinTenantDisplayScope } from '../../lib/trades/tenantTradeScope';
import ClearImportedTradesDialog from './ClearImportedTradesDialog';
import { isImportedTradeForAccount } from '../../utils/importedTradesStorage';
import { removeTagFromTrades, shouldRefreshTradesForImportedUpdate, TRADES_PAGE_DELETE_MUTATION_SOURCE, TRADES_PAGE_TAG_MUTATION_SOURCE } from '../../lib/trades/tradeTagMutation';
import { resolveSelectedTradingAccountId, shouldShowTradingAccountSelector } from '../../lib/trades/tradingAccountSelector';
import { useDisplayTimezone } from '../app/DisplayTimezoneProvider';

export function fitTradeTableColumnsToWidth<K extends string>(args: {
    visibleKeys: K[];
    widthByKey: Partial<Record<K, number>>;
    availableWidth: number | null;
    hidePriority: K[];
}): { visibleKeys: K[]; hiddenKeys: K[]; fits: boolean } {
    const { visibleKeys, widthByKey, availableWidth, hidePriority } = args;
    if (!Number.isFinite(availableWidth) || Number(availableWidth) <= 0) {
        return { visibleKeys: [...visibleKeys], hiddenKeys: [], fits: true };
    }

    const widthFor = (key: K) => Math.max(0, Number(widthByKey[key]) || 0);
    let totalWidth = visibleKeys.reduce((sum, key) => sum + widthFor(key), 0);
    const hidden = new Set<K>();
    const candidates = [
        ...hidePriority,
        ...visibleKeys.slice().reverse(),
    ].filter((key, index, list) => list.indexOf(key) === index);

    for (const key of candidates) {
        if (totalWidth <= Number(availableWidth)) break;
        if (!visibleKeys.includes(key) || hidden.has(key)) continue;
        hidden.add(key);
        totalWidth -= widthFor(key);
    }

    return {
        visibleKeys: visibleKeys.filter((key) => !hidden.has(key)),
        hiddenKeys: visibleKeys.filter((key) => hidden.has(key)),
        fits: totalWidth <= Number(availableWidth),
    };
}

export function shrinkColumnWidthsToFit<K extends string>(args: {
    visibleKeys: K[];
    requestedWidthByKey: Partial<Record<K, number>>;
    minimumWidthByKey: Partial<Record<K, number>>;
    availableWidth: number | null;
}): { widths: Partial<Record<K, number>>; total: number; fits: boolean } {
    const { visibleKeys, requestedWidthByKey, minimumWidthByKey, availableWidth } = args;
    const widths: Partial<Record<K, number>> = {};
    const minimums: Partial<Record<K, number>> = {};

    for (const key of visibleKeys) {
        const minimum = Math.max(0, Math.round(Number(minimumWidthByKey[key]) || 0));
        minimums[key] = minimum;
        widths[key] = Math.max(minimum, Math.round(Number(requestedWidthByKey[key]) || minimum));
    }

    let total = visibleKeys.reduce((sum, key) => sum + (widths[key] ?? 0), 0);
    if (!Number.isFinite(availableWidth) || Number(availableWidth) <= 0 || total <= Number(availableWidth)) {
        return { widths, total, fits: true };
    }

    const target = Math.max(0, Math.round(Number(availableWidth)));
    const reducibleTotal = visibleKeys.reduce(
        (sum, key) => sum + Math.max(0, (widths[key] ?? 0) - (minimums[key] ?? 0)),
        0,
    );
    const deficit = total - target;

    if (reducibleTotal > 0) {
        const reductionRatio = Math.min(1, deficit / reducibleTotal);
        for (const key of visibleKeys) {
            const current = widths[key] ?? 0;
            const minimum = minimums[key] ?? 0;
            const reducible = Math.max(0, current - minimum);
            widths[key] = Math.max(minimum, current - Math.floor(reducible * reductionRatio));
        }

        total = visibleKeys.reduce((sum, key) => sum + (widths[key] ?? 0), 0);
        for (let index = visibleKeys.length - 1; index >= 0 && total > target; index -= 1) {
            const key = visibleKeys[index];
            const minimum = minimums[key] ?? 0;
            const current = widths[key] ?? minimum;
            const reduction = Math.min(current - minimum, total - target);
            if (reduction <= 0) continue;
            widths[key] = current - reduction;
            total -= reduction;
        }
    }

    return { widths, total, fits: total <= target };
}

// Small helper to render a value or a dash when empty.
const RenderOrDash = ({ children, className }: { children?: React.ReactNode; className?: string }) => {
    const val = children;
    const empty = val === null || val === undefined || (typeof val === 'string' && val.trim() === '');
    if (empty) return <span className={className ?? 'text-black dark:text-zinc-200'}>—</span>;
    if (typeof val === 'string' || typeof val === 'number') return <span className={className ?? 'text-black dark:text-zinc-200'}>{val}</span>;
    return val;
};

// Stable, deterministic string -> positive integer hash used for generating fallback ids
function hashStringToPositiveInt(s: string): number {
    let h = 5381;
    for (let i = 0; i < s.length; i++) {
        h = (h * 33) ^ s.charCodeAt(i);
    }
    // Convert to unsigned 32-bit and ensure positive non-zero
    const v = (h >>> 0) || 1;
    return v;
}

function getTradeStorageKey(t: any): string {
    return String(t?.storageId ?? t?.id ?? `${t?.symbol}-${t?.timestamp}`);
}

export function isDemoTradeRecord(trade: any): boolean {
    if (!trade || typeof trade !== 'object') return false;
    const source = String(trade?.sourceType ?? trade?.source ?? '').trim().toLowerCase();
    return trade?.isDemo === true || source === 'demo' || String(trade?.tradeId ?? '').trim().toLowerCase().startsWith('demo_');
}

export function getTradesFromDemoOverride(demoTradesOverride: { enabled?: boolean; trades?: Record<string, any>[] } | null | undefined): Trade[] {
    if (!demoTradesOverride?.enabled || !Array.isArray(demoTradesOverride.trades) || demoTradesOverride.trades.length === 0) return [];
    return demoTradesOverride.trades as Trade[];
}

const TRADES_TABLE_LAYOUT_PREFERENCE_KEY = 'trades-table-layout-v1';

type PersistedTradesTableLayout = {
    pageSize?: number;
    colWidths?: Record<string, number>;
};

function getApiBase(): string {
    const envBase = String((import.meta as any).env?.VITE_API_BASE || '').trim();
    return envBase.replace(/\/+$/, '');
}

function buildApiUrl(path: string): string {
    const base = getApiBase();
    return base ? `${base}${path}` : path;
}

async function buildPreferenceAuthHeaders(): Promise<Record<string, string> | null> {
    const session = await getCurrentSession();
    const userId = String(session?.user?.id || '').trim();
    if (!userId) return null;

    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-user-id': userId,
    };

    const accessToken = String(session?.access_token || '').trim();
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    return applyTenantRequestHeaders(headers);
}

async function fetchTradesTableLayoutPreference(): Promise<PersistedTradesTableLayout | null> {
    const headers = await buildPreferenceAuthHeaders();
    if (!headers) return null;

    const response = await fetch(buildApiUrl(`/api/user-preferences/${TRADES_TABLE_LAYOUT_PREFERENCE_KEY}`), {
        method: 'GET',
        headers,
    });
    if (!response.ok) return null;

    const data = await response.json().catch(() => null);
    const value = data?.value;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as PersistedTradesTableLayout;
}

async function saveTradesTableLayoutPreference(layout: PersistedTradesTableLayout): Promise<void> {
    const headers = await buildPreferenceAuthHeaders();
    if (!headers) return;

    await fetch(buildApiUrl(`/api/user-preferences/${TRADES_TABLE_LAYOUT_PREFERENCE_KEY}`), {
        method: 'PUT',
        headers,
        body: JSON.stringify(layout),
    }).catch(() => undefined);
}

function hasTechnicalCsvTradeId(trade: Trade): boolean {
    const raw =
        (trade as any).tradeId ??
        (trade as any).storageId ??
        (trade as any).id;
    return String(raw ?? '').trim().toLowerCase().startsWith('csv_trade:');
}

function computeColumnDropIndicatorLeft(args: {
    scroller: HTMLDivElement | null;
    overHeader: HTMLTableHeaderCellElement | null | undefined;
}): number | null {
    const { scroller, overHeader } = args;
    if (!scroller || !overHeader) return null;
    const sRect = scroller.getBoundingClientRect();
    const oRect = overHeader.getBoundingClientRect();
    return Math.round(oRect.left - sRect.left + scroller.scrollLeft);
}

function toFiniteNumber(value: unknown): number | null {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function toDateValue(value: unknown): Date | null {
    if (value == null || value === '') return null;
    if (value instanceof Date) {
        return Number.isFinite(value.getTime()) ? value : null;
    }

    const direct = new Date(value as any);
    if (Number.isFinite(direct.getTime())) return direct;

    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
        const normalized = numeric > 1e12 ? numeric : numeric * 1000;
        const fromNumeric = new Date(normalized);
        if (Number.isFinite(fromNumeric.getTime())) return fromNumeric;
    }

    return null;
}

function parseTags(value: unknown): string[] {
    if (Array.isArray(value)) {
        return value
            .map((v) => String(v ?? '').trim())
            .filter(Boolean);
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return [];
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed
                    .map((v) => String(v ?? '').trim())
                    .filter(Boolean);
            }
        } catch {}
        return trimmed
            .split(/[|,]/)
            .map((part) => part.trim())
            .filter(Boolean);
    }
    return [];
}

function getExecutionTotalCosts(input: any): number {
    const explicit = toFiniteNumber(input?.commissionAndFees ?? input?.commissionsAndFees);
    if (explicit != null) return explicit;
    return (toFiniteNumber(input?.commission) ?? 0) + (toFiniteNumber(input?.fees ?? input?.fee) ?? 0);
}

function parseOptionalTradePnl(value: unknown): number | null {
    const n = toFiniteNumber(value);
    return n != null ? n : null;
}

function getSummaryTradeGrossPnl(trade: any, resolvedSide?: 'buy' | 'sell'): number | null {
    const direct = toFiniteNumber(
        trade?.grossPnl
        ?? trade?.gross_pnl
        ?? trade?.realizedGrossPnl
        ?? trade?.realized_gross_pnl
    );
    if (direct != null) return direct;

    const entryPrice = toFiniteNumber(trade?.entryPrice ?? trade?.entry_price);
    const exitPrice = toFiniteNumber(trade?.exitPrice ?? trade?.exit_price);
    const qty = Math.abs(toFiniteNumber(trade?.qty ?? trade?.quantity ?? trade?.volume ?? trade?.shares) ?? 0);
    if (entryPrice == null || exitPrice == null || qty <= 0) return null;

    const side = resolvedSide ?? (String(trade?.side ?? '').toLowerCase().includes('short') ? 'sell' : 'buy');
    const perUnit = side === 'sell' ? (entryPrice - exitPrice) : (exitPrice - entryPrice);
    return perUnit * qty;
}

// Detect whether a trade should be considered "open-like" (used in multiple renderers)
function tradeHasCloseTime(t: any): boolean {
  // consider many possible close/exit timestamp fields and parse them via toDateValue
  // only treat parsed dates as valid close-times when they look realistic (post-2000)
  const MIN_VALID_CLOSE_MS = new Date(2000, 0, 1).getTime(); // 2000-01-01
  const candidates = [
    t.closeDate, t.close_time, t.closeTime, t.close_at, t.closeAt, t.closeTimestamp, t.close_ts,
    t.exitTime, t.exit_time, t.exit_at, t.exitAt, t.exitTimestamp, t.exit_ts, t.closedAt,
  ];
  for (const c of candidates) {
    try {
      const parsed = toDateValue(c);
      if (parsed && parsed.getTime() >= MIN_VALID_CLOSE_MS) return true;
    } catch {}
  }
  return false;
}

export function isOpenLikeTrade(t: any, importedTradeLookup?: Map<string, any>): boolean {
  const status = String(t?.status ?? '').toLowerCase();
  if (status === 'open') return true;

  // if exitPrice is missing/null/empty, treat as open-like as a fallback
  if (t?.exitPrice == null || t?.exitPrice === '') return true;

  // if there is no close/exit timestamp information on the provided trade, consider it open-like
  if (!tradeHasCloseTime(t)) return true;

  // fallback: check authoritative imported-trades storage (match by storageId)
  try {
    const sid = String(t?.storageId ?? t?.storageID ?? '');
    if (sid) {
            const imported = importedTradeLookup
                ? importedTradeLookup.get(sid)
                : loadImportedTrades().find((r: any) => String(r.storageId) === sid);
      if (imported) {
        if (String(imported.status ?? '').toLowerCase() === 'open') return true;
        if (imported?.exitPrice == null || imported?.exitPrice === '') return true;
        if (!tradeHasCloseTime(imported)) return true;
      }
    }
  } catch {}

  return false;
}

type GroupedTrade = Trade & {
    executionsList: Trade[];
    executionCount: number;
    executions: number;
    sourceIds: number[];
    openTime: number;
    closeTime?: number | null;
    openTimestampMs: number;
    closeTimestampMs?: number | null;
    status: 'open' | 'closed';
    timestamp?: number;
    commission?: number;
    fees?: number;
    calculatedPnl?: number | null;
    brokerReportedPnl?: number | null;
    pnlDifference?: number | null;
};

type MarketCategory = 'STOCKS' | 'FUTURES' | 'FOREX' | 'CRYPTO';

function normalizeMarketCategory(raw: unknown): MarketCategory | undefined {
    const v = String(raw ?? '').trim().toUpperCase();
    if (!v) return undefined;
    if (['STOCK', 'STOCKS', 'EQUITY', 'EQUITIES', 'STK', 'INDEX_CFD', 'CASH_INDEX', 'INDEX', 'INDICES'].includes(v)) return 'STOCKS';
    if (['FUTURE', 'FUTURES', 'FUT', 'DERIVATIVE', 'DERIVATIVES'].includes(v)) return 'FUTURES';
    if (['FOREX', 'FX', 'CURRENCY', 'CURRENCIES'].includes(v)) return 'FOREX';
    if (['CRYPTO', 'CRYPTOCURRENCY', 'CRYPTOCURRENCIES', 'DIGITAL_ASSET', 'DIGITAL_ASSETS'].includes(v)) return 'CRYPTO';
    return undefined;
}

function detectMarketCategory(input: { symbol?: unknown; market?: unknown; marketType?: unknown; assetClass?: unknown; category?: unknown; instrumentType?: unknown; instrument?: unknown; securityType?: unknown }): MarketCategory {
    const sym = String(input.symbol || '').trim().toUpperCase();
    const compact = sym.replace(/[\s._-]/g, '');
    const noVenue = compact.includes(':') ? compact.split(':').pop() || compact : compact;
    const isFxPair = /^[A-Z]{3}\/[A-Z]{3}$/.test(noVenue);
    const slashParts = noVenue.split('/').filter(Boolean);
    const normalizedContract = !isFxPair && slashParts.length > 0 ? slashParts[0] : noVenue;
    const futuresRoots = new Set(['ES', 'MES', 'NQ', 'MNQ', 'RTY', 'M2K', 'YM', 'MYM', 'CL', 'GC', 'SI', 'HG', 'NG', 'ZB', 'ZN', 'ZF', 'ZT', '6A', '6B', '6C', '6E', '6J', '6N', '6S']);
    const cryptoRoots = new Set(['BTC', 'ETH', 'LTC', 'XRP', 'DOGE', 'SOL', 'ADA', 'BNB']);
    const fxCodes = new Set(['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'SEK', 'NOK', 'DKK', 'HKD', 'SGD', 'CNH', 'CNY', 'MXN', 'ZAR', 'TRY', 'PLN', 'HUF', 'CZK', 'ILS', 'AED', 'SAR']);

    const explicitStrong =
        normalizeMarketCategory(input.assetClass) ??
        normalizeMarketCategory(input.category) ??
        normalizeMarketCategory(input.instrumentType) ??
        normalizeMarketCategory(input.instrument) ??
        normalizeMarketCategory(input.securityType);

    if (sym) {
        // Symbol-driven classification takes precedence to avoid stale persisted labels.
        if (sym.includes(':')) {
            if (/CME|NYMEX|CBOT|COMEX/i.test(sym)) return 'FUTURES';
            if (/NASDAQ|NYSE|AMEX/i.test(sym)) return 'STOCKS';
        }
        if (futuresRoots.has(normalizedContract)) return 'FUTURES';
        if (/^(ES|MES|NQ|MNQ|RTY|M2K|YM|MYM|CL|GC|SI|HG|NG|ZB|ZN|ZF|ZT|6A|6B|6C|6E|6J|6N|6S)[FGHJKMNQUVXZ]\d{1,2}$/.test(normalizedContract)) return 'FUTURES';
        if (/^[A-Z]{1,6}[FGHJKMNQUVXZ]\d{1,4}$/.test(normalizedContract)) return 'FUTURES';
        if (/^\d[A-Z]([FGHJKMNQUVXZ]\d{1,2})?$/.test(normalizedContract)) return 'FUTURES';
        if (isIndexOrCfdInstrument(sym, input.instrumentType ?? input.instrument, input as any)) return 'STOCKS';
        if (cryptoRoots.has(normalizedContract) || /^(BTC|ETH|LTC|XRP|DOGE|SOL|ADA|BNB)\b/.test(normalizedContract)) return 'CRYPTO';
        if (isFxPair) {
            // If either side of the slash is a known crypto root, prefer CRYPTO
            const parts = noVenue.split('/').filter(Boolean);
            const baseCode = parts[0] ?? '';
            const quoteCode = parts[1] ?? '';
            if (cryptoRoots.has(baseCode) || cryptoRoots.has(quoteCode)) return 'CRYPTO';
            if (explicitStrong && explicitStrong !== 'FOREX') return explicitStrong;
            return 'FOREX';
        }
        if (/^[A-Z]{6}$/.test(normalizedContract)) {
            const base = normalizedContract.slice(0, 3);
            const quote = normalizedContract.slice(3);
            if (cryptoRoots.has(base) || cryptoRoots.has(quote)) return 'CRYPTO';
            if (fxCodes.has(base) && fxCodes.has(quote)) return 'FOREX';
            if (quote === 'USD') return 'FOREX';
        }
    }
    if (explicitStrong) return explicitStrong;

    if (!sym) return 'STOCKS';

    const explicitWeak =
        normalizeMarketCategory(input.marketType) ??
        normalizeMarketCategory(input.market);
    if (explicitWeak) return explicitWeak;
    return 'STOCKS';
}

function getTradeMarketCategory(trade: any): MarketCategory {
    return detectMarketCategory({
        symbol: trade?.symbol,
        market: trade?.market,
        marketType: trade?.marketType,
        assetClass: trade?.assetClass,
        category: trade?.category,
        instrumentType: trade?.instrumentType,
        instrument: trade?.instrument,
        securityType: trade?.securityType,
    });
}

function getPricePrecisionForMarket(category: MarketCategory): number {
    if (category === 'FOREX') return 5;
    return 2;
}

export function formatTradePriceValue(value: unknown, trade: any): string {
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value ?? '');
    const precision = getPricePrecisionForMarket(getTradeMarketCategory(trade));
    return `$${num.toFixed(precision)}`;
}

let lastKnownTradesSnapshot: Trade[] = [];

function getInitialTradesSnapshot(): Trade[] {
    if (Array.isArray(lastKnownTradesSnapshot) && lastKnownTradesSnapshot.length > 0) {
        return lastKnownTradesSnapshot;
    }
    return loadImportedTrades();
}

function useGroupedTrades(trades: Trade[]): GroupedTrade[] {
    return useMemo(() => {
        if (!Array.isArray(trades) || trades.length === 0) return [];
        const MIN_VALID_TS_MS = 946684800000; // 2000-01-01T00:00:00.000Z
        const TSMIX_DEBUG_ID = 'TSMIX-1';
        const shouldDebugTsMix = (row: any): boolean => {
            const ids = [
                row?.tradeId,
                row?.orderId,
                row?.originalId,
                row?.id,
            ].map((v) => String(v ?? '').trim().toUpperCase());
            if (ids.includes(TSMIX_DEBUG_ID)) return true;
            if (Array.isArray(row?.sourceOrderIds)) {
                return row.sourceOrderIds.some((v: unknown) => String(v ?? '').trim().toUpperCase() === TSMIX_DEBUG_ID);
            }
            return false;
        };

        const getExecutionId = (t: Trade, index: number): number => {
            const raw = (t as any).id ?? (t as any).tradeId ?? `${index}`;
            const num = Number(raw);
            return Number.isFinite(num) ? num : hashStringToPositiveInt(String(raw));
        };

        const getExecutionSide = (t: Trade): 'buy' | 'sell' | undefined => {
            const raw = String(
                (t as any).side ??
                (t as any).sideCode ??
                (t as any).side_code ??
                (t as any).orderSide ??
                (t as any).order_side ??
                (t as any)['b/s'] ??
                (t as any).bs ??
                (t as any).direction ??
                (t as any).action ??
                (t as any).type ??
                (t as any).position ??
                (t as any).positionType ??
                (t as any).position_type ??
                (t as any).positionSide ??
                (t as any).position_side ??
                (t as any).tradeSide ??
                (t as any).trade_side ??
                (t as any).buySell ??
                (t as any).buy_sell ??
                (t as any).transactionType ??
                (t as any).transaction_type ??
                ''
            ).trim().toLowerCase();
            const numeric = Number(raw);
            if (Number.isFinite(numeric) && numeric !== 0) return numeric < 0 ? 'sell' : 'buy';
            if (raw === '-' || raw === '--') return 'sell';
            if (raw === '+' || raw === '++') return 'buy';
            const compact = raw.replace(/[\s_-]+/g, '');
            if (['s', 'sl', 'sld', 'sell', 'short', 'sellshort', 'shortsell'].includes(compact)) return 'sell';
            if (['b', 'bot', 'buy', 'long', 'buytocover', 'cover', 'buycover'].includes(compact)) return 'buy';
            if (raw.includes('sell') || raw.includes('short')) return 'sell';
            if (raw.includes('buy') || raw.includes('long')) return 'buy';
            const qty = Number((t as any).qty ?? (t as any).quantity ?? (t as any).volume ?? (t as any).shares ?? 0);
            if (Number.isFinite(qty) && qty !== 0) return qty < 0 ? 'sell' : 'buy';

            const entry = Number((t as any).entryPrice);
            const exit = Number((t as any).exitPrice);
            const pnl = Number((t as any).pnl);
            if (Number.isFinite(entry) && Number.isFinite(exit) && entry !== exit && Number.isFinite(pnl) && pnl !== 0) {
                if ((pnl > 0 && exit < entry) || (pnl < 0 && exit > entry)) return 'sell';
                if ((pnl > 0 && exit > entry) || (pnl < 0 && exit < entry)) return 'buy';
            }
            return undefined;
        };

        const isLikelyPreGrouped = trades.length > 0 && trades.every((t) => {
            const hasExecutionShape =
                Array.isArray((t as any).executionsList) ||
                Number.isFinite(Number((t as any).executionCount)) ||
                Number.isFinite(Number((t as any).executions));
            const side = String((t as any).side ?? '').trim().toLowerCase();
            const hasTradeSide = side === 'long' || side === 'short';
            return hasExecutionShape || hasTradeSide;
        });

        if (isLikelyPreGrouped) {
            return trades
                .map((t, index) => {
                    const executionsList = Array.isArray((t as any).executionsList)
                        ? (t as any).executionsList
                        : [];
                    const shouldDebugRow = shouldDebugTsMix(t) || executionsList.some((e: any) => shouldDebugTsMix(e));
                    if (shouldDebugRow) {
                        console.log('RAW TRADE', t);
                        console.log('EXECUTIONS', executionsList);
                    }

                    const normalizedTimesArray: Array<{ ts: number; source: string; raw: unknown }> = executionsList
                        .map((e: any): { ts: number; source: string; raw: unknown } | null => {
                            const candidates: Array<{ source: string; value: unknown }> = [
                                { source: 'timestamp', value: e?.timestamp },
                            ];
                            for (const c of candidates) {
                                const parsed = normalizeTimestampToMs(c.value);
                                if (parsed != null && Number.isFinite(parsed) && parsed >= MIN_VALID_TS_MS) {
                                    return { ts: parsed, source: c.source, raw: c.value };
                                }
                            }
                            return null;
                        })
                        .filter((x: { ts: number; source: string; raw: unknown } | null): x is { ts: number; source: string; raw: unknown } => x != null)
                        .sort((a: { ts: number; source: string; raw: unknown }, b: { ts: number; source: string; raw: unknown }) => a.ts - b.ts);
                    if (shouldDebugRow) {
                        console.log('NORMALIZED EXEC TIMES', normalizedTimesArray.map((x: { ts: number; source: string; raw: unknown }) => x.ts));
                        console.log('NORMALIZED EXEC TIME SOURCES', normalizedTimesArray.map((x: { ts: number; source: string; raw: unknown }) => ({ source: x.source, raw: x.raw, ts: x.ts })));
                    }

                    let openTs: number | null = null;
                    let closeTs: number | null = null;

                    if (normalizedTimesArray.length > 0) {
                        openTs = normalizedTimesArray[0].ts;
                        closeTs = normalizedTimesArray.length > 1
                            ? normalizedTimesArray[normalizedTimesArray.length - 1].ts
                            : null;
                    } else {
                        const openCandidates = [
                            { field: 'openTimestampMs', value: (t as any).openTimestampMs },
                            { field: 'timestamp', value: (t as any).timestamp },
                            { field: 'entryTime', value: (t as any).entryTime },
                            { field: 'openTime', value: (t as any).openTime },
                        ];
                        const selectedOpen = openCandidates.find((c) => {
                            const parsed = normalizeTimestampToMs(c.value);
                            return parsed != null && Number.isFinite(parsed) && parsed >= MIN_VALID_TS_MS;
                        });

                        openTs = selectedOpen ? normalizeTimestampToMs(selectedOpen.value) : null;
                        if (openTs && openTs >= MIN_VALID_TS_MS) {
                            const closeCandidates = [
                                { field: 'closeTimestampMs', value: (t as any).closeTimestampMs },
                                { field: 'closeTime', value: (t as any).closeTime },
                                { field: 'exitTime', value: (t as any).exitTime },
                            ];
                            const selectedClose = closeCandidates.find((c) => {
                                const parsed = normalizeTimestampToMs(c.value);
                                return parsed != null && Number.isFinite(parsed) && parsed >= MIN_VALID_TS_MS;
                            });

                            if (shouldDebugRow) {
                                console.log('TRADE LEVEL CANDIDATES', {
                                    openCandidates,
                                    selectedOpenField: selectedOpen?.field ?? null,
                                    closeCandidates,
                                    selectedCloseField: selectedClose?.field ?? null,
                                });
                            }

                            closeTs = selectedClose ? normalizeTimestampToMs(selectedClose.value) : null;
                            if (closeTs && closeTs < openTs) closeTs = null;
                        }
                    }

                    if (!openTs || openTs < MIN_VALID_TS_MS) return null;
                    const sideFromTrade = getExecutionSide(t);
                    const sideFromFirstExecution = executionsList.length > 0
                        ? getExecutionSide(executionsList[0] as Trade)
                        : undefined;
                    const resolvedSide = sideFromTrade ?? sideFromFirstExecution;
                    const executionCount = Math.max(
                        1,
                        Number((t as any).executionCount ?? (t as any).executions ?? executionsList.length ?? 1) || 1
                    );

                    const sourceIds = executionsList.length > 0
                        ? executionsList.map((e: any, i: number) => {
                            const raw = e?.id ?? e?.executionId ?? `${index}-${i}`;
                            const n = Number(raw);
                            return Number.isFinite(n) ? n : hashStringToPositiveInt(String(raw));
                        })
                        : [getExecutionId(t, index)];

                    const safeStatus: 'open' | 'closed' = closeTs ? 'closed' : 'open';
                    const safeDuration = safeStatus === 'closed' && closeTs && closeTs >= openTs
                        ? Math.max(0, Math.round((closeTs - openTs) / 60000))
                        : undefined;

                    type PreGroupedExecution = { side: 'buy' | 'sell'; qty: number; price: number; commission: number; fees: number; totalCosts: number };

                    const normalizedExecutions = executionsList
                        .map((e: any): PreGroupedExecution | null => {
                            const side = getExecutionSide(e as Trade);
                            const qty = Math.abs(Number(e?.qty ?? e?.quantity ?? e?.volume ?? e?.shares ?? 0));
                            const price = Number(e?.price ?? e?.entryPrice ?? e?.exitPrice ?? e?.fillPrice ?? e?.avgPrice ?? 0);
                            const commission = Number(e?.commission ?? 0);
                            const fees = Number(e?.fees ?? 0);
                            const totalCosts = getExecutionTotalCosts(e);
                            if (!side || !Number.isFinite(qty) || qty <= 0 || !Number.isFinite(price)) return null;
                            return { side, qty, price, commission, fees, totalCosts };
                        })
                        .filter((e: PreGroupedExecution | null): e is PreGroupedExecution => e != null);

                    const preGroupedMultiplierMarket = String(
                        (t as any).market
                        ?? (t as any).assetClass
                        ?? (executionsList[0] as any)?.market
                        ?? (executionsList[0] as any)?.assetClass
                        ?? ''
                    ).toUpperCase();
                    const preGroupedBuyQty = normalizedExecutions.reduce((sum: number, e: PreGroupedExecution) => sum + (e.side === 'buy' ? e.qty : 0), 0);
                    const preGroupedBuyValue = normalizedExecutions.reduce((sum: number, e: PreGroupedExecution) => sum + (e.side === 'buy' ? (e.qty * e.price) : 0), 0);
                    const preGroupedSellQty = normalizedExecutions.reduce((sum: number, e: PreGroupedExecution) => sum + (e.side === 'sell' ? e.qty : 0), 0);
                    const preGroupedSellValue = normalizedExecutions.reduce((sum: number, e: PreGroupedExecution) => sum + (e.side === 'sell' ? (e.qty * e.price) : 0), 0);
                    const preGroupedEntryPrice = resolvedSide === 'sell'
                        ? (preGroupedSellQty > 0 ? preGroupedSellValue / preGroupedSellQty : undefined)
                        : (preGroupedBuyQty > 0 ? preGroupedBuyValue / preGroupedBuyQty : undefined);
                    const preGroupedExitPrice = safeStatus === 'closed'
                        ? (resolvedSide === 'sell'
                            ? (preGroupedBuyQty > 0 ? preGroupedBuyValue / preGroupedBuyQty : undefined)
                            : (preGroupedSellQty > 0 ? preGroupedSellValue / preGroupedSellQty : undefined))
                        : undefined;
                    const preGroupedMultiplier = preGroupedMultiplierMarket.includes('FUT')
                        ? getContractMultiplier(String((t as any).symbol ?? (executionsList[0] as any)?.symbol ?? ''), 'FUTURES', (executionsList[0] as any)?.instrument ?? (t as any)?.instrument)
                        : 1;
                    const preGroupedSummaryGrossPnl = getSummaryTradeGrossPnl(t, resolvedSide);
                    const preGroupedGrossPnl = normalizedExecutions.length > 0
                        ? calculateFifoGrossPnl(normalizedExecutions.map((e: PreGroupedExecution) => ({ side: e.side, qty: e.qty, price: e.price }))).grossPnl * preGroupedMultiplier
                        : (preGroupedSummaryGrossPnl ?? Number((t as any).calculatedPnl ?? (t as any).grossPnl ?? (t as any).pnl ?? 0));
                    const preGroupedCommission = normalizedExecutions.reduce((sum: number, e: PreGroupedExecution) => sum + (Number.isFinite(e.commission) ? e.commission : 0), 0);
                    const preGroupedFees = normalizedExecutions.reduce((sum: number, e: PreGroupedExecution) => sum + (Number.isFinite(e.fees) ? e.fees : 0), 0);
                    const preGroupedTotalCosts = normalizedExecutions.reduce((sum: number, e: PreGroupedExecution) => sum + (Number.isFinite(e.totalCosts) ? e.totalCosts : 0), 0);
                    const isSummaryOrIndexTrade =
                        isIndexOrCfdInstrument((t as any).symbol ?? (executionsList[0] as any)?.symbol, (t as any).instrumentType ?? (executionsList[0] as any)?.instrumentType, t as any) ||
                        (executionsList[0] as any)?.executionSource === 'completed_trade_summary' ||
                        (t as any)?.executionSource === 'completed_trade_summary';
                    const authoritativePreGroupedPnl = [
                        (t as any).brokerReportedPnl,
                        ...executionsList.map((e: any) => e?.brokerReportedPnl),
                        (t as any).netPnl,
                        (t as any).net_pnl,
                        (t as any).calculatedPnl,
                        (t as any).pnl,
                    ]
                        .map((val) => (val == null || val === '' ? Number.NaN : Number(val)))
                        .find((val) => Number.isFinite(val));
                    const preGroupedNetPnl = safeStatus === 'closed'
                        ? (isSummaryOrIndexTrade && authoritativePreGroupedPnl !== undefined
                            ? authoritativePreGroupedPnl
                            : (preGroupedGrossPnl - preGroupedTotalCosts))
                        : 0;

                    const groupedTradeObject = {
                        ...(t as Trade),
                        side: resolvedSide ? (resolvedSide === 'sell' ? 'short' : 'long') : (t as any).side,
                        entryPrice: Number.isFinite(Number(preGroupedEntryPrice)) ? Number(preGroupedEntryPrice) : (t as any).entryPrice,
                        exitPrice: Number.isFinite(Number(preGroupedExitPrice)) ? Number(preGroupedExitPrice) : (safeStatus === 'closed' ? (t as any).exitPrice : undefined),
                        executionsList,
                        executionCount,
                        executions: executionCount,
                        sourceIds,
                        openTime: openTs,
                        closeTime: closeTs,
                        openTimestampMs: openTs,
                        closeTimestampMs: closeTs,
                        status: safeStatus,
                        duration: safeDuration,
                        timestamp: openTs,
                        pnl: normalizedExecutions.length > 0 ? preGroupedNetPnl : (t as any).pnl,
                        calculatedPnl: normalizedExecutions.length > 0 ? preGroupedNetPnl : (t as any).calculatedPnl,
                        commission: normalizedExecutions.length > 0 ? preGroupedCommission : (t as any).commission,
                        fees: normalizedExecutions.length > 0 ? preGroupedFees : (t as any).fees,
                        commissionAndFees: normalizedExecutions.length > 0 ? preGroupedTotalCosts : (t as any).commissionAndFees,
                    } as GroupedTrade;

                    if (shouldDebugRow) {
                        console.log('DERIVED TIMES', { openTime: openTs, closeTime: closeTs });
                        console.log('FINAL GROUPED TRADE', groupedTradeObject);
                    }

                    return groupedTradeObject;
                })
                    .filter((t): t is GroupedTrade => t != null)
                .sort((a, b) => (a.openTime ?? 0) - (b.openTime ?? 0));
        }

        const executions = trades
            .map((t, index) => {
                if (shouldDebugTsMix(t)) {
                    console.log('RAW TRADE', t);
                }
                const symbol = String((t as any).symbol ?? '').trim();
                const rawTs = (t as any).openTimestampMs ?? (t as any).timestamp ?? (t as any).entryTime ?? (t as any).openTime;
                const ts = normalizeTimestampToMs(rawTs);
                console.log('DEBUG TSMIX-1', {
                    raw: (t as any).openTimestampMs ?? (t as any).timestamp ?? (t as any).entryTime ?? (t as any).openTime,
                    normalized: ts,
                });
                if (import.meta.env.DEV && shouldDebugTsMix(t)) {
                    console.debug('[TSMIX-1][useGroupedTrades][execution]', {
                        tradeId: (t as any)?.tradeId,
                        orderId: (t as any)?.orderId,
                        executionId: (t as any)?.executionId,
                        symbol,
                        rawTimestamp: rawTs,
                        normalizedTimestampMs: ts,
                    });
                }
                if (!ts || ts < MIN_VALID_TS_MS) return null;
                const qty = Number((t as any).qty ?? (t as any).quantity ?? (t as any).volume ?? (t as any).shares ?? 0);
                const side = getExecutionSide(t);
                const price = Number((t as any).price ?? (t as any).entryPrice ?? (t as any).fillPrice ?? 0);
                const commission = Number((t as any).commission ?? 0);
                const fees = Number((t as any).fees ?? 0);
                const totalCosts = getExecutionTotalCosts(t as any);
                const brokerReportedPnl = parseOptionalTradePnl(
                    (t as any).brokerReportedPnl ??
                    (t as any).broker_reported_pnl ??
                    (t as any).brokerPnl ??
                    (t as any).reportedPnl ??
                    (t as any).realizedPnl ??
                    (t as any).realized_pnl ??
                    (t as any).pnl ??
                    (t as any).profit ??
                    (t as any).pl
                );

                if (!symbol || !Number.isFinite(ts) || !Number.isFinite(qty) || qty === 0 || !side) {
                    console.warn('[TradesPage] Skipping execution row due to missing numeric timestamp/qty/side', { symbol, ts, qty, side });
                    return null;
                }

                const market = detectMarketCategory({
                    symbol,
                    market: (t as any).market,
                    marketType: (t as any).marketType,
                    assetClass: (t as any).assetClass,
                    category: (t as any).category,
                    instrumentType: (t as any).instrumentType,
                    instrument: (t as any).instrument,
                    securityType: (t as any).securityType,
                });

                return {
                    trade: t,
                    symbol,
                    market,
                    timestamp: ts,
                    qty: Math.abs(qty),
                    side,
                    price,
                    commission,
                    fees,
                    totalCosts,
                    brokerReportedPnl,
                    id: getExecutionId(t, index),
                };
            })
            .filter((e): e is NonNullable<typeof e> => !!e)
            .sort((a, b) => a.timestamp - b.timestamp);

        const groups: GroupedTrade[] = [];
        const openBySymbol = new Map<string, {
            executions: typeof executions;
            net: number;
            maxAbs: number;
            buyQty: number;
            buyValue: number;
            sellQty: number;
            sellValue: number;
            totalCommission: number;
            totalFees: number;
            totalCosts: number;
            brokerReportedPnl?: number;
            tagSet: Set<string>;
            notesList: string[];
        }>();

        const finalizeGroup = (symbolKey: string, symbol: string, ctx: ReturnType<typeof openBySymbol.get>, isClosed: boolean) => {
            if (!ctx || ctx.executions.length === 0) return;
            const first = ctx.executions[0];
            const last = ctx.executions[ctx.executions.length - 1];
            const shouldDebugGroup = ctx.executions.some((e) => shouldDebugTsMix(e.trade)) || shouldDebugTsMix(first.trade) || shouldDebugTsMix(last.trade);
            const executionsList = ctx.executions.map((e) => e.trade);
            if (shouldDebugGroup) console.log('EXECUTIONS', executionsList);

            const normalizedTimesArray = ctx.executions
                .map((e) => normalizeTimestampToMs(e.timestamp))
                .filter((ts: number | null): ts is number => ts != null && Number.isFinite(ts) && ts >= MIN_VALID_TS_MS)
                .sort((a, b) => a - b);
            if (shouldDebugGroup) console.log('NORMALIZED EXEC TIMES', normalizedTimesArray);

            const openTs = normalizedTimesArray.length > 0
                ? normalizedTimesArray[0]
                : normalizeTimestampToMs(first.timestamp);
            if (!openTs || openTs < MIN_VALID_TS_MS) return;

            const derivedCloseFromExec = normalizedTimesArray.length > 0
                ? normalizedTimesArray[normalizedTimesArray.length - 1]
                : normalizeTimestampToMs(last.timestamp);
            let closeTs = isClosed ? derivedCloseFromExec : null;
            if (closeTs && closeTs < openTs) closeTs = null;

            const safeStatus: 'open' | 'closed' = closeTs ? 'closed' : 'open';
            const openingSide = first.side === 'buy' ? 'long' : 'short';

            const entryPrice = openingSide === 'long'
                ? (ctx.buyQty ? ctx.buyValue / ctx.buyQty : first.price)
                : (ctx.sellQty ? ctx.sellValue / ctx.sellQty : first.price);
            const exitPrice = openingSide === 'long'
                ? (ctx.sellQty ? ctx.sellValue / ctx.sellQty : null)
                : (ctx.buyQty ? ctx.buyValue / ctx.buyQty : null);
            const fifo = calculateFifoGrossPnl(
                ctx.executions.map((execution) => ({
                    side: execution.side,
                    qty: execution.qty,
                    price: execution.price,
                }))
            );
            const futuresMultiplier = first.market === 'FUTURES'
                ? getContractMultiplier(symbol, 'FUTURES', (first as any)?.instrument)
                : 1;
            const grossPnl = fifo.grossPnl * futuresMultiplier;
            const rawCalculatedPnl = safeStatus === 'closed' ? (grossPnl - ctx.totalCosts) : null;
            const normalizedBrokerPnl = safeStatus === 'closed' && Number.isFinite(Number(ctx.brokerReportedPnl))
                ? Number(ctx.brokerReportedPnl)
                : null;
            const preferBrokerPnl =
                safeStatus === 'closed' &&
                normalizedBrokerPnl != null &&
                (isIndexOrCfdInstrument(symbol, (first.trade as any)?.instrumentType, first.trade as any) ||
                    (first.trade as any)?.executionSource === 'completed_trade_summary');
            const calculatedPnl = preferBrokerPnl ? normalizedBrokerPnl : rawCalculatedPnl;
            const pnlDifference = normalizedBrokerPnl != null && calculatedPnl != null
                ? (preferBrokerPnl ? 0 : (normalizedBrokerPnl - calculatedPnl))
                : null;

            const openTime = openTs;
            const closeTime = closeTs;
            const executionCount = ctx.executions.length;
            const sourceIds = ctx.executions.map((e) => e.id);
            const groupId = hashStringToPositiveInt(`${symbolKey}|${openTime}|${executionCount}|${openingSide}`);
            const safeDuration = safeStatus === 'closed' && closeTs && closeTs >= openTs
                ? Math.max(0, Math.round((closeTs - openTs) / 60000))
                : undefined;
                    console.log('DEBUG TSMIX-1 COMPUTED', {
                        openTime: openTs,
                        closeTime: closeTs,
                        duration: safeDuration,
                    });
            console.log('DEBUG TSMIX-1 COMPUTED', {
                openTime,
                closeTime,
                duration: safeDuration,
            });
            if (shouldDebugGroup) {
                console.debug('[TSMIX-1][useGroupedTrades][group]', {
                    symbolKey,
                    symbol,
                    rawOpenTimestamp: first.timestamp,
                    rawCloseTimestamp: isClosed ? last.timestamp : null,
                    normalizedOpenTimestampMs: openTs,
                    normalizedCloseTimestampMs: closeTs,
                    computedStatus: safeStatus,
                    computedDurationMinutes: safeDuration ?? null,
                });
            }

            const groupedTradeObject = {
                ...(first.trade as Trade),
                id: groupId,
                symbol,
                market: ctx.executions[0]?.market,
                side: openingSide,
                volume: ctx.maxAbs,
                entryPrice,
                exitPrice: isClosed ? exitPrice ?? undefined : undefined,
                executionsList,
                executionCount,
                executions: executionCount,
                sourceIds,
                tags: Array.from(ctx.tagSet),
                notes: ctx.notesList.join(' | '),
                openTimestampMs: openTime,
                closeTimestampMs: closeTime,
                timestamp: openTime,
                openTime,
                closeTime,
                duration: safeDuration,
                pnl: safeStatus === 'closed' ? (calculatedPnl ?? 0) : 0,
                calculatedPnl: safeStatus === 'closed' ? (calculatedPnl ?? 0) : 0,
                brokerReportedPnl: normalizedBrokerPnl,
                pnlDifference,
                commission: ctx.totalCommission,
                fees: ctx.totalFees,
                commissionAndFees: ctx.totalCosts,
                status: safeStatus,
            } as GroupedTrade;

            if (shouldDebugGroup) {
                console.log('DERIVED TIMES', { openTime, closeTime });
                console.log('FINAL GROUPED TRADE', groupedTradeObject);
            }

            groups.push(groupedTradeObject);
        };

        for (const exec of executions) {
            const symbol = exec.symbol;
            const tradeIdentity = (() => {
                const rawIdentity = (exec.trade as any).tradeId ?? (exec.trade as any).orderId ?? (exec.trade as any).id;
                const normalizedIdentity = String(rawIdentity ?? '').trim().toLowerCase();
                return normalizedIdentity || String(exec.id);
            })();
            const symbolKey = `${exec.market}:${symbol}:${tradeIdentity}`;
            let ctx = openBySymbol.get(symbolKey);
            if (!ctx || ctx.net === 0) {
                ctx = {
                    executions: [],
                    net: 0,
                    maxAbs: 0,
                    buyQty: 0,
                    buyValue: 0,
                    sellQty: 0,
                    sellValue: 0,
                    totalCommission: 0,
                    totalFees: 0,
                    totalCosts: 0,
                    brokerReportedPnl: undefined,
                    tagSet: new Set<string>(),
                    notesList: [],
                };
                openBySymbol.set(symbolKey, ctx);
            }

            ctx.executions.push(exec);
            const signed = exec.side === 'buy' ? exec.qty : -exec.qty;
            ctx.net += signed;
            ctx.maxAbs = Math.max(ctx.maxAbs, Math.abs(ctx.net));

            if (exec.side === 'buy') {
                ctx.buyQty += exec.qty;
                ctx.buyValue += exec.qty * exec.price;
            } else {
                ctx.sellQty += exec.qty;
                ctx.sellValue += exec.qty * exec.price;
            }

            ctx.totalCommission += exec.commission;
            ctx.totalFees += exec.fees;
            ctx.totalCosts += Number.isFinite(Number(exec.totalCosts)) ? Number(exec.totalCosts) : 0;
            if (typeof exec.brokerReportedPnl === 'number' && Number.isFinite(exec.brokerReportedPnl)) {
                ctx.brokerReportedPnl = exec.brokerReportedPnl;
            }

            const tags = parseTags((exec.trade as any).tags) ?? [];
            for (const tag of tags) ctx.tagSet.add(String(tag));
            if ((exec.trade as any).notes) ctx.notesList.push(String((exec.trade as any).notes));

            if (ctx.net === 0) {
                finalizeGroup(symbolKey, symbol, ctx, true);
                openBySymbol.delete(symbolKey);
            }
        }

        for (const [symbolKey, ctx] of openBySymbol.entries()) {
            if (ctx.net !== 0) {
                const idx = symbolKey.indexOf(':');
                const symbol = idx >= 0 ? symbolKey.slice(idx + 1) : symbolKey;
                finalizeGroup(symbolKey, symbol, ctx, false);
            }
        }

        return groups.sort((a, b) => (a.openTime ?? 0) - (b.openTime ?? 0));
    }, [trades]);
}

    // NOTE: `formatDateCell` and `formatTimeCell` are defined inside the component below
    // so they can read the shared account display-timezone setting.


function ActionMenu({ selectedIds, onAction }: { selectedIds: string[]; onAction: (a: string) => void }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

    const MENU_WIDTH = 160;

    const updateMenuPosition = () => {
        if (!ref.current) return;
        const triggerRect = ref.current.getBoundingClientRect();
        const viewportPadding = 8;
        const preferredLeft = triggerRect.right - MENU_WIDTH;
        const clampedLeft = Math.max(
            viewportPadding,
            Math.min(preferredLeft, window.innerWidth - MENU_WIDTH - viewportPadding)
        );
        setMenuPos({
            top: triggerRect.bottom + 8,
            left: clampedLeft,
        });
    };

    useEffect(() => {
        function onDoc(e: PointerEvent) {
            const target = e.target as Node;
            if (ref.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            setOpen(false);
        }
        document.addEventListener('pointerdown', onDoc);
        return () => document.removeEventListener('pointerdown', onDoc);
    }, []);

    useEffect(() => {
        if (!open) return;
        updateMenuPosition();
        const onViewportChange = () => updateMenuPosition();
        window.addEventListener('resize', onViewportChange);
        window.addEventListener('scroll', onViewportChange, true);
        return () => {
            window.removeEventListener('resize', onViewportChange);
            window.removeEventListener('scroll', onViewportChange, true);
        };
    }, [open]);

    const actions = ['Select All', 'Set Initial Risk', 'Merge Trades', 'Delete Selected', 'Delete All', 'Split Trade'];

    // use selectedIds to disable the action menu when nothing is selected
    const disabled = selectedIds.length === 0;

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen(s => !s)}
                disabled={disabled}
                title={disabled ? 'Select one or more trades to perform actions' : undefined}
                className={`${disabled ? 'opacity-50 pointer-events-none' : ''} inline-flex items-center gap-2 pl-3 pr-2 h-10 rounded-xl border bg-white border-blue-500 text-zinc-700 dark:bg-zinc-900 dark:border-blue-500 dark:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 text-sm font-medium transition-colors`}
            >
                <Cloud className="w-4 h-4" />

                <span className="text-sm font-medium">Select action</span>
                <ChevronDown size={16} className="opacity-80" />
            </button>

            {open && menuPos && createPortal(
                <div
                    ref={menuRef}
                    style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px`, width: `${MENU_WIDTH}px` }}
                    className="fixed bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-lg z-[10050]"
                >
                    <div className="py-1">
                        {actions.map(a => {
                            const actionDisabled = a === 'Merge Trades' && selectedIds.length < 2;
                            return (
                            <button
                                key={a}
                                type="button"
                                disabled={actionDisabled}
                                title={actionDisabled ? 'Select at least two trades to merge' : undefined}
                                onClick={() => {
                                    if (actionDisabled) return;
                                    setOpen(false);
                                    onAction(a);
                                }}
                                className={`w-full px-3 py-2 text-left text-sm ${actionDisabled
                                    ? 'cursor-not-allowed text-zinc-400 opacity-50 dark:text-zinc-500'
                                    : 'hover:bg-zinc-100 dark:hover:bg-zinc-700'
                                }`}
                            >
                                {a}
                            </button>
                            );
                        })}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

// --- Tags Management Modal (copied from TradeDetails Add Tag popup) ---
const TagsModal = ({ isOpen, onClose, tags, onAdd, onRemove, editingTag, onRename }: { isOpen: boolean; onClose: () => void; tags: string[]; onAdd: (t: string) => void; onRemove: (t: string) => void; editingTag?: string | null; onRename?: (original: string, next: string) => void }) => {
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        setInputValue(isOpen ? (editingTag ?? '') : '');
    }, [editingTag, isOpen]);

    if (!isOpen) return null;

    const handleAdd = (e?: React.FormEvent) => {
        e?.preventDefault();
        const trimmed = inputValue.trim();
        if (trimmed) {
            if (editingTag && onRename) onRename(editingTag, trimmed);
            else onAdd(trimmed);
            setInputValue('');
        }
    };

    const suggestions = ["Trend Day", "Choppy", "FOMC", "Reversal", "Breakout", "Range Bound", "High Volatility", "Low Volume", "Long Term"];

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-sm bg-white dark:bg-[#1a1a1c] p-6 rounded-2xl shadow-2xl border border-zinc-200 dark:border-zinc-800">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">{editingTag ? 'Edit Tag' : 'Manage Tags'}</h3>
                </div>

                <form onSubmit={handleAdd} className="flex gap-2 mb-4">
                    <input
                        className="flex-1 px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        aria-label={editingTag ? 'Edit tag' : 'Add tag'}
                        placeholder={editingTag ? 'Edit tag...' : 'Add a tag...'}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        autoFocus
                    />
                    <button type="submit" aria-label={editingTag ? 'Save tag' : 'Add tag'} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
                        <Plus className="w-5 h-5" />
                    </button>
                </form>

                <div className="mb-4">
                    <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2">Active Tags</div>
                    <div className="flex flex-wrap gap-2 min-h-[40px]">
                        {tags.length === 0 ? (
                            <span className="text-sm text-zinc-400 italic">No tags selected</span>
                        ) : (
                            tags.map((tag) => (
                                <RemovableTag key={tag} label={tag} onRemove={() => onRemove(tag)} />
                            ))
                        )}
                    </div>
                </div>

                <div>
                    <div className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-2">Suggestions</div>
                    <div className="flex flex-wrap gap-2">
                        {suggestions.map((s) => {
                            const isLongTerm = s === 'Long Term';
                            return (
                                <button
                                    key={s}
                                    onClick={() => onAdd(s)}
                                    disabled={tags.includes(s)}
                                    className={`px-2 py-1 rounded-md text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isLongTerm ? 'bg-orange-100 text-orange-900 hover:bg-orange-200 dark:bg-yellow-600/20 dark:text-yellow-300' : 'bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-zinc-600 dark:text-zinc-300'}`}
                                >
                                    + {s}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-28 flex items-center justify-center px-4 py-2 h-9 rounded-md border border-zinc-800 bg-zinc-900 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-all"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            const toRemove = Array.isArray(tags) ? [...tags] : [];
                            toRemove.forEach(t => onRemove(t));
                            setInputValue('');
                        }}
                        disabled={!Array.isArray(tags) || tags.length === 0}
                        className={`w-28 flex items-center justify-center px-4 py-2 h-9 text-sm rounded-md transition-colors ${Array.isArray(tags) && tags.length > 0 ? 'bg-zinc-100 text-zinc-900 dark:bg-zinc-700 dark:text-white' : 'bg-zinc-900 border border-zinc-700 text-zinc-500 opacity-50 cursor-not-allowed'}`}
                    >
                        Clear
                    </button>

                    <button
                        type="button"
                        onClick={() => { handleAdd(); onClose(); }}
                        className="w-28 flex items-center justify-center px-4 py-2 h-9 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-500"
                    >
                        {editingTag ? 'Update' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export type ImportResult = {
    success: boolean;
    title?: string;
    body?: string;
    count?: number;
    persistedTradeCount?: number;
    accountName?: string;
    executionCount?: number;
    duplicateCount?: number;
    skippedInvalidCount?: number;
    rejectedCount?: number; // number of rows filtered out (e.g. session validation)
    rowsRead?: number; // number of meaningful rows parsed from the file
    summary?: CsvImportSummary | null;
    message?: string;
};

/**
 * Builds the user-facing import banner content (title + body).
 *
 * - Successful import with no rejections -> "Import complete"
 * - Successful import with rejected rows  -> "Import completed with warnings"
 * - CSV parsed but zero trades created     -> "No trades were imported"
 * - Duplicate-only reimport                -> existing duplicate report text
 */
export function buildImportBannerContent(res: ImportResult): { title: string; body: string } {
    const trades = Number(res.count ?? 0);
    const rowsRead = Number(res.rowsRead ?? 0);
    const rejected = Number(res.rejectedCount ?? 0);
    const duplicates = Number(res.duplicateCount ?? 0);

    if (!res.success) {
        return { title: 'Import failed', body: res.message || 'The CSV file could not be processed.' };
    }

    if (res.summary) {
        const message = formatCsvImportToast(res.summary);
        const successPrefix = 'Import complete — ';
        if (res.summary.newTradesImported === 0 && res.summary.sourceRowsProcessed > 0
            && !message.startsWith('No new trades imported.')) {
            const duplicates = res.summary.sourceRowsByOutcome.duplicate;
            const otherSkipped = res.summary.sourceRowsByOutcome.invalid
                + res.summary.sourceRowsByOutcome.rejected
                + res.summary.sourceRowsByOutcome.tenantRestricted
                + res.summary.sourceRowsByOutcome.unclassified;
            return {
                title: '',
                body: `No new trades imported.${duplicates > 0 ? ` ${duplicates} duplicates skipped.` : ''}${otherSkipped > 0 ? ` ${otherSkipped} ${otherSkipped === 1 ? 'row' : 'rows'} could not be imported.` : ''}`,
            };
        }
        return {
            title: message.startsWith(successPrefix) ? 'Import complete' : '',
            body: message.startsWith(successPrefix) ? `— ${message.slice(successPrefix.length)}` : message,
        };
    }

    if (res.message && res.count == null && res.executionCount == null) {
        return { title: 'Import complete', body: res.message };
    }

    if (trades > 0 && rejected > 0) {
        return {
            title: '',
            body: `${trades} ${trades === 1 ? 'trade' : 'trades'} imported. ${rejected} ${rejected === 1 ? 'row' : 'rows'} skipped.`,
        };
    }

    if (trades > 0) {
        return {
            title: 'Import complete',
            body: `— ${trades} ${trades === 1 ? 'trade' : 'trades'} imported successfully.`,
        };
    }

    if (duplicates > 0) {
        return {
            title: '',
            body: `No new trades imported. ${duplicates} duplicates skipped.`,
        };
    }

    if (rowsRead > 0) {
        return {
            title: '',
            body: `Import failed — ${rowsRead} ${rowsRead === 1 ? 'row' : 'rows'} could not be imported.`,
        };
    }

    return {
        title: '',
        body: rowsRead > 0
            ? `Import failed — ${rowsRead} ${rowsRead === 1 ? 'row' : 'rows'} could not be imported.`
            : 'No valid trade rows could be imported.',
    };
}

export function buildImportToastDetail(res: ImportResult): { title: string; message: string; variant: 'info' | 'success' | 'error' } {
    return buildCsvImportToastDetail(res);
}

/** Normalize Import Trades' one-shot result while preserving confirmed server counts. */
export function parseImportResultPayload(payload: unknown): ImportResult | null {
    if (!payload || typeof payload !== 'object' || typeof (payload as any).success !== 'boolean') return null;
    const parsed = payload as Record<string, any>;
    return {
        success: parsed.success,
        count: Number(parsed.count) || 0,
        persistedTradeCount: Number.isFinite(Number(parsed.persistedTradeCount)) ? Number(parsed.persistedTradeCount) : undefined,
        accountName: typeof parsed.accountName === 'string' ? parsed.accountName : undefined,
        executionCount: Number(parsed.executionCount) || Number(parsed.executions) || Number(parsed.rows) || Number(parsed.count) || 0,
        duplicateCount: Number(parsed.duplicateCount) || Number(parsed.duplicates) || 0,
        rowsRead: Number(parsed.rowsRead) || 0,
        skippedInvalidCount: Number(parsed.skippedInvalidCount) || Number(parsed.skippedInvalids) || Number(parsed.skipped) || 0,
        rejectedCount: Number(parsed.rejectedCount) || Number(parsed.rejected) || 0,
        summary: parsed.summary || null,
        message: parsed.message || (parsed.success ? 'CSV imported successfully.' : 'The CSV file could not be processed.'),
    };
}

function dispatchImportResultToastIfNeeded(parsed: Record<string, any>, result: ImportResult): void {
    if (parsed.toastDispatched !== true) {
        const toast = buildImportToastDetail(result);
        window.dispatchEvent(new CustomEvent('tt:toast', {
            detail: {
                ...toast,
                duration: 7000,
                ...(result.success && Number(result.count) > 0 && result.rejectedCount ? { actionLabel: 'Rejected', actionUrl: '/import/rejections' } : {}),
            },
        }));
    }
}

/**
 * Format the human-readable banner message shown after an import attempt.
 *
 * The message will include the total rows processed along with a breakdown
 * of how many executions were rejected (if >0), and the number of trades
 * produced. Keeping this logic in a helper makes it easy to unit test.
 */
export function formatImportBannerMessage(res: ImportResult): string {
    if (!res.success) {
        return res.message || '';
    }

    if (res.summary) return formatCsvImportSummary(res.summary);

    const execs = Number(res.executionCount ?? 0);
    const trades = Number(res.count ?? 0);
    const duplicates = Number(res.duplicateCount ?? 0);
    const skippedInvalid = Number(res.skippedInvalidCount ?? 0);
    const rejected = Number(res.rejectedCount ?? 0);

    if (execs === 0 && trades === 0) {
        let msg = 'No new data imported. 0 rows and 0 trades added.';
        if (duplicates > 0) {
            msg += ` Skipped ${duplicates} duplicate ${duplicates === 1 ? 'execution' : 'executions'}.`;
        }
        if (skippedInvalid > 0) {
            msg += ` Skipped ${skippedInvalid} invalid ${skippedInvalid === 1 ? 'row' : 'rows'}.`;
        }
        if (rejected > 0) {
            msg += ` Rejected ${rejected}.`;
        }
        return msg;
    }

    let msg = `Imported ${execs} ${execs === 1 ? 'row' : 'rows'} and grouped them into ${trades} ${trades === 1 ? 'trade' : 'trades'}.`;
    if (duplicates > 0) {
        msg += ` Skipped ${duplicates} duplicate ${duplicates === 1 ? 'execution' : 'executions'}.`;
    }
    if (skippedInvalid > 0) {
        msg += ` Skipped ${skippedInvalid} invalid ${skippedInvalid === 1 ? 'row' : 'rows'}.`;
    }
    if (rejected > 0) {
        msg += ` Rejected ${rejected}.`;
    }
    return msg;
}

function collectExecutionDedupeKeys(tradeLike: any): string[] {
    return sharedCollectExecutionDedupeKeys(tradeLike);
}

// Exported normalization helper reused by tests and other modules.
export function normalizeTradeSide(value: unknown, fallbackVolume?: unknown): 'buy' | 'sell' | undefined {
    const raw = String(value ?? '').trim().toLowerCase();
    const numeric = Number(raw);
    if (Number.isFinite(numeric) && numeric !== 0) return numeric < 0 ? 'sell' : 'buy';
    if (raw === '-' || raw === '--') return 'sell';
    if (raw === '+' || raw === '++') return 'buy';
    const compact = raw.replace(/([\s_-])+/, '');
    if (['s', 'sl', 'sld', 'sell', 'short', 'sellshort', 'shortsell'].includes(compact)) return 'sell';
    if (['b', 'bot', 'buy', 'long', 'buytocover', 'cover', 'buycover'].includes(compact)) return 'buy';
    if (raw) {
        if (raw.includes('sell') || raw.includes('short')) return 'sell';
        if (raw.includes('buy') || raw.includes('long')) return 'buy';
    }
    const volNum = Number(fallbackVolume);
    if (Number.isFinite(volNum) && volNum !== 0) {
        return volNum < 0 ? 'sell' : 'buy';
    }
    return undefined;
}

export function TradesPage() {
    const displayTimeZone = useDisplayTimezone();
    const demoMode = useDemoModeState();
    const demoTradesOverride = useDemoTradesOverride();
    const demoTrades = useMemo<Trade[]>(
        () => getTradesFromDemoOverride(demoTradesOverride),
        [demoTradesOverride]
    );
    const demoTradesActive = demoTrades.length > 0;
    const [assetVisibility] = useAssetClassVisibility();
    const isStocksOnly = assetVisibility.STOCKS && !assetVisibility.FUTURES && !assetVisibility.FOREX && !assetVisibility.CRYPTO;
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [importBannerFading, setImportBannerFading] = useState(false);
    const [pendingCsvVerification, setPendingCsvVerification] = useState<{ remoteResult: unknown; saveLocal: () => boolean | Promise<boolean> } | null>(null);
    const [retryingCsvVerification, setRetryingCsvVerification] = useState(false);
    const [showClearModal, setShowClearModal] = useState(false);
    const [showCsvAccountPicker, setShowCsvAccountPicker] = useState(false);
    const [csvSourceBroker, setCsvSourceBroker] = useState('');
    const [csvSourceAccountId, setCsvSourceAccountId] = useState('');
    const [csvSourceAccountName, setCsvSourceAccountName] = useState('');
    const [csvSourceAccounts, setCsvSourceAccounts] = useState<CsvSourceAccountOption[]>([]);
    const [csvAccountsLoading, setCsvAccountsLoading] = useState(false);
    const [csvAccountsError, setCsvAccountsError] = useState('');
    const csvImportAccountRef = useRef<{
        sourceBroker: string;
        sourceAccountId?: string;
        accountName: string;
    } | null>(null);

    const showImportBanner = (next: ImportResult) => {
        setImportBannerFading(false);
        setImportResult(next);
    };

    // lightweight CSV parser borrowed from ImportTradesPage so both importers
    // behave identically when handling malformed input and trimming fields.
    const parseCSV = (content: string) => {
        try {
            const result = Papa.parse(content, { header: true, skipEmptyLines: true, transform: (v: string) => (typeof v === 'string' ? v.trim() : v) });
            const fields = (result.meta.fields || []).map(f => String(f || '').trim().replace(/^[\uFEFF\uFFFE]/, ''));
            const data = (result.data || []).map((row: any) => {
                const out: any = {};
                for (const k of Object.keys(row)) {
                    const key = String(k || '').trim();
                    const val = row[k];
                    if (typeof val === 'string') {
                        out[key] = val.replace(/^\s*"+|"+\s*$/g, '').trim();
                    } else {
                        out[key] = val;
                    }
                }
                return out;
            });
            return { meta: { fields }, data };
        } catch {
            // fallback simple parser
            const lines = content.split(/\r\n|\n/).filter(line => line.trim() !== '');
            if (lines.length < 2) return { meta: { fields: [] }, data: [] };
            const headers = lines[0].split(',').map(h => h.trim().replace(/^[\uFEFF\uFFFE]/, ''));
            const data = lines.slice(1).map(line => {
                const values = line.split(',');
                const row: any = {};
                headers.forEach((header, index) => {
                    row[header] = values[index]?.trim().replace(/^\s*"+|"+\s*$/g, '');
                });
                return row;
            });
            return { meta: { fields: headers }, data };
        }
    };

    const [importing, setImporting] = useState(false);
    const [progress, setProgress] = useState(0);

    const [tradesTimeMode, setTradesTimeMode] = useState<ChartTzMode>(() => loadTradesTimeMode());
    const [timeMenuOpen, setTimeMenuOpen] = useState(false);
    const [timeMenuPosition, setTimeMenuPosition] = useState<{ top: number; left: number } | null>(null);
    const timeMenuRef = useRef<HTMLDivElement | null>(null);
    const timeButtonRef = useRef<HTMLButtonElement | null>(null);

    const updateTimeMenuPosition = () => {
        const button = timeButtonRef.current;
        if (!button) return;
        const rect = button.getBoundingClientRect();
        const menuHeight = 116;
        const menuWidth = 130;
        const preferredTop = rect.bottom + 4;
        const top = preferredTop + menuHeight <= window.innerHeight - 8
            ? preferredTop
            : Math.max(8, rect.top - menuHeight - 4);
        setTimeMenuPosition({
            top,
            left: Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8),
        });
    };

    useEffect(() => {
        if (!timeMenuOpen) return;
        updateTimeMenuPosition();
        const onViewportChange = () => updateTimeMenuPosition();
        window.addEventListener('resize', onViewportChange);
        window.addEventListener('scroll', onViewportChange, true);
        return () => {
            window.removeEventListener('resize', onViewportChange);
            window.removeEventListener('scroll', onViewportChange, true);
        };
    }, [timeMenuOpen]);

    useEffect(() => {
        saveTradesTimeMode(tradesTimeMode);
    }, [tradesTimeMode]);

    useEffect(() => {
        if (!timeMenuOpen) return;
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (timeButtonRef.current?.contains(target) || timeMenuRef.current?.contains(target)) return;
            setTimeMenuOpen(false);
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setTimeMenuOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [timeMenuOpen]);

    const formatDateCell = (value: unknown): string => {
        if (value == null || value === '') return '';
        const parsed = toDateValue(value);
        if (!parsed) return String(value);
        const out = formatDateTimeForMode(parsed.getTime(), tradesTimeMode);
        return out.dateStr || formatNyDateTime(parsed.getTime(), 'dd MMM yyyy');
    };

    const formatTimeCell = (value: unknown): string => {
        if (value == null || value === '') return '';
        const parsed = toDateValue(value);
        if (!parsed) return String(value);
        const out = formatDateTimeForMode(parsed.getTime(), tradesTimeMode);
        return out.timeStr || formatNyDateTime(parsed.getTime(), 'HH:mm:ss');
    };

    const trimTimeToMinutes = (fullTime: string): string => {
        const raw = String(fullTime ?? '').trim();
        if (!raw) return raw;
        const withAmPm = raw.match(/^(\d{1,2}:\d{2})(?::\d{2})\s*([AaPp][Mm])$/);
        if (withAmPm) return `${withAmPm[1]} ${withAmPm[2].toUpperCase()}`;
        const twentyFourHour = raw.match(/^(\d{1,2}:\d{2})(?::\d{2})$/);
        if (twentyFourHour) return twentyFourHour[1];
        return raw;
    };

    const getCloseTimestampMs = (trade: Trade): number | null => {
        const closeValue = (trade as any).closeTime ?? (trade as any).closeDate ?? (trade as any).exitTime ?? (trade as any).closeTimestampMs;
        const closeDate = toDateValue(closeValue);
        return closeDate ? closeDate.getTime() : null;
    };

    // `getTradeTimestampMs` is provided by `tradesFilters` for consistency with tests.

    // Local storage key for remembering the user's preferred trades time mode.

    // Timestamp precision modes used for deciding how to format time columns.
    type TimestampPrecisionMode = 'auto' | 'minutes' | 'seconds';
    type EffectiveTimestampPrecision = 'minutes' | 'seconds';

    function getExecutionsCount(trade: any): number {
        if (!trade) return 0;
        if (Array.isArray(trade.executionsList) && trade.executionsList.length > 0) return trade.executionsList.length;
        const n = Number(trade?.executionCount ?? trade?.executions ?? 0);
        if (Number.isFinite(n) && n > 0) return Math.max(1, Math.round(n));
        return 1;
    }

    // `getStartOfLocalDayMs` and `getEndOfLocalDayMs` are provided by `tradesFilters`.

    const [timestampPrecisionMode] = useState<TimestampPrecisionMode>('auto');


    useEffect(() => {
        try {
            const raw = readPrivateTradingJson('import-result', 'session', ['tt_import_result']);
            if (raw) {
                const parsed = JSON.parse(raw);
                const result = parseImportResultPayload(parsed);
                if (result) {
                    showImportBanner(result);
                    dispatchImportResultToastIfNeeded(parsed, result);
                    if (result.success && Number(result.count) > 0) {
                        // Ensure newly imported historical trades are not hidden by
                        // a previously active market, date, side, or search filter.
                        setLiveFilters(buildDefaultTradesPageFilters());
                        setMarketFilter('ALL');
                        setPage(1);
                    }
                }
                removePrivateTradingValue('import-result', 'session');
                return;
            }

            if (typeof window !== 'undefined') {
                const params = new URLSearchParams(window.location.search || '');
                const success = params.get('success');
                if (success === 'true' || success === '1') {
                    const tradeCount = Number(params.get('count')) || 0;
                    const executionCount =
                        Number(params.get('executions')) ||
                        Number(params.get('executionCount')) ||
                        tradeCount;
                    const rejectedCount =
                        Number(params.get('rejected')) ||
                        Number(params.get('rejectedCount')) ||
                        0;
                    const duplicateCount =
                        Number(params.get('duplicates')) ||
                        Number(params.get('duplicateCount')) ||
                        0;
                    const skippedInvalidCount =
                        Number(params.get('skippedInvalid')) ||
                        Number(params.get('skippedInvalidCount')) ||
                        Number(params.get('skipped')) ||
                        0;
                    if (tradeCount > 0 || duplicateCount > 0 || skippedInvalidCount > 0 || rejectedCount > 0) {
                        showImportBanner({
                            success: true,
                            count: tradeCount,
                            executionCount,
                            duplicateCount,
                            skippedInvalidCount,
                            rejectedCount,
                            rowsRead: Number(params.get('rowsRead')) || undefined,
                            message: 'CSV imported successfully.',
                        });
                    }
                }
            }
        } catch {}
    }, []);

    useEffect(() => {
        if (!importResult) return;
        setImportBannerFading(false);

        const fadeTimer = window.setTimeout(() => {
            setImportBannerFading(true);
        }, 9500);

        const dismissTimer = window.setTimeout(() => {
            setImportResult(null);
            setImportBannerFading(false);
        }, 10000);

        return () => {
            window.clearTimeout(fadeTimer);
            window.clearTimeout(dismissTimer);
        };
    }, [importResult]);

    // normalizeHeaderKey removed (unused)

    // parseDateCell removed (unused)

    function hashStringToInt(input: string): number {
        let h = 0;
        for (let i = 0; i < input.length; i++) {
            h = (h * 31 + input.charCodeAt(i)) | 0;
        }
        return Math.abs(h || 1);
    }



    const formatTradeSizeValue = (value: number): string => {
        if (!Number.isFinite(value)) return '';
        return formatQuantityDisplayValue(value);
    };

    const getTradeSizeContext = (trade: Trade): {
        market: MarketCategory;
        maxSize: number | null;
        closedQty: number | null;
        isScaled: boolean;
    } => {
        const rawTrade = trade as any;
        const market = getTradeMarketCategory(rawTrade);
        const rawMaxSize = rawTrade.volume ?? rawTrade.quantity ?? rawTrade.shares;
        const maxSize = market === 'FOREX'
            ? toDisplayForexLots(rawMaxSize)
            : (() => {
                const n = Number(rawMaxSize);
                return Number.isFinite(n) ? Math.abs(n) : null;
            })();

        const executions = Array.isArray(rawTrade.executionsList)
            ? rawTrade.executionsList
            : Array.isArray(rawTrade.executions)
                ? rawTrade.executions
                : [];

        if (executions.length === 0) {
            return { market, maxSize, closedQty: null, isScaled: false };
        }

        let buyQty = 0;
        let sellQty = 0;

        for (const execution of executions) {
            const rawQty =
                (execution as any).qty ??
                (execution as any).quantity ??
                (execution as any).volume ??
                (execution as any).shares;
            const qty = market === 'FOREX'
                ? (toDisplayForexLots(rawQty) ?? 0)
                : (() => {
                    const n = Number(rawQty);
                    return Number.isFinite(n) ? Math.abs(n) : 0;
                })();
            if (!qty) continue;

            const side = normalizeTradeSide(
                (execution as any).side ?? (execution as any).action ?? (execution as any).type,
                rawQty,
            );
            if (side === 'sell') sellQty += qty;
            else buyQty += qty;
        }

        const openingSide = normalizeTradeSide(rawTrade.side, rawTrade.volume)
            ?? normalizeTradeSide((executions[0] as any)?.side, (executions[0] as any)?.qty ?? (executions[0] as any)?.quantity ?? (executions[0] as any)?.volume ?? (executions[0] as any)?.shares)
            ?? 'buy';
        const closedQty = openingSide === 'sell' ? buyQty : sellQty;
        const tolerance = market === 'FOREX' ? 1e-6 : 1e-9;
        const isScaled = maxSize != null && closedQty > maxSize + tolerance;

        return {
            market,
            maxSize,
            closedQty: Number.isFinite(closedQty) ? closedQty : null,
            isScaled,
        };
    };

    // Convert numeric milliseconds into a human-friendly string used by the table
    function formatDurationMs(durationMs?: number | null): string {
        if (!Number.isFinite(Number(durationMs))) return '';
        const ms = Math.max(0, Number(durationMs));
        if (ms < 60000) {
            const seconds = Math.floor(ms / 1000);
            return `${seconds}s`;
        }

        const mins = Math.max(0, Math.round(ms / 60000));
        const days = Math.floor(mins / 1440);
        const hours = Math.floor((mins % 1440) / 60);
        const rem = mins % 60;

        const parts: string[] = [];
        if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`); // spelled-out "days"
        if (hours > 0) parts.push(`${hours}h`);
        // always show minutes when nothing else or when rem > 0
        if (rem > 0 || parts.length === 0) parts.push(`${rem}min`); // use "min" instead of "m"
        return parts.join(' ');
    }

    // Debug helpers removed — diagnostics/migration UI has been cleaned up


    const settingsButtonRef = useRef<HTMLButtonElement | null>(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const SETTINGS_KEY = 'trades_view_columns';

    // tradeInfoItems removed (unused) — the modal uses localTradeInfo inside TableColumnsModalInline

    const TOTAL_FEES_TOOLTIP = 'Includes commissions, exchange fees, regulatory fees, and other trade-related fees when available.';
    const COLUMN_FIT_MESSAGE = 'No more columns can fit at the current width. Hide another column or reduce widened column sizes.';

    const [pendingSettings, setPendingSettings] = useState<Record<string, boolean> | null>(null);
    const [pendingPresetIntent, setPendingPresetIntent] = useState<ColumnPresetName | null>(null);
    const [tradeExcursionsByKey, setTradeExcursionsByKey] = useState<Record<string, TradeExcursionSummary | false>>({});
    const pendingTradeExcursionKeysRef = useRef<Set<string>>(new Set());

    // In order to safely compute default visible columns, define column keys and defaults
    // before computeDefaultVisible is used.
    type ColKey =
        | 'opened'
        | 'closed'
        | 'tradeId'
        | 'symbol'
        | 'side'
        | 'volume'
        | 'executions'
        | 'pnl'
        | 'pnlR'
        | 'pnlTicksGrossFutures'
        | 'entryPrice'
        | 'exitPrice'
        | 'initialRisk'
        | 'positionMFE'
        | 'positionMAE'
        | 'totalFees'
        | 'duration'
        | 'notes'
        | 'tags';

    type ColumnSection = 'Trade Information' | 'Time & Price' | 'Performance Metrics' | 'Risk Analysis';
    type ColumnPresetName = 'Default' | 'Advanced' | 'Futures-Focused';
    type ColumnConfig = {
        key: ColKey;
        settingsKey: string;
        label: string;
        section: ColumnSection;
        defaultVisible: boolean;
        tooltip?: string;
        presets: Record<ColumnPresetName, boolean>;
    };

    // Single source of truth: canonical order, labels, sections, default visibility, and preset readiness.
    const COLUMN_CONFIG: ColumnConfig[] = [
        { key: 'tradeId', settingsKey: 'tradeId', label: 'TradeId', section: 'Trade Information', defaultVisible: true, presets: { 'Default': true, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'opened', settingsKey: 'opened', label: 'Opened', section: 'Time & Price', defaultVisible: true, presets: { 'Default': true, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'symbol', settingsKey: 'symbol', label: 'Symbol', section: 'Trade Information', defaultVisible: true, presets: { 'Default': true, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'side', settingsKey: 'side', label: 'Side', section: 'Trade Information', defaultVisible: true, presets: { 'Default': true, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'volume', settingsKey: 'volume', label: 'Max Size', section: 'Trade Information', defaultVisible: false, presets: { 'Default': false, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'executions', settingsKey: 'executionCount', label: 'Executions', section: 'Trade Information', defaultVisible: false, presets: { 'Default': false, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'entryPrice', settingsKey: 'entryPrice', label: 'Entry Price', section: 'Time & Price', defaultVisible: true, presets: { 'Default': true, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'exitPrice', settingsKey: 'exitPrice', label: 'Exit Price', section: 'Time & Price', defaultVisible: true, presets: { 'Default': true, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'totalFees', settingsKey: 'totalFees', label: 'Total Fees', section: 'Risk Analysis', defaultVisible: true, tooltip: TOTAL_FEES_TOOLTIP, presets: { 'Default': true, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'pnl', settingsKey: 'pnl', label: 'Profit & Loss', section: 'Performance Metrics', defaultVisible: true, presets: { 'Default': true, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'duration', settingsKey: 'duration', label: 'Duration', section: 'Time & Price', defaultVisible: true, presets: { 'Default': true, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'pnlR', settingsKey: 'pnlR', label: 'Profit & Loss (R)', section: 'Performance Metrics', defaultVisible: false, presets: { 'Default': false, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'pnlTicksGrossFutures', settingsKey: 'pnlTicksGrossFutures', label: isStocksOnly ? 'P&L (Ticks)' : 'P&L (Ticks) · Futures Only', section: 'Performance Metrics', defaultVisible: false, presets: { 'Default': false, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'initialRisk', settingsKey: 'initialRisk', label: 'Initial Risk', section: 'Risk Analysis', defaultVisible: false, presets: { 'Default': false, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'positionMAE', settingsKey: 'positionMAE', label: 'Position MAE', section: 'Risk Analysis', defaultVisible: false, presets: { 'Default': false, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'positionMFE', settingsKey: 'positionMFE', label: 'Position MFE', section: 'Risk Analysis', defaultVisible: false, presets: { 'Default': false, 'Advanced': true, 'Futures-Focused': true } },
        { key: 'closed', settingsKey: 'closed', label: 'Closed', section: 'Time & Price', defaultVisible: false, presets: { 'Default': false, 'Advanced': true, 'Futures-Focused': false } },
        { key: 'notes', settingsKey: 'notes', label: 'Notes', section: 'Trade Information', defaultVisible: true, presets: { 'Default': true, 'Advanced': true, 'Futures-Focused': false } },
        { key: 'tags', settingsKey: 'tags', label: 'Tags', section: 'Trade Information', defaultVisible: true, presets: { 'Default': true, 'Advanced': true, 'Futures-Focused': true } },
    ];

    const DEFAULT_TRADES_SETTINGS: { key: string; label: string; tooltip?: string; locked?: boolean; default?: boolean }[] = COLUMN_CONFIG.map((column) => ({
        key: column.settingsKey,
        label: column.label,
        tooltip: column.tooltip,
        default: column.defaultVisible,
    }));

    // Preset persistence key
    const PRESET_KEY = 'trades_view_columns_preset';

    // Build preset -> settingsKey mapping from COLUMN_CONFIG for exact-match checks
    const PRESET_NAMES: ColumnPresetName[] = ['Default', 'Advanced', 'Futures-Focused'];
    const PRESET_TO_SETTINGS_MAP: Record<ColumnPresetName, Record<string, boolean>> = PRESET_NAMES.reduce((acc, name) => {
        const map: Record<string, boolean> = {};
        for (const col of COLUMN_CONFIG) {
            map[col.settingsKey] = !!col.presets[name];
        }
        acc[name] = map;
        return acc;
    }, {} as Record<ColumnPresetName, Record<string, boolean>>);




    const COL_ORDER: ColKey[] = COLUMN_CONFIG.map((column) => column.key);
    const COLUMN_ORDER_KEY = 'trading-tracker:trades-page:columnOrder:v1';
    const PRESET_FALLBACK_PRIORITY: Record<ColumnPresetName, ColKey[]> = {
        'Default': ['notes', 'tags', 'duration', 'totalFees'],
        'Advanced': ['closed', 'notes', 'tags', 'positionMFE', 'positionMAE', 'initialRisk', 'pnlTicksGrossFutures'],
        'Futures-Focused': ['tags', 'totalFees', 'positionMFE', 'positionMAE', 'initialRisk', 'pnlTicksGrossFutures'],
    };

    const LEGACY_SETTING_KEY_MAP: Record<string, ColKey | undefined> = {
        date: 'opened',
        openDate: 'opened',
        openTime: 'opened',
        closeDate: 'closed',
        closeTime: 'closed',
        executionCount: 'executions',
        commission: 'totalFees',
        commissionAndFees: 'totalFees',
        commissionsAndFees: 'totalFees',
        result: undefined,
        pnlPercentEquities: undefined,
        pnlTicksPerContractGrossFutures: undefined,
        priceMAE: undefined,
        priceMFE: undefined,
        exitEfficiencyPct: undefined,
        exitEfficiencyWinnersOnly: undefined,
        options: undefined,
        bestExitPnl: undefined,
        shared: undefined,
    };

    const SETTINGS_TO_COLKEY: Record<string, ColKey | undefined> = COLUMN_CONFIG.reduce((acc, column) => {
        acc[column.settingsKey] = column.key;
        return acc;
    }, { executions: 'executions' as ColKey } as Record<string, ColKey | undefined>);

    const migrateSettingKeyToColKey = (rawKey: string): ColKey | undefined => {
        const key = String(rawKey ?? '').trim();
        if (!key) return undefined;
        if (SETTINGS_TO_COLKEY[key]) return SETTINGS_TO_COLKEY[key];
        if ((COL_ORDER as string[]).includes(key)) return key as ColKey;
        return LEGACY_SETTING_KEY_MAP[key];
    };

    // Map canonical column key back to the settings key used in DEFAULT_TRADES_SETTINGS
    const COLKEY_TO_SETTING_KEY: Record<ColKey, string> = COLUMN_CONFIG.reduce((acc, column) => {
        acc[column.key] = column.settingsKey;
        return acc;
    }, {} as Record<ColKey, string>);

    // Normalize stored settings into the SETTINGS-keyed shape expected by the modal
    const normalizeStoredSettings = (input: Record<string, boolean> | null | undefined): Record<string, boolean> => {
        const next: Record<string, boolean> = {};
        for (const [rawKey, rawValue] of Object.entries(input ?? {})) {
            const mapped = migrateSettingKeyToColKey(rawKey);
            if (!mapped) continue;
            const settingsKey = COLKEY_TO_SETTING_KEY[mapped];
            if (!settingsKey) continue;
            const prev = !!next[settingsKey];
            next[settingsKey] = prev || !!rawValue;
        }
        return next;
    };

    const normalizeStoredColumnOrder = (input: unknown): ColKey[] => {
        const seen = new Set<ColKey>();
        const ordered: ColKey[] = [];

        if (Array.isArray(input)) {
            for (const raw of input) {
                const mapped = migrateSettingKeyToColKey(String(raw ?? ''));
                if (!mapped) continue;
                if (seen.has(mapped)) continue;
                seen.add(mapped);
                ordered.push(mapped);
            }
        }

        for (const key of COL_ORDER) {
            if (seen.has(key)) continue;
            ordered.push(key);
        }

        return ordered;
    };

    // Strict defaults used by first load and Reset Default (not data-driven).
    const computeSettingsDefaults = () => {
        return DEFAULT_TRADES_SETTINGS.reduce((acc, cur) => ({ ...acc, [cur.key]: !!cur.default }), {} as Record<string, boolean>);
    };

    const computeDefaultVisible = (): Record<ColKey, boolean> => {
        const defaults = {} as Record<ColKey, boolean>;
        for (const k of COL_ORDER) defaults[k] = false;
        for (const s of DEFAULT_TRADES_SETTINGS) {
            const col = SETTINGS_TO_COLKEY[s.key];
            if (col) defaults[col] = !!s.default;
        }
        return defaults;
    };

    const settingsSelectionToVisibleCols = (settings: Record<string, boolean> | null | undefined): Record<ColKey, boolean> => {
        const next = { ...computeDefaultVisible() };
        for (const [key, value] of Object.entries(settings ?? {})) {
            const mapped = SETTINGS_TO_COLKEY[key];
            if (mapped) next[mapped] = !!value;
        }
        return next;
    };

    const [visibleCols, setVisibleCols] = useState<Record<ColKey, boolean>>(computeDefaultVisible());
    const [colOrder, setColOrder] = useState<ColKey[]>(() => COL_ORDER.slice());

    useEffect(() => {
        try {
            const raw = localStorage.getItem(COLUMN_ORDER_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            const cleaned = normalizeStoredColumnOrder(parsed);
            setColOrder(cleaned);
        } catch {}
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(COLUMN_ORDER_KEY, JSON.stringify(colOrder));
        } catch {}
    }, [colOrder]);

    function applySettingsFromStorage() {
        try {
            // If a preset id is stored, prefer it (recommended persistence behavior)
            try {
                const presetRaw = localStorage.getItem(PRESET_KEY);
                if (presetRaw === 'Default' || presetRaw === 'Advanced' || presetRaw === 'Futures-Focused') {
                    setVisibleCols(settingsSelectionToVisibleCols(PRESET_TO_SETTINGS_MAP[presetRaw as ColumnPresetName]));
                    return;
                }
            } catch {}
            const raw = localStorage.getItem(SETTINGS_KEY);
            if (raw) {
                const parsed = normalizeStoredSettings(JSON.parse(raw) as Record<string, boolean>);
                const next = settingsSelectionToVisibleCols(parsed);
                try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(parsed)); } catch {}
                setVisibleCols(next);
                return;
            }
        } catch {}
        setVisibleCols(computeDefaultVisible());
    }

    useEffect(() => {
        applySettingsFromStorage();
        const onStorage = (e: StorageEvent) => {
            if (e.key === SETTINGS_KEY || e.key === PRESET_KEY) applySettingsFromStorage();
        };
        window.addEventListener('storage', onStorage);
        // Also intercept same-window localStorage.setItem so saves apply immediately.
        const originalSet = (localStorage as any).setItem;
        (localStorage as any).setItem = function (this: Storage, k: string, v: string) {
            try {
                originalSet.call(this, k, v);
            } catch {
                // ignore
            }
            try {
                if (k === SETTINGS_KEY) {
                        // apply immediately
                        try {
                            const parsed = normalizeStoredSettings(JSON.parse(v));
                            const next = settingsSelectionToVisibleCols(parsed);
                            setVisibleCols(next);
                        } catch {}
                    }
                    if (k === PRESET_KEY) {
                        try {
                            const presetRaw = String(v);
                            if (presetRaw === 'Default' || presetRaw === 'Advanced' || presetRaw === 'Futures-Focused') {
                                setVisibleCols(settingsSelectionToVisibleCols(PRESET_TO_SETTINGS_MAP[presetRaw as ColumnPresetName]));
                            }
                        } catch {}
                }
            } catch {}
        } as any;

        return () => {
            window.removeEventListener('storage', onStorage);
            try { (localStorage as any).setItem = originalSet; } catch {};
        };
    }, []);

    // Re-apply whenever the settings menu closes (covers Save behavior)
    useEffect(() => {
        if (!settingsOpen) applySettingsFromStorage();
    }, [settingsOpen]);

    // The Table Columns modal is rendered in a fullscreen portal backdrop that already
    // handles outside clicks. A second click-away listener here can race with in-modal
    // pointer events and make controls feel non-responsive.

    // Settings popup is centered via fixed flex container
    const [activeView, setActiveView] = useState<'Gross' | 'Net'>('Net');
    const [sideMenuActive, setSideMenuActive] = useState<boolean>(() => {
        try {
            return typeof document !== 'undefined' && !!document.querySelector('.sidebar-root');
        } catch {
            return false;
        }
    });

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const mo = new MutationObserver(() => {
            try { setSideMenuActive(!!document.querySelector('.sidebar-root')); } catch {}
        });
        mo.observe(document.body, { childList: true, subtree: true });
        return () => mo.disconnect();
    }, []);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(30);
    const [historyStatus, setHistoryStatus] = useState(getPersistedTradeHistoryStatus);
    useEffect(() => subscribePersistedTradeHistoryStatus(() => setHistoryStatus(getPersistedTradeHistoryStatus())), []);
    const [stickyTradesHeaderTop, setStickyTradesHeaderTop] = useState(18);
    const stickyHeaderTableRef = useRef<HTMLTableElement | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [selectedTradingAccountId, setSelectedTradingAccountId] = useState('');
    const [manualTradeAccounts, setManualTradeAccounts] = useState<CsvSourceAccountOption[]>([]);
    const [manualTradeAccountsLoading, setManualTradeAccountsLoading] = useState(true);
    const [manualTradeAccountsError, setManualTradeAccountsError] = useState('');
    const [showRejectedTradesButton, setShowRejectedTradesButton] = useState(false);

    const manualTradeAccountRequestRef = useRef(0);
    const refreshManualTradeAccounts = useCallback(async () => {
        const request = ++manualTradeAccountRequestRef.current;
        setManualTradeAccountsLoading(true);
        setManualTradeAccountsError('');
        try {
            const accounts = await listCsvSourceAccounts();
            if (request !== manualTradeAccountRequestRef.current) return;
            const activeAccounts = accounts.filter((account) => account.status === 'active');
            setManualTradeAccounts(activeAccounts);
            const persistedAccountId = readPrivateTradingValue<string>('trades-selected-account');
            setSelectedTradingAccountId((current) => resolveSelectedTradingAccountId(activeAccounts, current, persistedAccountId));
        } catch (error) {
            if (request === manualTradeAccountRequestRef.current) {
                setManualTradeAccounts([]);
                setSelectedTradingAccountId('');
                setManualTradeAccountsError(error instanceof Error ? error.message : 'Saved trading accounts could not be loaded.');
            }
        } finally {
            if (request === manualTradeAccountRequestRef.current) setManualTradeAccountsLoading(false);
        }
    }, []);

    useEffect(() => {
        void refreshManualTradeAccounts();
        const refreshAccounts = () => { void refreshManualTradeAccounts(); };
        const refreshAccountsForPrivateScope = () => {
            setManualTradeAccounts([]);
            setSelectedTradingAccountId('');
            void refreshManualTradeAccounts();
        };
        window.addEventListener('csv-source-accounts-updated', refreshAccounts);
        window.addEventListener('trade-proof-csv-account-cleared', refreshAccounts);
        window.addEventListener(privateTradingScopeChangedEventName(), refreshAccountsForPrivateScope);
        return () => {
            manualTradeAccountRequestRef.current += 1;
            window.removeEventListener('csv-source-accounts-updated', refreshAccounts);
            window.removeEventListener('trade-proof-csv-account-cleared', refreshAccounts);
            window.removeEventListener(privateTradingScopeChangedEventName(), refreshAccountsForPrivateScope);
        };
    }, [createOpen, refreshManualTradeAccounts]);

    const handleCreateManualTrade = async (payload: ManualEntryPayload) => {
        if (!payload.accountId) throw new Error('Select an active trading account.');
        const idempotencyKey = globalThis.crypto?.randomUUID?.()
            || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
                const random = Math.random() * 16 | 0;
                return (character === 'x' ? random : (random & 0x3 | 0x8)).toString(16);
            });
        const saved = await createManualTrade({ ...payload, accountId: payload.accountId, idempotencyKey });
        const generation = tradeRefreshGenerationRef.current;
        const request = ++tradeRefreshRequestRef.current;
        const refreshPromise = loadTrades().then((data) => {
            if (generation !== tradeRefreshGenerationRef.current || request !== tradeRefreshRequestRef.current) return false;
            const freshTrades = Array.isArray(data) ? data : [];
            setTrades(freshTrades);
            lastKnownTradesSnapshot = freshTrades;
            setSelectedTrades(new Set());
            setPage(1);
            return true;
        }).catch((error) => {
            console.error('[TradesPage] manual trade persisted but authoritative refresh failed', error);
            return false;
        });

        const hasFiltersThatMayHideTrade = marketFilter !== 'ALL'
            || liveFilters.timeframe !== 'alltime'
            || Boolean(liveFilters.fromDate || liveFilters.toDate || liveFilters.searchTerm.trim())
            || liveFilters.sideFilter !== 'All'
            || sortBy !== null;
        const viewTrade = () => {
            setLiveFilters(buildDefaultTradesPageFilters());
            setMarketFilter('ALL');
            setSortBy(null);
            setPage(1);
            const retryGeneration = ++tradeRefreshGenerationRef.current;
            const retryRequest = ++tradeRefreshRequestRef.current;
            void loadTrades().then((data) => {
                if (retryGeneration !== tradeRefreshGenerationRef.current || retryRequest !== tradeRefreshRequestRef.current) return;
                const freshTrades = Array.isArray(data) ? data : [];
                setTrades(freshTrades);
                lastKnownTradesSnapshot = freshTrades;
                setSelectedTrades(new Set());
            }).catch((error) => console.error('[TradesPage] retry after manual trade save failed', error));
        };

        void refreshPromise.then((refreshed) => {
            window.dispatchEvent(new CustomEvent('tt:toast', {
                detail: {
                    title: saved.created ? 'Trade Saved' : 'Trade Already Saved',
                    message: `${payload.symbol} ${saved.created ? 'was saved' : 'is already saved'} to ${saved.accountName}.${refreshed ? '' : ' The Trades list could not be refreshed yet.'}`,
                    variant: saved.created ? 'success' : 'info',
                    duration: 6000,
                    ...(hasFiltersThatMayHideTrade ? { actionLabel: 'View trade', onAction: viewTrade } : {}),
                },
            }));
        });
    };

    type TradesPageFilters = {
        timeframe: TimeframeValue;
        fromDate?: string;
        toDate?: string;
        sideFilter: SideFilterValue;
        searchTerm: string;
        advancedFilters: JournalFilters;
    };

    function buildDefaultTradesPageFilters(): TradesPageFilters {
        return {
            timeframe: 'alltime',
            fromDate: undefined,
            toDate: undefined,
            sideFilter: 'All',
            searchTerm: '',
            advancedFilters: emptyJournalFilters(),
        };
    }
    const [trades, setTrades] = useState<Trade[]>(() => getInitialTradesSnapshot());
    const tradeRefreshGenerationRef = useRef(0);
    const tradeRefreshRequestRef = useRef(0);
    const [hasInitialTradesLoaded, setHasInitialTradesLoaded] = useState(false);
    const tradesForSelectedTradingAccount = useMemo(
        () => filterTradesForSelectedTradingAccount(trades as any[], selectedTradingAccountId),
        [trades, selectedTradingAccountId]
    );
    const groupedTrades = useGroupedTrades(tradesForSelectedTradingAccount);

    useEffect(() => {
        if (demoTradesActive) return;
        if (Array.isArray(trades) && trades.length > 0) {
            lastKnownTradesSnapshot = trades;
        }
    }, [demoTradesActive, trades]);
    const importedTradesByStorageId = useMemo(() => {
        const lookup = new Map<string, Trade>();
        for (const trade of trades) {
            const storageId = String((trade as any)?.storageId ?? (trade as any)?.storageID ?? '').trim();
            if (!storageId) continue;
            lookup.set(storageId, trade);
        }
        return lookup;
    }, [trades]);
    const isOpenLikeTradeForRender = useCallback(
        (trade: any) => isOpenLikeTrade(trade, importedTradesByStorageId),
        [importedTradesByStorageId]
    );
    const [marketFilter, setMarketFilter] = useState<'ALL'|'STOCKS'|'FUTURES'|'FOREX'|'CRYPTO'>(() => {
        try {
            const raw = localStorage.getItem('tt_trades_market_filter');
            if (raw === 'ALL' || raw === 'STOCKS' || raw === 'FUTURES' || raw === 'FOREX' || raw === 'CRYPTO') return raw;
        } catch {}
        return 'ALL';
    });
    const isColumnSupportedForAssetClasses = useCallback(
        (key: ColKey): boolean => {
            if (key === 'pnlTicksGrossFutures') return isAssetClassEnabled('FUTURES', assetVisibility);
            return true;
        },
        [assetVisibility]
    );

    useEffect(() => {
        setMarketFilter((current) => coerceMarketTypeToVisible(current, assetVisibility, { fallbackToAll: true }));
    }, [assetVisibility]);
    const [liveFilters, setLiveFilters] = useState<TradesPageFilters>(() => buildDefaultTradesPageFilters());
    const handleTopFiltersChange = useCallback((payload: {
        timeframe: TimeframeValue;
        fromDate?: string;
        toDate?: string;
        sideFilter: SideFilterValue;
        searchTerm: string;
        advancedFilters: JournalFilters;
    }) => {
        setLiveFilters((prev) => ({
            ...prev,
            timeframe: payload.timeframe,
            fromDate: payload.fromDate,
            toDate: payload.toDate,
            sideFilter: payload.sideFilter,
            searchTerm: payload.searchTerm,
            advancedFilters: payload.advancedFilters,
        }));
    }, []);
    const hasHydratedTradesRef = useRef(false);
    const skipFirstPersistRef = useRef(true);
    const [selectedTrades, setSelectedTrades] = useState<Set<string>>(new Set());
    useEffect(() => {
        if (!demoTradesActive) return;
        setTrades(demoTrades);
        setSelectedTrades(new Set());
        setMarketFilter('ALL');
        setPage(1);
        setHasInitialTradesLoaded(true);
    }, [demoTrades, demoTradesActive]);
    const [confirmDeleteSelectedOpen, setConfirmDeleteSelectedOpen] = useState(false);
    const [deletingSelectedTrades, setDeletingSelectedTrades] = useState(false);
    const [deleteSelectedError, setDeleteSelectedError] = useState('');
    const [confirmDeleteAllOpen, setConfirmDeleteAllOpen] = useState(false);
    const [deletingAllTrades, setDeletingAllTrades] = useState(false);
    const [deleteAllError, setDeleteAllError] = useState('');
    const [isTagsModalOpen, setIsTagsModalOpen] = useState(false);
    const [tagModalTargetKey, setTagModalTargetKey] = useState<string | null>(null); // when set, Tags modal targets a single trade
    const [editingTag, setEditingTag] = useState<string | null>(null);
    const [isDeleteTagOpen, setIsDeleteTagOpen] = useState(false);
    const [isMergeOpen, setIsMergeOpen] = useState(false);
    const [isSplitOpen, setIsSplitOpen] = useState(false);
    const [riskModalTrade, setRiskModalTrade] = useState<Trade | null>(null);
    const [hoveredRowKey, setHoveredRowKey] = useState<string | null>(null);
    const [riskModalMethod, setRiskModalMethod] = useState<InitialRiskMethod>('amount');
    const [riskAmountInput, setRiskAmountInput] = useState<string>('');
    const [stopPriceInput, setStopPriceInput] = useState<string>('');
    const defaultRiskPerTrade = readTradingSettings().riskRules.defaultRiskPerTrade;

    // Prefer immediate live updates for search; debounce removed per UX guardrails.

    // Inline notes editing state
    const [editingNoteKey, setEditingNoteKey] = useState<string | null>(null);
    const [editingNoteValue, setEditingNoteValue] = useState<string>('');
    const noteInputRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
        if (editingNoteKey && noteInputRef.current) noteInputRef.current.focus();
    }, [editingNoteKey]);

    // Show one-time Rejected Trades button if a rejection log exists and hasn't been inspected
    useEffect(() => {
        try {
            const raw = readPrivateTradingJson('import-rejections', 'session', ['tt_import_rejections']);
            const inspected = sessionStorage.getItem('tt_rejected_inspected');
            setShowRejectedTradesButton(!!raw && !inspected);
        } catch {
            setShowRejectedTradesButton(false);
        }
    }, []);
    function openTradeDetails(trade: Trade) {
        // Keep a persistent fallback so refresh/back/edge-cases always show the last clicked trade.
        try {
            writePrivateTradingJson('last-view-trade', JSON.stringify(trade), 'session');
        } catch {}

        // Prefer the App-provided in-app navigation hook.
        const view = (window as any).__TT_viewTrade;
        if (typeof view === 'function') {
            try {
                view(trade);
                return;
            } catch {
                // fall through
            }
        }

        const nav = (window as any).__TT_navigate;
        if (typeof nav === 'function') {
            try { nav('Trade Details'); } catch {}
        }
    }

    const [sortBy, setSortBy] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    const [colWidths, setColWidths] = useState<Partial<Record<ColKey, number>>>({});
    const [manualColWidthOverrides, setManualColWidthOverrides] = useState<Partial<Record<ColKey, number>>>({});
    const [tableWidthPx, setTableWidthPx] = useState<number | null>(null);
    const [availableTableWidthPx, setAvailableTableWidthPx] = useState<number | null>(null);
    const [columnFitMessage, setColumnFitMessage] = useState<string | null>(null);
    // side column must stay wide enough for LONG/SHORT pill + reversal badge
    const SIDE_MIN_PX = 96; // ~6rem plus some padding
    // Width groups metadata (used for preset-aware balanced layout)
    const COLUMN_WIDTH_META: Record<ColKey, { group: 'compact' | 'standard' | 'expanded'; min: number; preferred: number; max?: number }> = {
        // Keep the trade name/ID readable beside its selection checkbox.
        tradeId: { group: 'standard', min: 144, preferred: 160 },
        opened: { group: 'expanded', min: 160, preferred: 220, max: 380 },
        symbol: { group: 'standard', min: 110, preferred: 160 },
        // side needs a larger min for pills; keep it slightly larger but treat as standard
        side: { group: 'standard', min: SIDE_MIN_PX, preferred: 120 },
        // increase previously-compact columns to standard so they are not unusually short
        volume: { group: 'standard', min: 100, preferred: 140 },
        executions: { group: 'standard', min: 88, preferred: 110 },
        entryPrice: { group: 'expanded', min: 140, preferred: 200 },
        exitPrice: { group: 'expanded', min: 140, preferred: 200 },
        totalFees: { group: 'standard', min: 100, preferred: 140 },
        pnl: { group: 'standard', min: 120, preferred: 160 },
        duration: { group: 'standard', min: 84, preferred: 110 },
        pnlR: { group: 'standard', min: 100, preferred: 140 },
        pnlTicksGrossFutures: { group: 'standard', min: 100, preferred: 140 },
        initialRisk: { group: 'standard', min: 100, preferred: 140 },
        positionMAE: { group: 'standard', min: 100, preferred: 140 },
        positionMFE: { group: 'standard', min: 100, preferred: 140 },
        closed: { group: 'expanded', min: 160, preferred: 220 },
        notes: { group: 'standard', min: 120, preferred: 140 },
        tags: { group: 'standard', min: 100, preferred: 120 },
    };

    const getColumnMinWidth = (key: ColKey): number => {
        const meta = COLUMN_WIDTH_META[key];
        const min = Math.max(60, Math.round(meta?.min ?? 80));
        return key === 'side' ? Math.max(min, SIDE_MIN_PX) : min;
    };

    const normalizeColumnWidthValue = (key: ColKey, raw: unknown): number => {
        const meta = COLUMN_WIDTH_META[key];
        const min = getColumnMinWidth(key);
        const max = Math.max(min, Math.round(meta?.max ?? 1600));
        const parsed = Math.round(Number(raw));
        if (!Number.isFinite(parsed) || parsed <= 0) return min;
        return Math.min(max, Math.max(min, parsed));
    };

    const buildWidthPlan = (
        visibleMap: Record<ColKey, boolean>,
        overrides?: Partial<Record<ColKey, number>>,
        availablePx?: number | null,
    ): { widths: Partial<Record<ColKey, number>>; total: number; fits: boolean; visibleKeys: ColKey[] } => {
        const visibleKeys = colOrder.filter((k) => !!visibleMap[k] && isColumnSupportedForAssetClasses(k));
        const requestedWidths: Partial<Record<ColKey, number>> = {};
        const minimumWidths: Partial<Record<ColKey, number>> = {};
        for (const key of visibleKeys) {
            const minimum = getColumnMinWidth(key);
            minimumWidths[key] = minimum;
            requestedWidths[key] = normalizeColumnWidthValue(key, overrides?.[key] ?? minimum);
        }
        const fitted = shrinkColumnWidthsToFit({
            visibleKeys,
            requestedWidthByKey: requestedWidths,
            minimumWidthByKey: minimumWidths,
            availableWidth: availablePx ?? null,
        });
        const widths = fitted.widths;
        let total = fitted.total;
        if (typeof availablePx === 'number' && availablePx > 0 && total < availablePx) {
            const expandableKeys = visibleKeys.filter((key) => key !== 'tradeId');
            if (expandableKeys.length > 0) {
                let remainingExtra = availablePx - total;
                expandableKeys.forEach((key, index) => {
                    const share = index === expandableKeys.length - 1
                        ? remainingExtra
                        : Math.floor(remainingExtra / (expandableKeys.length - index));
                    widths[key] = (widths[key] ?? getColumnMinWidth(key)) + share;
                    remainingExtra -= share;
                });
                total = availablePx;
            }
        }
        const fits = fitted.fits;
        return { widths, total, fits, visibleKeys };
    };

    const RESPONSIVE_AUTO_HIDE_PRIORITY: ColKey[] = [
        'notes',
        'tags',
        'closed',
        'positionMFE',
        'positionMAE',
        'initialRisk',
        'pnlTicksGrossFutures',
        'pnlR',
        'executions',
        'volume',
        'duration',
        'totalFees',
        'exitPrice',
        'entryPrice',
        'side',
        'opened',
        'pnl',
        'symbol',
    ];

    const responsiveColumnFit = useMemo(() => {
        const requestedKeys = colOrder.filter((key) => (
            !!visibleCols[key] && isColumnSupportedForAssetClasses(key)
        ));
        // Responsive visibility is determined by the columns' true minimum footprint.
        // Saved/manual widths are reclaimed by buildWidthPlan before a selected column
        // is hidden, so a previously widened column cannot make the table display fewer
        // columns than the current viewport can actually accommodate.
        const widthByKey = requestedKeys.reduce((widths, key) => {
            widths[key] = getColumnMinWidth(key);
            return widths;
        }, {} as Partial<Record<ColKey, number>>);

        return fitTradeTableColumnsToWidth({
            visibleKeys: requestedKeys,
            widthByKey,
            availableWidth: availableTableWidthPx,
            hidePriority: RESPONSIVE_AUTO_HIDE_PRIORITY,
        });
    }, [
        availableTableWidthPx,
        assetVisibility,
        colOrder,
        visibleCols,
    ]);

    const responsiveVisibleCols = useMemo(() => {
        const visibleSet = new Set(responsiveColumnFit.visibleKeys);
        return COL_ORDER.reduce((map, key) => {
            map[key] = visibleSet.has(key);
            return map;
        }, {} as Record<ColKey, boolean>);
    }, [responsiveColumnFit.visibleKeys]);



    const resolvePresetForFit = (preset: ColumnPresetName) => {
        const baseVisible = settingsSelectionToVisibleCols(PRESET_TO_SETTINGS_MAP[preset]);
        const removable = PRESET_FALLBACK_PRIORITY[preset];
        const removableSet = new Set<ColKey>(removable);
        const required = colOrder.filter((k) => !!baseVisible[k] && isColumnSupportedForAssetClasses(k) && !removableSet.has(k));
        const requiredSet = new Set<ColKey>(required);

        const nextVisible: Record<ColKey, boolean> = { ...baseVisible };
        const widthOverrides: Partial<Record<ColKey, number>> = { ...manualColWidthOverrides };
        const hiddenKeys: ColKey[] = [];

        let plan = buildWidthPlan(nextVisible, widthOverrides, availableTableWidthPx);

        for (const key of removable) {
            if (plan.fits) break;
            if (!nextVisible[key] || requiredSet.has(key)) continue;
            nextVisible[key] = false;
            delete widthOverrides[key];
            hiddenKeys.push(key);
            plan = buildWidthPlan(nextVisible, widthOverrides, availableTableWidthPx);
        }

        if (!plan.fits) {
            const additional = colOrder
                .slice()
                .reverse()
                .filter((key) => !!nextVisible[key] && isColumnSupportedForAssetClasses(key) && !requiredSet.has(key) && !hiddenKeys.includes(key));
            for (const key of additional) {
                if (plan.fits) break;
                nextVisible[key] = false;
                delete widthOverrides[key];
                hiddenKeys.push(key);
                plan = buildWidthPlan(nextVisible, widthOverrides, availableTableWidthPx);
            }
        }

        if (!plan.fits) {
            for (const key of colOrder) {
                if (plan.fits) break;
                if (!nextVisible[key]) continue;
                if (widthOverrides[key] == null) continue;
                delete widthOverrides[key];
                plan = buildWidthPlan(nextVisible, widthOverrides, availableTableWidthPx);
            }
        }

        return {
            visible: nextVisible,
            widthOverrides,
            hiddenCount: hiddenKeys.length,
            hiddenKeys,
            fits: plan.fits,
        };
    };
    const headerRefs = useRef<Partial<Record<ColKey, HTMLTableHeaderCellElement | null>>>({});
    const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const containerRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const LAYOUT_KEY = 'tt_layout_default';
    const fitMessageTimerRef = useRef<number | null>(null);
    const localLayoutHydratedRef = useRef(false);
    const layoutPersistenceReadyRef = useRef(false);
    const remoteLayoutHydratedRef = useRef(false);
    const remoteLayoutSaveTimerRef = useRef<number | null>(null);

    const frozenWidthsRef = useRef<Partial<Record<ColKey, number>> | null>(null);
    const resizeLatestWidthsRef = useRef<Partial<Record<ColKey, number>>>({});
    const dragMeasuredWidthsRef = useRef<Partial<Record<ColKey, number>>>({});
    const [activeColumnId, setActiveColumnId] = useState<ColKey | null>(null);
    const [isColumnDragging, setIsColumnDragging] = useState(false);
    const [dragOverlayLabel, setDragOverlayLabel] = useState('');
    const [dropIndicator, setDropIndicator] = useState<null | { left: number; index: number }>(null);
    const [overlayRows, setOverlayRows] = useState<Trade[]>([]);
    const didDragRef = useRef(false);
    const pointerXRef = useRef<number>(0);
    const tableAreaRef = useRef<HTMLDivElement | null>(null);
    const autoScrollRafRef = useRef<number | null>(null);
    const autoScrollSpeedRef = useRef(0);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
    const noSensors = useSensors();
    const resizingRef = useRef<{
        key: ColKey | null;
        pairKey: ColKey | null;
        direction: 1 | -1;
        startX: number;
        startWidth: number;
        startPairWidth: number;
        startTableWidth: number;
    }>({
        key: null,
        pairKey: null,
        direction: 1,
        startX: 0,
        startWidth: 0,
        startPairWidth: 0,
        startTableWidth: 0,
    });

    const showColumnFitWarning = (message: string = COLUMN_FIT_MESSAGE) => {
        setColumnFitMessage(message);
        if (fitMessageTimerRef.current != null) window.clearTimeout(fitMessageTimerRef.current);
        fitMessageTimerRef.current = window.setTimeout(() => {
            setColumnFitMessage(null);
            fitMessageTimerRef.current = null;
        }, 4000);
    };

    const applyVisibleLayout = (
        nextVisible: Record<ColKey, boolean>,
        options?: { widthOverrides?: Partial<Record<ColKey, number>>; silent?: boolean },
    ): boolean => {
        const plan = buildWidthPlan(nextVisible, options?.widthOverrides ?? manualColWidthOverrides, availableTableWidthPx);
        if (!plan.fits) {
            if (!options?.silent) showColumnFitWarning();
            return false;
        }
        setVisibleCols(nextVisible);
        setColWidths(plan.widths);
        setTableWidthPx(plan.visibleKeys.length ? Math.max(200, plan.total) : null);
        setColumnFitMessage(null);
        return true;
    };

    const validateSettingsSelectionForFit = (selection: Record<string, boolean>) => {
        const normalized = normalizeStoredSettings(selection);
        const nextVisible = settingsSelectionToVisibleCols(normalized);
        const plan = buildWidthPlan(nextVisible, manualColWidthOverrides, availableTableWidthPx);
        return {
            fits: plan.fits,
            message: plan.fits ? null : COLUMN_FIT_MESSAGE,
        };
    };

    const autoHideSettingsSelection = (selection: Record<string, boolean>, presetIntent?: ColumnPresetName | null) => {
        const normalized = normalizeStoredSettings(selection);

        if (presetIntent) {
            const resolved = resolvePresetForFit(presetIntent);
            const adjusted = COLUMN_CONFIG.reduce((acc, col) => {
                acc[col.settingsKey] = !!resolved.visible[col.key];
                return acc;
            }, {} as Record<string, boolean>);
            return {
                columns: adjusted,
                hiddenCount: resolved.hiddenCount,
                fits: resolved.fits,
            };
        }

        const nextVisible = settingsSelectionToVisibleCols(normalized);
        const widthOverrides: Partial<Record<ColKey, number>> = { ...manualColWidthOverrides };
        const hiddenKeys: ColKey[] = [];
        const defaultVisible = computeDefaultVisible();

        let plan = buildWidthPlan(nextVisible, widthOverrides, availableTableWidthPx);

        const removeKeysUntilFit = (keys: ColKey[]) => {
            for (const key of keys) {
                if (plan.fits) break;
                if (!nextVisible[key]) continue;
                nextVisible[key] = false;
                delete widthOverrides[key];
                hiddenKeys.push(key);
                plan = buildWidthPlan(nextVisible, widthOverrides, availableTableWidthPx);
            }
        };

        const weakestFirst = colOrder
            .slice()
            .reverse()
            .filter((key) => !!nextVisible[key] && !defaultVisible[key]);
        removeKeysUntilFit(weakestFirst);

        if (!plan.fits) {
            const remaining = colOrder
                .slice()
                .reverse()
                .filter((key) => !!nextVisible[key] && !hiddenKeys.includes(key));
            removeKeysUntilFit(remaining);
        }

        const adjusted = COLUMN_CONFIG.reduce((acc, col) => {
            acc[col.settingsKey] = !!nextVisible[col.key];
            return acc;
        }, {} as Record<string, boolean>);

        return {
            columns: adjusted,
            hiddenCount: hiddenKeys.length,
            fits: plan.fits,
        };
    };

    useEffect(() => {
        return () => {
            if (fitMessageTimerRef.current != null) window.clearTimeout(fitMessageTimerRef.current);
        };
    }, []);

    useEffect(() => {
        const node = containerRef.current;
        if (!node || typeof ResizeObserver === 'undefined') return;
        const update = () => {
            const next = Math.max(0, Math.round(node.clientWidth || 0));
            setAvailableTableWidthPx(next || null);
        };
        update();
        const observer = new ResizeObserver(() => update());
        observer.observe(node);
        return () => observer.disconnect();
    }, [containerRef]);

    useEffect(() => {
        return () => {
            document.removeEventListener('pointermove', onPointerMove);
            document.removeEventListener('pointerup', onPointerUp);
        };
    }, []);

    function startResize(e: PointerEvent | any, key: ColKey) {
        e.preventDefault?.();

        const visibleKeys = orderedVisibleKeys;
        if (!visibleKeys.includes(key)) return;
        const index = visibleKeys.indexOf(key);
        const isLast = index === visibleKeys.length - 1;
        const pairKey = isLast ? visibleKeys[index - 1] : visibleKeys[index + 1];
        const direction: 1 | -1 = isLast ? -1 : 1;
        if (!pairKey) return;

        const currentSnapshot: Partial<Record<ColKey, number>> = {};
        for (const k of visibleKeys) {
            const fromState = Number(colWidths[k]);
            if (Number.isFinite(fromState) && fromState > 0) {
                currentSnapshot[k] = normalizeColumnWidthValue(k, fromState);
                continue;
            }
            const el = headerRefs.current[k];
            const measured = el ? el.getBoundingClientRect().width : undefined;
            currentSnapshot[k] = normalizeColumnWidthValue(k, measured ?? getColumnMinWidth(k));
        }

        // Always refresh the baseline so resizing another column won't revert prior user-adjusted widths.
        frozenWidthsRef.current = currentSnapshot;
        resizeLatestWidthsRef.current = currentSnapshot;

        // Freeze current layout to px widths to prevent % columns from redistributing (which feels like resizing from both sides).
        const isFrozen = visibleKeys.every((k) => typeof colWidths[k] === 'number') && typeof tableWidthPx === 'number';

        let startWidth: number;
        let startPairWidth: number;
        let startTableWidth: number;

        if (!isFrozen) {
            const frozen = currentSnapshot;
            const frozenTotal = visibleKeys.reduce(
                (sum, k) => sum + normalizeColumnWidthValue(k, frozen[k] || getColumnMinWidth(k)),
                0
            );

            frozenWidthsRef.current = frozen;
            setColWidths((prev) => ({ ...prev, ...frozen }));
            setTableWidthPx(frozenTotal);
            startWidth = normalizeColumnWidthValue(key, frozen[key] || colWidths[key] || getColumnMinWidth(key));
            startPairWidth = normalizeColumnWidthValue(pairKey, frozen[pairKey] || colWidths[pairKey] || getColumnMinWidth(pairKey));
            startTableWidth = frozenTotal;
        } else {
            startWidth = normalizeColumnWidthValue(key, colWidths[key] || getColumnMinWidth(key));
            startPairWidth = normalizeColumnWidthValue(pairKey, colWidths[pairKey] || getColumnMinWidth(pairKey));
            startTableWidth = Number(tableWidthPx) || 0;
        }

        resizingRef.current = { key, pairKey, direction, startX: e.clientX, startWidth, startPairWidth, startTableWidth };
        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
    }

    function onPointerMove(e: PointerEvent | any) {
        const { key, pairKey, direction, startX, startWidth, startPairWidth, startTableWidth } = resizingRef.current;
        if (!key || !pairKey) return;
        const visibleKeys = orderedVisibleKeys;
        if (!visibleKeys.length) return;
        const delta = (e.clientX - startX) * direction;
        const minWidth = getColumnMinWidth(key);
        const pairMinWidth = getColumnMinWidth(pairKey);
        const minDelta = minWidth - startWidth;
        const maxDelta = startPairWidth - pairMinWidth;
        const appliedDelta = Math.max(minDelta, Math.min(maxDelta, Math.round(delta)));
        const nextWidth = normalizeColumnWidthValue(key, startWidth + appliedDelta);
        const nextPairWidth = normalizeColumnWidthValue(pairKey, startPairWidth - appliedDelta);

        const frozen = frozenWidthsRef.current ?? {};
        const base: Partial<Record<ColKey, number>> = { ...frozen };

        // Move the shared divider between two columns so the table's right edge stays fixed.
        base[key] = nextWidth;
        base[pairKey] = nextPairWidth;
        resizeLatestWidthsRef.current = base;

        setTableWidthPx(Math.max(200, Math.round(startTableWidth)));

        setColWidths((prev) => {
            const next = { ...prev, ...base };
            if (next.side != null) next.side = normalizeColumnWidthValue('side', next.side);
            return next;
        });
        updateDivider();
    }

    function onPointerUp() {
        const resizedKey = resizingRef.current.key;
        resizingRef.current.key = null;
        resizingRef.current.pairKey = null;
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);
        if (!resizedKey) return;

        setManualColWidthOverrides((prev) => {
            const next = { ...prev };
            const latestWidths = resizeLatestWidthsRef.current;
            for (const key of orderedVisibleKeys) {
                const minWidth = getColumnMinWidth(key);
                const normalized = normalizeColumnWidthValue(key, latestWidths[key] ?? colWidths[key] ?? minWidth);
                if (Math.abs(normalized - minWidth) <= 1) delete next[key];
                else next[key] = normalized;
            }
            return next;
        });
    }

    function handleSort(key: string) {
        if (didDragRef.current) return;
        if (sortBy === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortDir('asc');
        }
    }

    const getTradeFeeAbs = (trade: Trade): number => {
        const explicit = Number((trade as any).commissionAndFees ?? (trade as any).commissionsAndFees);
        if (Number.isFinite(explicit)) return Math.abs(explicit);
        const commission = Number((trade as any).commission ?? 0);
        const fees = Number((trade as any).fees ?? (trade as any).fee ?? 0);
        return Math.abs((Number.isFinite(commission) ? commission : 0) + (Number.isFinite(fees) ? fees : 0));
    };

    const getTradeDisplayId = (trade: Trade): string => {
        const demoDisplayId = getDemoTradeDisplayId(trade);
        if (demoDisplayId) return demoDisplayId;

        const raw =
            (trade as any).tradeId ??
            (trade as any).storageId ??
            (trade as any).id ??
            (trade as any).originalId ??
            (trade as any).orderId;
        const value = String(raw ?? '').trim();
        return value || '—';
    };

    // Canonical P&L is always internally calculated.
    // Gross view adds back commissions/fees so users can compare.
    // The net value comes from the SAME canonical resolver (getTradePnl) used by
    // the dashboard daily aggregation, tradeLevelStats, authoritativeConsistency,
    // and every trade-derived widget — so the Trades page total can never
    // diverge from the Dashboard for the same closed trade.
    const getTradeDisplayPnl = (trade: Trade): number => {
        if (isOpenLikeTradeForRender(trade)) return 0;
        const pnl = getTradePnl(trade as any);

        if (activeView === 'Gross') {
            // Add back fees only for realized/closed trades (do not add entry-only fees for open trades)
            const hasRealized = resolveTradeRealizedPnl(trade as any) != null;
            return pnl + (hasRealized ? getTradeFeeAbs(trade) : 0);
        }

        return pnl;
    };

    const getTradeInitialRiskValue = (trade: Trade): number | null => {
        return resolveTradeInitialRiskValue(trade as any, defaultRiskPerTrade);
    };

    const mapMarketToStopRiskAssetClass = (market: MarketCategory): StopRiskAssetClass => {
        if (market === 'STOCKS') return 'STOCKS';
        if (market === 'FUTURES') return 'FUTURES';
        if (market === 'FOREX') return 'FOREX';
        if (market === 'CRYPTO') return 'CRYPTO';
        return 'OTHER';
    };

    const resolveFuturesMultiplierForRisk = (trade: Trade): number | null => {
        const rawTrade = trade as any;
        const executionCandidate = Array.isArray(rawTrade.executionsList) && rawTrade.executionsList.length > 0
            ? rawTrade.executionsList[0]
            : null;
        const instrument = rawTrade.instrument ?? (executionCandidate as any)?.instrument;
        const instrumentMultiplier = Number((instrument as any)?.multiplier ?? (instrument as any)?.contractSize);
        if (Number.isFinite(instrumentMultiplier) && instrumentMultiplier > 0) return instrumentMultiplier;

        const symbol = String(rawTrade.symbol ?? (executionCandidate as any)?.symbol ?? '').trim().toUpperCase();
        if (!symbol) return null;
        const root = extractFuturesRoot(symbol);
        if (!root || !Object.prototype.hasOwnProperty.call(CME_FUTURES_MULTIPLIERS, root)) return null;
        const mapped = Number(CME_FUTURES_MULTIPLIERS[root]);
        return Number.isFinite(mapped) && mapped > 0 ? mapped : null;
    };

    const deriveOpeningRiskBasis = (trade: Trade):
        | { ok: true; side: 'buy' | 'sell'; entryPrice: number; quantity: number }
        | { ok: false; reason: string } => {
        const rawTrade = trade as any;
        const executionRows = Array.isArray(rawTrade.executionsList)
            ? rawTrade.executionsList
            : (Array.isArray(rawTrade.executions) ? rawTrade.executions : []);

        const isNotNull = <T,>(v: T | null | undefined): v is T => v != null;

        const normalized = (executionRows
            .map((row: any) => {
                const ts = normalizeTimestampToMs(row?.timestamp ?? row?.openTimestampMs ?? row?.time ?? row?.date ?? row?.executedAt);
                const qtyRaw = row?.qty ?? row?.quantity ?? row?.volume ?? row?.shares;
                const qty = Math.abs(Number(qtyRaw));
                const price = Number(row?.price ?? row?.entryPrice ?? row?.avgPrice ?? row?.fillPrice);
                const side = normalizeTradeSide(row?.side ?? row?.action ?? row?.type ?? row?.positionSide, qtyRaw);
                if (!Number.isFinite(Number(ts)) || !Number.isFinite(qty) || qty <= 0 || !Number.isFinite(price) || price <= 0 || !side) {
                    return null;
                }
                return { ts: Number(ts), qty, price, side };
            })
            .filter(isNotNull)
            .sort((a: { ts: number; qty: number; price: number; side: 'buy' | 'sell' }, b: { ts: number; qty: number; price: number; side: 'buy' | 'sell' }) => a.ts - b.ts)) as { ts: number; qty: number; price: number; side: 'buy' | 'sell' }[];

        if (normalized.length === 0) {
            return { ok: false, reason: 'Opening execution data is unavailable for this trade. Use Risk Amount ($).' };
        }

        const first = normalized[0];
        const openingSide = first.side;
        const openingTs = first.ts;
        const openingRows = normalized.filter((row: { ts: number; qty: number; price: number; side: 'buy' | 'sell' }) => row.side === openingSide && Math.abs(row.ts - openingTs) <= 1000);
        const quantity = openingRows.reduce((sum: number, row: { ts: number; qty: number; price: number; side: 'buy' | 'sell' }) => sum + row.qty, 0);
        if (!Number.isFinite(quantity) || quantity <= 0) {
            return { ok: false, reason: 'Opening quantity is unavailable for this trade. Use Risk Amount ($).' };
        }
        const weightedValue = openingRows.reduce((sum: number, row: { ts: number; qty: number; price: number; side: 'buy' | 'sell' }) => sum + (row.qty * row.price), 0);
        const entryPrice = weightedValue / quantity;
        if (!Number.isFinite(entryPrice) || entryPrice <= 0) {
            return { ok: false, reason: 'Opening entry price is unavailable for this trade. Use Risk Amount ($).' };
        }
        return {
            ok: true,
            side: openingSide,
            entryPrice,
            quantity,
        };
    };

    const getFuturesTickSize = (trade: Trade): number | null => {
        const rawTrade = trade as any;
        const executionCandidate = Array.isArray(rawTrade.executionsList) && rawTrade.executionsList.length > 0
            ? rawTrade.executionsList[0]
            : null;
        const symbol = String(rawTrade.symbol ?? (executionCandidate as any)?.symbol ?? '').trim();
        const fromTradeInstrument = resolveFuturesTickSize(symbol, rawTrade.instrument);
        if (fromTradeInstrument != null) return fromTradeInstrument;

        const fromExecutionInstrument = resolveFuturesTickSize(symbol, (executionCandidate as any)?.instrument);
        if (fromExecutionInstrument != null) return fromExecutionInstrument;

        return resolveFuturesTickSize(symbol);
    };

    const getTradePnlRValue = (trade: Trade): number | null => {
        if (isOpenLikeTradeForRender(trade)) return null;
        const initialRisk = getTradeInitialRiskValue(trade);
        if (initialRisk == null || initialRisk <= 0) return null;
        const pnl = getTradeDisplayPnl(trade);
        if (!Number.isFinite(pnl)) return null;
        return pnl / initialRisk;
    };

    const getTradePnlTicksGrossFuturesValue = (trade: Trade): number | null => {
        if (isOpenLikeTradeForRender(trade)) return null;
        const market = getTradeMarketCategory(trade as any);
        if (market !== 'FUTURES') return null;

        const tickSize = getFuturesTickSize(trade);
        if (tickSize == null || tickSize <= 0) return null;

        const entry = toFiniteNumber((trade as any).entryPrice ?? (trade as any).entry_price);
        const exit = toFiniteNumber((trade as any).exitPrice ?? (trade as any).exit_price);
        if (entry == null || exit == null) return null;

        const side = normalizeTradeSide((trade as any).side, (trade as any).volume);
        if (!side) return null;

        const ticks = computeDirectionalTicks(entry, exit, side, tickSize);
        if (ticks == null || !Number.isFinite(ticks)) return null;
        return Math.round(ticks * 100) / 100;
    };

    const getTradePositionExcursionValue = (trade: Trade, kind: 'MFE' | 'MAE'): number | null => {
        const persisted = readPersistedTradePositionExcursionValue(trade, kind);
        if (persisted != null) return persisted;

        const cached = tradeExcursionsByKey[getTradeStorageKey(trade)];
        if (cached !== false) {
            return kind === 'MFE' ? cached.avgPositionMfe : cached.avgPositionMae;
        }

        return null;
    };

    // Filtering helpers are provided by `src/components/pages/tradesFilters.ts` and
    // imported at the top of this file (`matchesTradeSearch`, `matchesTimeframeFilter`).

    const effectiveFilters = useMemo<TradesPageFilters>(() => ({
        ...liveFilters,
    }), [liveFilters]);

    const filteredTrades = useMemo(() => {
        try {
            return groupedTrades.filter((t) => {
                if (!matchesTradeSearch(t, effectiveFilters.searchTerm)) return false;
                if (!isTradeWithinTenantDisplayScope(t)) return false;

                try {
                    const market = detectMarketCategory({
                        symbol: (t as any).symbol,
                        market: (t as any).market,
                        marketType: (t as any).marketType,
                        assetClass: (t as any).assetClass,
                        category: (t as any).category,
                        instrumentType: (t as any).instrumentType,
                        instrument: (t as any).instrument,
                        securityType: (t as any).securityType,
                    });

                    if (!isAssetClassEnabled(market, assetVisibility)) return false;

                    if (marketFilter && marketFilter !== 'ALL') {
                        if (market !== marketFilter) return false;
                    }

                    if (effectiveFilters.sideFilter !== 'All') {
                        const normalizedSide = normalizeTradeSide(
                            (t as any).side ??
                            (t as any).sideCode ??
                            (t as any).side_code ??
                            (t as any).orderSide ??
                            (t as any).order_side ??
                            (t as any)['b/s'] ??
                            (t as any).bs ??
                            (t as any).direction ??
                            (t as any).action ??
                            (t as any).type ??
                            (t as any).position ??
                            (t as any).positionType ??
                            (t as any).position_type,
                            (t as any).volume ?? (t as any).quantity ?? (t as any).shares
                        );
                        const wantedSide = effectiveFilters.sideFilter === 'Long' ? 'buy' : 'sell';
                        if (normalizedSide !== wantedSide) return false;
                    }

                    if (!matchesTimeframeFilter(t, effectiveFilters)) return false;
                    return true;
                } catch {
                    return false;
                }
            });
        } catch {
            return groupedTrades;
        }
    }, [assetVisibility, groupedTrades, marketFilter, effectiveFilters]);

    const demoFallbackDisplayIdsByKey = useMemo(() => {
        const lookup = new Map<string, string>();
        if (!demoMode.enabled && !demoTradesActive) return lookup;

        const fallbackTrades = [...filteredTrades]
            .filter((trade) => isDemoTradeRecord(trade) || hasTechnicalCsvTradeId(trade))
            .sort((a, b) => (getTradeTimestampMs(a) ?? 0) - (getTradeTimestampMs(b) ?? 0));

        fallbackTrades.forEach((trade, index) => {
            lookup.set(getTradeStorageKey(trade), `Demo_${index + 1}`);
        });

        return lookup;
    }, [demoMode.enabled, demoTradesActive, filteredTrades]);

    const getTradeDisplayIdForTable = useCallback(
        (trade: Trade): string => demoFallbackDisplayIdsByKey.get(getTradeStorageKey(trade)) ?? getTradeDisplayId(trade),
        [demoFallbackDisplayIdsByKey]
    );

    const sortedTrades = useMemo(() => {
        if (!sortBy) return filteredTrades;
        const copy = [...filteredTrades];
        copy.sort((a: Trade, b: Trade) => {
            if (sortBy === 'tradeId') {
                return sortDir === 'asc'
                    ? compareTradeIdValues(getTradeDisplayIdForTable(a), getTradeDisplayIdForTable(b))
                    : compareTradeIdValues(getTradeDisplayIdForTable(b), getTradeDisplayIdForTable(a));
            }

            let av: any = (a as any)[sortBy as string];
            let bv: any = (b as any)[sortBy as string];

            if (sortBy === 'opened') {
                av = getTradeTimestampMs(a) ?? 0;
                bv = getTradeTimestampMs(b) ?? 0;
            } else if (sortBy === 'closed') {
                const aClosed = toDateValue((a as any).closeTime ?? (a as any).closeDate ?? (a as any).exitTime ?? (a as any).closeTimestampMs);
                const bClosed = toDateValue((b as any).closeTime ?? (b as any).closeDate ?? (b as any).exitTime ?? (b as any).closeTimestampMs);
                av = aClosed ? aClosed.getTime() : 0;
                bv = bClosed ? bClosed.getTime() : 0;
            } else if (sortBy === 'pnl') {
                av = getTradeDisplayPnl(a);
                bv = getTradeDisplayPnl(b);
            } else if (sortBy === 'pnlR') {
                av = getTradePnlRValue(a);
                bv = getTradePnlRValue(b);
            } else if (sortBy === 'pnlTicksGrossFutures') {
                av = getTradePnlTicksGrossFuturesValue(a);
                bv = getTradePnlTicksGrossFuturesValue(b);
            } else if (sortBy === 'initialRisk') {
                av = getTradeInitialRiskValue(a);
                bv = getTradeInitialRiskValue(b);
            } else if (sortBy === 'positionMFE') {
                av = getTradePositionExcursionValue(a, 'MFE');
                bv = getTradePositionExcursionValue(b, 'MFE');
            } else if (sortBy === 'positionMAE') {
                av = getTradePositionExcursionValue(a, 'MAE');
                bv = getTradePositionExcursionValue(b, 'MAE');
            } else if (sortBy === 'totalFees') {
                av = getTradeFeeAbs(a);
                bv = getTradeFeeAbs(b);
            } else if (sortBy === 'tags') {
                av = Array.isArray((a as any).tags) ? (a as any).tags.length : 0;
                bv = Array.isArray((b as any).tags) ? (b as any).tags.length : 0;
            }

            if (av == null) av = '';
            if (bv == null) bv = '';
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();

            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return copy;
    }, [filteredTrades, getTradeDisplayIdForTable, sortBy, sortDir]);

    const orderedVisibleKeys = useMemo(
        () => colOrder.filter((k) => responsiveVisibleCols[k] && isColumnSupportedForAssetClasses(k)),
        [colOrder, isColumnSupportedForAssetClasses, responsiveVisibleCols]
    );

    const pagedTrades = useMemo(
        () => sortedTrades.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize),
        [sortedTrades, page, pageSize]
    );
    const currentPageHasManualEntry = useMemo(
        () => hasManualEntryTrade(pagedTrades),
        [pagedTrades]
    );

    useEffect(() => {
        const needsPositionExcursions = visibleCols.positionMFE || visibleCols.positionMAE || sortBy === 'positionMFE' || sortBy === 'positionMAE';
        if (!needsPositionExcursions) return;

            // Disabled: automatic per-trade OHLC fetches for excursion derivation compete with the
            // main trade chart replay path through the shared ibkrHistory throttler (COOLDOWN_MS=15s).
            // Until a dedicated low-priority fetch queue exists, auto-fetch is disabled to keep chart
            // load fast and stable.  Persisted excursion values (readPersistedTradePositionExcursionValue)
            // still display correctly; this only affects trades with no stored MAE/MFE.
            // Re-enable by setting EXCURSION_LIVE_FETCH_ENABLED = true once the coordination issue is solved.
            const EXCURSION_LIVE_FETCH_ENABLED = false;
            if (!EXCURSION_LIVE_FETCH_ENABLED) return;

        const sourceTrades = (sortBy === 'positionMFE' || sortBy === 'positionMAE') ? filteredTrades : pagedTrades;
        const needsMfe = visibleCols.positionMFE || sortBy === 'positionMFE';
        const needsMae = visibleCols.positionMAE || sortBy === 'positionMAE';

        const candidates = sourceTrades.filter((trade) => {
            if (String((trade as any)?.status ?? '').toLowerCase() !== 'closed') return false;

            const key = getTradeStorageKey(trade);
            if (pendingTradeExcursionKeysRef.current.has(key)) return false;
            if (Object.prototype.hasOwnProperty.call(tradeExcursionsByKey, key)) return false;

            const hasPersistedMfe = readPersistedTradePositionExcursionValue(trade, 'MFE') != null;
            const hasPersistedMae = readPersistedTradePositionExcursionValue(trade, 'MAE') != null;
            if ((!needsMfe || hasPersistedMfe) && (!needsMae || hasPersistedMae)) return false;

            const executions = Array.isArray((trade as any)?.executionsList)
                ? (trade as any).executionsList
                : (Array.isArray((trade as any)?.executions) ? (trade as any).executions : []);
            return executions.length > 0;
        });

        if (candidates.length === 0) return;

        const abortController = new AbortController();
        const chunkSize = 4;

        const run = async () => {
            for (const trade of candidates) {
                pendingTradeExcursionKeysRef.current.add(getTradeStorageKey(trade));
            }

            for (let index = 0; index < candidates.length; index += chunkSize) {
                if (abortController.signal.aborted) break;
                const chunk = candidates.slice(index, index + chunkSize);

                const settled = await Promise.allSettled(chunk.map(async (trade) => {
                    const key = getTradeStorageKey(trade);
                    try {
                        const multiplier = getTradeMarketCategory(trade as any) === 'FUTURES'
                            ? (resolveFuturesMultiplierForRisk(trade) ?? 1)
                            : 1;
                        const summary = await fetchCanonicalTradeExcursionSummary({
                            trade,
                            signal: abortController.signal,
                            multiplier,
                        });
                        return { key, summary };
                    } finally {
                        pendingTradeExcursionKeysRef.current.delete(key);
                    }
                }));

                if (abortController.signal.aborted) break;

                setTradeExcursionsByKey((prev) => {
                    const next = { ...prev };
                    for (const result of settled) {
                        if (result.status !== 'fulfilled') continue;
                        next[result.value.key] = result.value.summary ?? false;
                    }
                    return next;
                });
            }
        };

        run().catch(() => {
            for (const trade of candidates) {
                pendingTradeExcursionKeysRef.current.delete(getTradeStorageKey(trade));
            }
        });

        return () => {
            abortController.abort();
            for (const trade of candidates) {
                pendingTradeExcursionKeysRef.current.delete(getTradeStorageKey(trade));
            }
        };
    }, [filteredTrades, pagedTrades, sortBy, tradeExcursionsByKey, visibleCols.positionMAE, visibleCols.positionMFE]);

    const getEffectivePrecisionForColumn = (rows: Trade[], getTimestampMsForRow: (trade: Trade) => number | null): EffectiveTimestampPrecision => {
        if (timestampPrecisionMode === 'minutes' || timestampPrecisionMode === 'seconds') return timestampPrecisionMode;
        const seenAtMinutePrecision = new Set<string>();
        for (const row of rows) {
            const ms = getTimestampMsForRow(row);
            if (ms == null) continue;
            const datePart = formatDateCell(ms);
            const minutePart = trimTimeToMinutes(formatTimeCell(ms));
            const minuteKey = `${datePart}|${minutePart}`;
            if (seenAtMinutePrecision.has(minuteKey)) return 'seconds';
            seenAtMinutePrecision.add(minuteKey);
        }
        return 'minutes';
    };

    const openedColumnPrecision = useMemo(
        () => getEffectivePrecisionForColumn(pagedTrades, (trade: Trade) => getTradeTimestampMs(trade)),
        [pagedTrades, timestampPrecisionMode, tradesTimeMode]
    );

    const closedColumnPrecision = useMemo(
        () => getEffectivePrecisionForColumn(pagedTrades, (trade: Trade) => {
            if (isOpenLikeTradeForRender(trade as any)) return null;
            return getCloseTimestampMs(trade);
        }),
        [pagedTrades, timestampPrecisionMode, tradesTimeMode, isOpenLikeTradeForRender]
    );

    useEffect(() => {
        if (!isColumnDragging) return;
        const onMove = (e: PointerEvent) => {
            pointerXRef.current = e.clientX;
        };
        window.addEventListener('pointermove', onMove);
        return () => window.removeEventListener('pointermove', onMove);
    }, [isColumnDragging]);

    const stopAutoScroll = () => {
        if (autoScrollRafRef.current != null) {
            cancelAnimationFrame(autoScrollRafRef.current);
            autoScrollRafRef.current = null;
        }
        autoScrollSpeedRef.current = 0;
    };

    const startAutoScroll = () => {
        if (autoScrollRafRef.current != null) return;
        const tick = () => {
            const scroller = containerRef.current;
            if (scroller && autoScrollSpeedRef.current !== 0) {
                scroller.scrollLeft += autoScrollSpeedRef.current;
            }
            autoScrollRafRef.current = requestAnimationFrame(tick);
        };
        autoScrollRafRef.current = requestAnimationFrame(tick);
    };

    const measureDragWidths = () => {
        const next: Partial<Record<ColKey, number>> = {};
        for (const key of orderedVisibleKeys) {
            const el = headerRefs.current[key];
            if (el) next[key] = Math.round(el.getBoundingClientRect().width);
        }
        dragMeasuredWidthsRef.current = next;
    };

    const computeDropIndicator = (clientX: number | null, overId: ColKey | null) => {
        const scroller = tableAreaRef.current;
        if (!scroller) return null;

        const orderedHeaders = orderedVisibleKeys
            .map((key, index) => {
                const header = headerRefs.current[key];
                if (!header) return null;
                return {
                    key,
                    index,
                    header,
                    rect: header.getBoundingClientRect(),
                };
            })
            .filter((entry): entry is { key: ColKey; index: number; header: HTMLTableHeaderCellElement; rect: DOMRect } => Boolean(entry));

        if (!orderedHeaders.length) return null;

        const scrollerRect = scroller.getBoundingClientRect();
        const toLeft = (viewportX: number) => Math.round(viewportX - scrollerRect.left + scroller.scrollLeft);

        if (clientX != null) {
            const first = orderedHeaders[0];
            if (clientX <= first.rect.left) {
                return { index: 0, left: toLeft(first.rect.left) };
            }

            const last = orderedHeaders[orderedHeaders.length - 1];
            if (clientX >= last.rect.right) {
                return { index: orderedHeaders.length, left: toLeft(last.rect.right) };
            }

            const directHit = orderedHeaders.find((entry) => clientX >= entry.rect.left && clientX <= entry.rect.right);
            if (directHit) {
                const insertAfter = clientX > directHit.rect.left + directHit.rect.width / 2;
                return {
                    index: directHit.index + (insertAfter ? 1 : 0),
                    left: toLeft(insertAfter ? directHit.rect.right : directHit.rect.left),
                };
            }

            for (let index = 0; index < orderedHeaders.length - 1; index += 1) {
                const current = orderedHeaders[index];
                const next = orderedHeaders[index + 1];
                if (clientX > current.rect.right && clientX < next.rect.left) {
                    return { index: index + 1, left: toLeft(current.rect.right) };
                }
            }
        }

        if (overId) {
            const overHeader = orderedHeaders.find((entry) => entry.key === overId);
            if (overHeader) {
                return {
                    index: overHeader.index,
                    left: computeColumnDropIndicatorLeft({
                        scroller,
                        overHeader: overHeader.header,
                    }) ?? toLeft(overHeader.rect.left),
                };
            }
        }

        return null;
    };

    const handleColumnDragStart = (event: DragStartEvent) => {
        const active = String(event.active.id) as ColKey;
        setActiveColumnId(active);
        setDragOverlayLabel(COL_LABELS[active] ?? active);
        setIsColumnDragging(true);
        setDropIndicator(null);
        didDragRef.current = true;
        measureDragWidths();

        try {
            const scroller = containerRef.current;
            if (!scroller) {
                setOverlayRows(pagedTrades.slice(0, 12));
                return;
            }
            const sRect = scroller.getBoundingClientRect();
            const visible: Trade[] = [];
            for (const t of pagedTrades) {
                const key = tradeKey(t as any);
                const rowEl = rowRefs.current[key];
                if (!rowEl) continue;
                const rRect = rowEl.getBoundingClientRect();
                if (rRect.bottom >= sRect.top && rRect.top <= sRect.bottom) visible.push(t);
            }
            setOverlayRows((visible.length ? visible : pagedTrades).slice(0, 16));
        } catch {
            setOverlayRows(pagedTrades.slice(0, 12));
        }
    };

    const handleColumnDragMove = (event: DragMoveEvent) => {
        const dragEvent = event as DragMoveEvent & { sensorEvent?: MouseEvent; originalEvent?: MouseEvent };
        const clientX = dragEvent.sensorEvent?.clientX ?? dragEvent.originalEvent?.clientX ?? pointerXRef.current ?? null;
        if (clientX != null) pointerXRef.current = clientX;
        const scroller = containerRef.current;
        if (scroller && typeof clientX === 'number') {
            const rect = scroller.getBoundingClientRect();
            const edge = 40;
            const leftDist = clientX - rect.left;
            const rightDist = rect.right - clientX;
            let speed = 0;
            if (leftDist < edge) {
                const t = Math.max(0, (edge - leftDist) / edge);
                speed = -Math.round(6 + t * 18);
            } else if (rightDist < edge) {
                const t = Math.max(0, (edge - rightDist) / edge);
                speed = Math.round(6 + t * 18);
            }
            autoScrollSpeedRef.current = speed;
            if (speed !== 0) startAutoScroll(); else stopAutoScroll();
        }

        const overId = event.over ? (String(event.over.id) as ColKey) : null;
        setDropIndicator(computeDropIndicator(clientX, overId));
    };

    const handleColumnDragEnd = (event: DragEndEvent) => {
        stopAutoScroll();
        const active = String(event.active.id) as ColKey;
        const over = event.over ? (String(event.over.id) as ColKey) : null;
        if (active) {
            setColOrder((prev) => {
                const visibleOrder = prev.filter((key) => visibleCols[key] && isColumnSupportedForAssetClasses(key));
                const oldIndex = visibleOrder.indexOf(active);
                if (oldIndex < 0) return prev;

                const fallbackIndex = over ? visibleOrder.indexOf(over) : -1;
                const targetIndex = dropIndicator?.index ?? fallbackIndex;
                if (targetIndex < 0) return prev;

                const withoutActive = visibleOrder.filter((key) => key !== active);
                const adjustedIndex = oldIndex < targetIndex ? targetIndex - 1 : targetIndex;
                const boundedIndex = Math.max(0, Math.min(adjustedIndex, withoutActive.length));
                const reorderedVisible = withoutActive.slice();
                reorderedVisible.splice(boundedIndex, 0, active);

                const visibleSet = new Set(visibleOrder);
                let nextVisibleIndex = 0;
                return prev.map((key) => {
                    if (!visibleSet.has(key)) return key;
                    return reorderedVisible[nextVisibleIndex++] ?? key;
                });
            });
        }
        setIsColumnDragging(false);
        setActiveColumnId(null);
        setDropIndicator(null);
        requestAnimationFrame(() => { didDragRef.current = false; dragMeasuredWidthsRef.current = {}; });
    };

    const handleColumnDragCancel = () => {
        stopAutoScroll();
        setIsColumnDragging(false);
        setActiveColumnId(null);
        setDropIndicator(null);
        requestAnimationFrame(() => { didDragRef.current = false; dragMeasuredWidthsRef.current = {}; });
    };

    useEffect(() => {
        if (!settingsOpen) return;
        stopAutoScroll();
        setIsColumnDragging(false);
        setActiveColumnId(null);
        setDropIndicator(null);
        requestAnimationFrame(() => { didDragRef.current = false; dragMeasuredWidthsRef.current = {}; });
    }, [settingsOpen]);

    const totalPages = Math.max(1, Math.ceil(filteredTrades.length / pageSize));

    useEffect(() => {
        setPage(1);
    }, [marketFilter, effectiveFilters.searchTerm, effectiveFilters.sideFilter, effectiveFilters.timeframe, effectiveFilters.fromDate, effectiveFilters.toDate]);

    useEffect(() => {
        try {
            localStorage.setItem('tt_trades_market_filter', marketFilter);
        } catch {}
    }, [marketFilter]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const colStyle = (key: ColKey) => {
        if (isColumnDragging && dragMeasuredWidthsRef.current[key]) {
            const fixed = `${dragMeasuredWidthsRef.current[key]}px`;
            if (key === 'side') return { width: fixed, minWidth: `${SIDE_MIN_PX}px`, maxWidth: fixed };
            return { width: fixed, minWidth: fixed, maxWidth: fixed };
        }
        // use explicit pixel width only if the user has resized the column
        const base = colWidths[key] ? `${colWidths[key]}px` : undefined;
        if (key === 'side') {
            return { width: base, minWidth: `${SIDE_MIN_PX}px` };
        }
        return base ? { width: base } : {};
    };

    const SortableHeader = ({ k }: { k: ColKey }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: k });
        const stickySafeTransform = transform ? { ...transform, y: 0 } : null;
        const isProfitLossHeader = k === 'pnl';
        const style = {
            ...colStyle(k),
            transform: isDragging ? undefined : CSS.Transform.toString(stickySafeTransform),
            transition,
            opacity: isDragging ? 0.6 : 1,
            ...(isProfitLossHeader ? {
                fontFamily: '"Inter Variable", Arial, sans-serif',
                fontWeight: 600,
            } : {}),
        };

        return (
            <th
                key={k}
                ref={(el) => { headerRefs.current[k] = el; setNodeRef(el); }}
                style={style}
                onClick={() => handleSort(k)}
                className="relative px-4 py-4 text-center font-bold text-zinc-800 hover:text-zinc-900 dark:text-white/50 dark:hover:text-white transition-colors cursor-grab border-r border-b border-zinc-200 bg-zinc-200 dark:border-r-zinc-800 dark:border-b-zinc-700 dark:bg-zinc-800 last:border-r-0 overflow-hidden"
                {...attributes}
                {...listeners}
            >
                <div className="flex min-w-0 items-center justify-center gap-2">
                    <span
                        className={`min-w-0 truncate ${isProfitLossHeader ? 'font-semibold' : 'font-bold'}`}
                        style={isProfitLossHeader ? { fontFamily: '"Inter Variable", Arial, sans-serif' } : undefined}
                    >
                        {COL_LABELS[k]}
                    </span>
                    {k === 'totalFees' ? (
                        <span title={TOTAL_FEES_TOOLTIP}>
                            <Info size={12} className="text-zinc-400" aria-hidden />
                        </span>
                    ) : null}
                    {sortBy === k && (
                        <span className="text-xs text-zinc-400">{sortDir === 'asc' ? '▲' : '▼'}</span>
                    )}
                </div>
                {resizeHandle(k)}
            </th>
        );
    };

    const [reversalDividerLeftPx, setReversalDividerLeftPx] = useState<number | null>(null);

    useEffect(() => {
        const navigation = document.querySelector<HTMLElement>('[data-tour-target="top-navigation"]');
        const updateOffset = () => {
            const navigationBottom = navigation?.getBoundingClientRect().bottom ?? 0;
            setStickyTradesHeaderTop(Math.max(0, Math.ceil(navigationBottom) + 18));
        };

        updateOffset();
        window.addEventListener('resize', updateOffset);
        const observer = typeof ResizeObserver === 'undefined' || !navigation
            ? null
            : new ResizeObserver(updateOffset);
        observer?.observe(navigation!);
        return () => {
            window.removeEventListener('resize', updateOffset);
            observer?.disconnect();
        };
    }, []);

     const updateDivider = () => {
         if (!visibleCols.side) {
             setReversalDividerLeftPx(null);
             return;
         }

         const sideHeader = headerRefs.current.side;
         let tableEl = sideHeader?.closest('table') as HTMLTableElement | null;
         let leftPx: number | null = null;

         if (sideHeader && tableEl) {
             const sideRect = sideHeader.getBoundingClientRect();
             const tableRect = tableEl.getBoundingClientRect();
             leftPx = Math.round(sideRect.right - tableRect.left);
         } else if (!tableEl && containerRef.current) {
             tableEl = containerRef.current.querySelector('table');
         }

         if (leftPx == null && tableEl) {
             const firstRow = tableEl.querySelector('tbody tr');
             if (firstRow) {
                 const cells = Array.from(firstRow.children) as HTMLElement[];
                const idx = orderedVisibleKeys.findIndex(k => k === 'side');
                 if (idx >= 0 && cells[idx]) {
                     const sideCellRect = cells[idx].getBoundingClientRect();
                     const tableRect = tableEl.getBoundingClientRect();
                     leftPx = Math.round(sideCellRect.right - tableRect.left);
                 }
             }
         }

         if (leftPx == null) {
             // nothing to update; keep previous value
             return;
         }
         setReversalDividerLeftPx(leftPx);
     };

     useEffect(() => {
        updateDivider();
        window.addEventListener('resize', updateDivider);
        return () => window.removeEventListener('resize', updateDivider);
    }, [visibleCols.side, colWidths, tableWidthPx, orderedVisibleKeys]);

    const quantityHeaderLabel = marketFilter === 'FOREX' ? 'Max Lots' : 'Max Size';

    const COL_LABELS: Record<ColKey, string> = {
        opened: 'Opened',
        closed: 'Closed',
        tradeId: 'TradeId',
        symbol: 'Symbol',
        side: 'Side',
        volume: quantityHeaderLabel,
        executions: 'Executions',
        pnl: 'Profit & Loss',
        pnlR: 'Profit & Loss (R)',
        pnlTicksGrossFutures: isStocksOnly ? 'P&L (Ticks)' : 'P&L (Ticks) · Futures Only',
        entryPrice: 'Entry Price',
        exitPrice: 'Exit Price',
        initialRisk: 'Initial Risk',
        positionMFE: 'Position MFE',
        positionMAE: 'Position MAE',
        totalFees: 'Total Fees',
        duration: 'Duration',
        notes: 'Notes',
        tags: 'Tags',
    };

    const renderCell = (key: ColKey, trade: Trade) => {
        const t: any = trade as any;
        switch (key) {
            case 'opened':
                return (() => {
                    const ts = getTradeTimestampMs(trade);
                    if (ts == null) {
                        return (
                            <div className="flex items-center justify-center gap-4">
                                <span className="text-black dark:text-zinc-200">—</span>
                            </div>
                        );
                    }

                    const dateLabel = formatDateCell(ts);
                    const fullTimeLabel = formatTimeCell(ts);
                    const timeLabel = openedColumnPrecision === 'seconds' ? fullTimeLabel : trimTimeToMinutes(fullTimeLabel);
                    const fullTimestampTitle = `${dateLabel} ${fullTimeLabel}`.trim();

                    return (
                        <div className="flex items-center justify-center gap-4 group relative">
                            <span className="inline-flex flex-col items-center leading-tight gap-0.5 max-w-[16rem] text-center">
                                <span className="whitespace-nowrap truncate text-sm font-semibold text-zinc-700 dark:text-white/85">{dateLabel}</span>
                                <span className="whitespace-nowrap truncate text-sm font-normal text-zinc-600 dark:text-white/75">{timeLabel}</span>
                            </span>

                            {/* instant tooltip (replaces native title) */}
                            <span className="pointer-events-none absolute -top-9 left-1/2 transform -translate-x-1/2 z-50 whitespace-nowrap rounded-md bg-zinc-900 text-white text-xs px-2 py-1 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-75">
                                {fullTimestampTitle}
                            </span>
                        </div>
                    );
                })();
            case 'closed':
                {
                    const isOpenLike = isOpenLikeTradeForRender(t);
                    const closeTs = getCloseTimestampMs(trade);
                    return (
                        <div className="text-zinc-500 dark:text-white/60">
                            {isOpenLike || closeTs == null
                                ? '—'
                                : (() => {
                                    const dateLabel = formatDateCell(closeTs);
                                    const fullTimeLabel = formatTimeCell(closeTs);
                                    const timeLabel = closedColumnPrecision === 'seconds' ? fullTimeLabel : trimTimeToMinutes(fullTimeLabel);
                                    const fullTimestampTitle = `${dateLabel} ${fullTimeLabel}`.trim();
                                    return (
                                        <span className="inline-flex flex-col items-center leading-tight gap-0.5" title={fullTimestampTitle}>
                                            <span className="whitespace-nowrap truncate text-sm font-semibold text-zinc-700 dark:text-white/85">{dateLabel}</span>
                                            <span className="whitespace-nowrap truncate text-sm font-normal text-zinc-600 dark:text-white/75">{timeLabel}</span>
                                        </span>
                                    );
                                })()}
                        </div>
                    );
                }
            case 'tradeId':
                return (
                    <div className="flex min-w-0 items-center justify-center gap-3 overflow-hidden">
                        <input type="checkbox" aria-label={`Select trade ${getTradeDisplayIdForTable(trade)}`} checked={selectedTrades.has(tradeKey(trade))} onChange={() => toggleTradeSelection(tradeKey(trade))} onClick={(e) => e.stopPropagation()} className="h-4 w-4 rounded border-gray-300 dark:border-zinc-600 text-blue-600 focus:ring-blue-500 bg-transparent dark:bg-zinc-700" />
                        <span className="min-w-0 truncate text-black dark:text-zinc-200">
                            {getTradeDisplayIdForTable(trade)}
                        </span>
                        {isManualEntryTrade(trade) && <span title="Manual Entry" aria-label="Manual Entry" className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-sky-400/50 bg-sky-500/15 text-[9px] font-bold leading-none text-sky-700 dark:text-sky-300">M</span>}
                    </div>
                );
            case 'symbol':
                return <span className="text-blue-600 dark:text-blue-300 font-medium tracking-wide ">{String(trade.symbol || '').toUpperCase()}</span>;
            case 'side': {
                const normalizedSide = normalizeTradeSide(
                    t.side ??
                    t.sideCode ??
                    t.side_code ??
                    t.orderSide ??
                    t.order_side ??
                    t['b/s'] ??
                    t.bs ??
                    t.direction ??
                    t.action ??
                    t.type ??
                    t.position ??
                    t.positionType ??
                    t.position_type,
                    t.volume ?? t.quantity ?? t.shares
                );
                if (!normalizedSide) return '';
                const isShort = normalizedSide === 'sell';
                const isLong = normalizedSide === 'buy';
                const wrapperClasses = "flex items-center justify-center w-full";
                    if (isShort) {
                        return (
                            <div className={wrapperClasses}>
                                <span className="inline-flex items-center justify-center gap-1 w-20 p-1.5 rounded-md text-xs font-semibold whitespace-nowrap leading-none bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-700">
                                    <ArrowDownRight className="w-3.5 h-3.5" />
                                    <span className="uppercase text-[11px] tracking-wide leading-none">SHORT</span>
                                </span>
                            </div>
                        );
                    }
                    if (isLong) {
                        return (
                            <div className={wrapperClasses}>
                                <span className="inline-flex items-center justify-center gap-1 w-20 p-1.5 rounded-md text-xs font-semibold whitespace-nowrap leading-none bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700">
                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                    <span className="uppercase text-[11px] tracking-wide leading-none">LONG</span>
                                </span>
                            </div>
                        );
                    }
                    return (
                        <div className={wrapperClasses}>
                            <span className="inline-flex items-center justify-center gap-2 W-18 px-2 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap leading-none bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700">{String(t.side ?? '').toUpperCase()}</span>
                        </div>
                    );
            }
            case 'volume':
                {
                    const size = getTradeSizeContext(trade);
                    const displayValue = size.maxSize;
                    if (!Number.isFinite(displayValue)) return <span className="text-black dark:text-zinc-200"></span>;

                    const formatted = formatTradeSizeValue(Number(displayValue));
                    if (size.isScaled && Number.isFinite(Number(size.closedQty))) {
                        const unitLabel = size.market === 'FOREX' ? 'lots' : 'qty';
                        const formattedClosed = formatTradeSizeValue(Number(size.closedQty));
                        return (
                            <span
                                className="inline-flex items-center justify-center gap-1 text-black dark:text-zinc-200"
                                title={`Scaled Trade\nMax ${formatted} ${unitLabel} | Closed ${formattedClosed} ${unitLabel}`}
                            >
                                <Layers className="h-3.5 w-3.5 text-blue-400" aria-hidden />
                                <span>{formatted}</span>
                            </span>
                        );
                    }
                    return <RenderOrDash className="text-black dark:text-zinc-200">{formatted}</RenderOrDash>;
                }
            case 'executions':
                return (
                    <span className="text-black dark:text-zinc-200">
                        {getExecutionsCount(trade as any)}
                    </span>
                );
            case 'pnl':
                {
                    if (isOpenLikeTradeForRender(trade)) return <RenderOrDash />;
                    const pnl = getTradeDisplayPnl(trade);
                    return <div className={`font-semibold ${pnl >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmtMoney(pnl)}</div>;
                }
            case 'pnlR': {
                const pnlR = getTradePnlRValue(trade);
                const initialRisk = getTradeInitialRiskValue(trade);
                const missingInitialRisk = initialRisk == null || initialRisk <= 0;
                if (missingInitialRisk) {
                    return (
                        <span className="text-black dark:text-zinc-200" title="Add Initial Risk to calculate R">—</span>
                    );
                }
                return (
                    <RenderOrDash className={pnlR == null ? 'text-black dark:text-zinc-200' : (pnlR >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                        {pnlR != null ? `${pnlR.toFixed(2)}R` : ''}
                    </RenderOrDash>
                );
            }
            case 'pnlTicksGrossFutures': {
                const ticks = getTradePnlTicksGrossFuturesValue(trade);
                return (
                    <RenderOrDash className={ticks == null ? 'text-zinc-700 dark:text-white/50' : (ticks >= 0 ? 'text-emerald-600' : 'text-red-600')}>
                        {ticks != null ? ticks.toFixed(2) : ''}
                    </RenderOrDash>
                );
            }
            case 'entryPrice':
                return (
                    <span className="text-black dark:text-zinc-200">
                        {t[key] == null ? '' : formatTradePriceValue(t[key], trade)}
                    </span>
                );
            case 'initialRisk':
                {
                    const initialRisk = getTradeInitialRiskValue(trade);
                    const missing = initialRisk == null || initialRisk <= 0;
                    const isHovered = hoveredRowKey === tradeKey(trade);
                    return (
                        <div className="inline-flex items-center justify-center relative">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openInitialRiskModalForTrade(trade);
                                }}
                                className="text-zinc-700 hover:text-blue-500 dark:text-white/50 dark:hover:text-blue-400 underline-offset-2 hover:underline"
                                title={missing ? 'Click to set Initial Risk' : 'Click to edit Initial Risk'}
                            >
                                {missing ? '+' : fmtMoney(initialRisk)}
                            </button>
                            {missing ? (
                                <span className={`pointer-events-none absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 px-2 py-1 rounded bg-zinc-900 text-sm text-white/90 shadow-sm transition-opacity whitespace-nowrap ${isHovered ? 'visible opacity-100' : 'invisible opacity-0'}`}>
                                    + Set Initial Risk
                                </span>
                            ) : null}
                        </div>
                    );
                }
            case 'totalFees':
                return (
                    <span className="text-black dark:text-zinc-200">
                        {fmtMoney(getTradeFeeAbs(trade))}
                    </span>
                );
            case 'exitPrice':
                {
                    const isOpenLike = isOpenLikeTradeForRender(t);
                    if (isOpenLike) {
                        return (
                            <div className="flex items-center justify-center">
                                <span className="inline-flex items-center justify-center text-xs uppercase p-1 rounded bg-emerald-700 border border-emerald-700 text-white leading-none" title="Open trade">
                                    OPEN
                                </span>
                            </div>
                        );
                    }
                }
                return (
                    <span className="text-black dark:text-zinc-200">
                        {t.exitPrice == null ? '' : formatTradePriceValue(t.exitPrice, trade)}
                    </span>
                );
            case 'positionMFE': {
                const mfe = getTradePositionExcursionValue(trade, 'MFE');
                return (
                    <RenderOrDash className={mfe == null ? 'text-black dark:text-zinc-200' : 'text-emerald-600'}>
                        {mfe != null ? fmtMoney(mfe) : ''}
                    </RenderOrDash>
                );
            }
            case 'positionMAE': {
                const mae = getTradePositionExcursionValue(trade, 'MAE');
                return (
                    <RenderOrDash className={mae == null ? 'text-black dark:text-zinc-200' : 'text-red-600'}>
                        {mae != null ? fmtMoney(mae) : ''}
                    </RenderOrDash>
                );
            }
            case 'duration': {
                const d = (trade as any).duration;
                const openTime = Number((trade as any).openTime);
                const closeTime = Number((trade as any).closeTime);
                const durationMs = Number.isFinite(openTime) && Number.isFinite(closeTime) && closeTime >= openTime
                    ? (closeTime - openTime)
                    : (Number.isFinite(Number(d)) ? Number(d) * 60000 : null);
                const isOpenLike = isOpenLikeTradeForRender(t);
                let result = '';

                if (isOpenLike) {
                    result = 'Open';
                } else if (durationMs != null) {
                    result = formatDurationMs(durationMs);
                }

                return (
                    <span className="text-black dark:text-zinc-200">
                        <RenderOrDash>{result}</RenderOrDash>
                    </span>
                );
            }
            case 'notes': {
                // Inline edit for empty notes (message-square-plus icon). Click to start editing.
                const noteKey = tradeKey(trade);
                if (editingNoteKey === noteKey) {
                    return (
                        <div className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                            <textarea
                                ref={noteInputRef}
                                value={editingNoteValue}
                                onChange={(e) => setEditingNoteValue(e.target.value)}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Escape') {
                                        setEditingNoteKey(null);
                                        setEditingNoteValue('');
                                    } else if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        // save
                                        setTrades(prev => {
                                            const next = prev.map(p => (tradeKey(p) === noteKey ? { ...p, notes: editingNoteValue } : p));
                                            try { saveImportedTrades(next as any[]); } catch {}
                                            try { removePrivateTradingValue('trades-cache'); } catch {}
                                            return next;
                                        });
                                        setEditingNoteKey(null);
                                        setEditingNoteValue('');
                                    }
                                }}
                                rows={3}
                                className="w-full max-w-[20rem] rounded-md bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none"
                                placeholder="Type a note... (Enter to save, Esc to cancel)"
                            />
                            <div className="mt-2 flex gap-2">
                                <button className="px-3 py-1 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-500" onClick={(e) => { e.stopPropagation(); setTrades(prev => {
                                    const next = prev.map(p => (tradeKey(p) === noteKey ? { ...p, notes: editingNoteValue } : p));
                                    try { saveImportedTrades(next as any[]); } catch {}
                                    try { removePrivateTradingValue('trades-cache'); } catch {}
                                    return next;
                                }); setEditingNoteKey(null); setEditingNoteValue(''); }}>
                                    Save
                                </button>
                                <button className="px-3 py-1 rounded-md border border-zinc-300 bg-white text-sm text-zinc-700 hover:bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800" onClick={(e) => { e.stopPropagation(); setEditingNoteKey(null); setEditingNoteValue(''); }}>Cancel</button>
                            </div>
                        </div>
                    );
                }

                if (!t.notes) {
                    return (
                        <div className="flex items-center justify-center">
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); setEditingNoteKey(noteKey); setEditingNoteValue(''); }}
                                className="inline-flex items-center justify-center w-6 h-6 rounded-md text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 dark:text-white/50 dark:hover:text-white/70 dark:hover:bg-zinc-800 transition-colors"
                                aria-label="Add note"
                            >
                                <MessageSquarePlus className="w-4 h-4" />
                            </button>
                        </div>
                    );
                }

                // Show a short preview (few words) in the table cell and expose the full note on hover
                const fullNote = String(t.notes ?? '');
                const words = fullNote.trim().length ? fullNote.trim().split(/\s+/) : [];
                const PREVIEW_WORDS = 4;
                const preview = words.length ? words.slice(0, PREVIEW_WORDS).join(' ') + (words.length > PREVIEW_WORDS ? '…' : '') : '';
                return (
                    <span className="block w-full min-w-0 max-w-full overflow-hidden text-sm text-black dark:text-zinc-200 text-center" title={fullNote}>
                        <span className="block w-full min-w-0 max-w-full truncate">{preview}</span>
                    </span>
                );
            }
            case 'tags':
                if (Array.isArray(t.tags) && t.tags.length > 0) {
                    const visibleTags = getVisibleTradeTags(
                        t.tags,
                        demoTradesActive,
                        demoTradesActive ? { tradeId: getTradeDisplayIdForTable(trade) } : trade
                    );
                    return (
                        <div className={`flex w-full min-w-0 max-w-full flex-wrap items-center justify-center overflow-hidden ${demoTradesActive ? 'gap-2' : 'gap-3'}`}>
                            {visibleTags.map((tag: string, index: number) => (
                                <RemovableTag
                                    key={tag}
                                    label={tag}
                                    title={`Edit ${tag}`}
                                    onClick={() => {
                                        setTagModalTargetKey(tradeKey(trade));
                                        setEditingTag(tag);
                                        setIsTagsModalOpen(true);
                                    }}
                                    onRemove={() => removeTagFromTrade(tradeKey(trade), tag)}
                                    className="!h-[30px] !gap-1.5 !rounded !border-[#3788E5]/30 !bg-[#3788E5]/10 !px-3 !py-1.5 !text-[11px] !font-medium !uppercase !tracking-wide !text-[#3788E5] hover:!bg-[#3788E5]/20"
                                    compact={demoTradesActive}
                                    compactWidth={demoTradesActive
                                        ? visibleTags.length === 1 || (visibleTags.length === 3 && index === 2)
                                            ? 'full'
                                            : 'half'
                                        : 'fixed'}
                                />
                            ))}
                        </div>
                    );
                }

                return (
                    <div className="flex items-center justify-center">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setTagModalTargetKey(tradeKey(trade)); setEditingTag(null); setIsTagsModalOpen(true); }}
                            className="inline-flex items-center justify-center w-6 h-6 rounded-md text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
                            aria-label="Add tags"
                        >
                            <Tag className="w-4 h-4" />
                        </button>
                    </div>
                );
            default:
                return <RenderOrDash>{t[key] ?? ''}</RenderOrDash>;
        }
    };


// Initialize trades from repository (authoritative)
useEffect(() => {
    if (demoTradesActive) return;
    let cancelled = false;

    const initializeTrades = async () => {
        const generation = ++tradeRefreshGenerationRef.current;
        const request = ++tradeRefreshRequestRef.current;
        try {
            const data = await loadTrades({
                onProgress: (partialTrades) => {
                    if (cancelled || generation !== tradeRefreshGenerationRef.current || request !== tradeRefreshRequestRef.current) return;
                    setTrades(partialTrades);
                    setHasInitialTradesLoaded(true);
                },
            });
            if (cancelled || generation !== tradeRefreshGenerationRef.current || request !== tradeRefreshRequestRef.current) return;
            const resolvedTrades = Array.isArray(data) ? data : [];
            if (resolvedTrades.length === 0) lastKnownTradesSnapshot = [];
            else lastKnownTradesSnapshot = resolvedTrades;
            setTrades(resolvedTrades);
            setSelectedTrades(new Set());
            setPage(1);
            setHasInitialTradesLoaded(true);
        } catch (e) {
            console.error('[TradesPage] loadTrades failed', e);
            if (cancelled || generation !== tradeRefreshGenerationRef.current || request !== tradeRefreshRequestRef.current) return;
                const fallbackTrades = loadImportedTrades();
                setTrades(fallbackTrades.length > 0 ? fallbackTrades : lastKnownTradesSnapshot);
            setSelectedTrades(new Set());
            setPage(1);
                setHasInitialTradesLoaded(true);
        }
    };

    void initializeTrades();

    return () => {
        cancelled = true;
    };
}, [demoTradesActive]);

// Keep TradesPage in sync with updates made from TradeDetails (notes/tags edits).
useEffect(() => {
    if (demoTradesActive) return;
    let cancelled = false;

    const refreshFromAuthoritative = async (event?: Event, options: { resume?: boolean } = {}) => {
        if (event && !shouldRefreshTradesForImportedUpdate(event)) return;
        const generation = ++tradeRefreshGenerationRef.current;
        const request = ++tradeRefreshRequestRef.current;
        try {
            const data = await loadTrades({
                resume: options.resume,
                onProgress: (partialTrades) => {
                    if (cancelled || generation !== tradeRefreshGenerationRef.current || request !== tradeRefreshRequestRef.current) return;
                    setTrades(partialTrades);
                    setHasInitialTradesLoaded(true);
                },
            });
            if (!cancelled && generation === tradeRefreshGenerationRef.current && request === tradeRefreshRequestRef.current) {
                const freshTrades = Array.isArray(data) ? data : [];
                setTrades(freshTrades);
                lastKnownTradesSnapshot = freshTrades;
                setSelectedTrades(new Set());
                setPage(1);
            }
        } catch (e) {
            console.error('[TradesPage] refresh from authoritative storage failed', e);
            if (!cancelled && generation === tradeRefreshGenerationRef.current && request === tradeRefreshRequestRef.current) {
                const fallbackTrades = loadImportedTrades();
                setTrades(fallbackTrades.length > 0 ? fallbackTrades : lastKnownTradesSnapshot);
            }
        }
    };

    const onStorage = (event: StorageEvent) => {
        if (!event.key || event.key.startsWith('trade-proof:private:v1:local:') && event.key.endsWith(':imported-trades')) {
            void refreshFromAuthoritative();
        }
    };
    const onPrivateScopeChanged = () => {
        tradeRefreshGenerationRef.current += 1;
        lastKnownTradesSnapshot = [];
        setTrades([]);
        setSelectedTrades(new Set());
        setPage(1);
        setHasInitialTradesLoaded(false);
        void refreshFromAuthoritative();
    };

    const onCsvImportsCleared = (event: Event) => {
        const detail = (event as CustomEvent<{
            localClearSucceeded?: boolean;
            sourceBroker?: string;
            sourceAccountRef?: string;
            sourceAccountId?: string;
        }>).detail;
        const localClearSucceeded = detail?.localClearSucceeded === true;
        tradeRefreshGenerationRef.current += 1;
        if (detail?.sourceBroker && detail.sourceAccountRef && detail.sourceAccountId) {
            const clearArgs = {
                sourceBroker: detail.sourceBroker,
                sourceAccountRef: detail.sourceAccountRef,
                sourceAccountId: detail.sourceAccountId,
            };
            setTrades((current) => current.filter((trade) => !isImportedTradeForAccount(trade, clearArgs)));
            lastKnownTradesSnapshot = lastKnownTradesSnapshot.filter((trade) => !isImportedTradeForAccount(trade, clearArgs));
        } else {
            // Legacy clear events have no account scope; only use the authoritative
            // refresh path for them so manually entered trades are not discarded.
            setPage(1);
        }
        setSelectedTrades((current) => {
            if (!detail?.sourceBroker || !detail.sourceAccountRef || !detail.sourceAccountId) return current;
            const clearArgs = { sourceBroker: detail.sourceBroker, sourceAccountRef: detail.sourceAccountRef, sourceAccountId: detail.sourceAccountId };
            return new Set([...current].filter((key) => trades.some((trade) => tradeKey(trade) === key && !isImportedTradeForAccount(trade, clearArgs))));
        });
        setPage(1);
        if (!localClearSucceeded) void refreshFromAuthoritative();
    };

    const onRetryHistory = () => void refreshFromAuthoritative(undefined, { resume: true });

    window.addEventListener(IMPORTED_TRADES_UPDATED_EVENT, refreshFromAuthoritative as EventListener);
    window.addEventListener(CUSTOM_TRADES_UPDATED_EVENT, refreshFromAuthoritative as EventListener);
    window.addEventListener('trade-proof-csv-imports-cleared', onCsvImportsCleared);
    window.addEventListener('storage', onStorage);
    window.addEventListener(privateTradingScopeChangedEventName(), onPrivateScopeChanged);
    window.addEventListener(PERSISTED_TRADE_HISTORY_RETRY_EVENT, onRetryHistory);
    return () => {
        cancelled = true;
        window.removeEventListener(IMPORTED_TRADES_UPDATED_EVENT, refreshFromAuthoritative as EventListener);
        window.removeEventListener(CUSTOM_TRADES_UPDATED_EVENT, refreshFromAuthoritative as EventListener);
        window.removeEventListener('trade-proof-csv-imports-cleared', onCsvImportsCleared);
        window.removeEventListener('storage', onStorage);
        window.removeEventListener(privateTradingScopeChangedEventName(), onPrivateScopeChanged);
        window.removeEventListener(PERSISTED_TRADE_HISTORY_RETRY_EVENT, onRetryHistory);
    };
}, [demoTradesActive]);


    const handleImportClick = () => {
        csvImportAccountRef.current = null;
        setCsvSourceBroker('');
        setCsvSourceAccountId('');
        setCsvSourceAccountName('');
        setCsvAccountsError('');
        setShowCsvAccountPicker(true);
        setCsvAccountsLoading(true);
        void listCsvSourceAccounts()
            .then(setCsvSourceAccounts)
            .catch(() => setCsvAccountsError('Saved broker accounts could not be loaded. Check your connection and try again.'))
            .finally(() => setCsvAccountsLoading(false));
    };

    const continueCsvImport = () => {
        const creatingAccount = csvSourceAccountId === '__create__';
        const selectedAccount = csvSourceAccounts.find((account) => account.id === csvSourceAccountId);
        if (!csvSourceBroker) {
            setCsvAccountsError('Select the broker used for this CSV export.');
            return;
        }
        if (!creatingAccount && !selectedAccount) {
            setCsvAccountsError('Select a saved account or choose to create a new one.');
            return;
        }
        const accountName = creatingAccount ? csvSourceAccountName.trim() : String(selectedAccount?.accountName || '').trim();
        if (!accountName) {
            setCsvAccountsError('Enter a name for the new broker account.');
            return;
        }
        const sourceBroker = selectedAccount?.sourceBroker || csvSourceBroker;
        if (selectedAccount && sourceBroker !== csvSourceBroker) {
            setCsvAccountsError('The selected account does not match the chosen broker.');
            return;
        }
        csvImportAccountRef.current = {
            sourceBroker,
            sourceAccountId: selectedAccount?.id,
            accountName,
        };
        setShowCsvAccountPicker(false);
        fileInputRef.current?.click();
    };

    const handleFileSelected = async (e: any) => {
        const f: File | undefined = e?.target?.files?.[0];
        if (e?.target) {
            e.target.value = ''; // Allow selecting the same file again
        }
        if (!f) return;

        if (!csvImportAccountRef.current) {
            showImportBanner({ success: false, message: 'Choose a supported broker account before importing this CSV.' });
            return;
        }

        setImporting(true);
        setProgress(10);

        try {
            const reader = new FileReader();
            reader.onload = async (evt) => {
                try {
                    const content = evt.target?.result as string;
                    const results = parseCSV(content);
                    const sourceTimeZone = resolveCsvSourceTimezone(results.data || [], results.meta.fields || []);
                    let rejectionDiagnostics = buildCsvRejectionDiagnostics(results.data || [], { timeZone: sourceTimeZone });
                    const parsedRowCount = rejectionDiagnostics.length;
                    const initialInvalidRowCount = rejectionDiagnostics.filter(isGenuineCsvIssueDiagnostic).length;

                    // simulate progress increments
                    const progressInterval = window.setInterval(() => {
                        setProgress(prev => Math.min(92, prev + 6));
                    }, 350);

                    // send to backend importer
                    const existingImported = loadImportedTrades();
                    const existingExecutionKeys = collectExistingExecutionKeys(existingImported);

                    const importResp = await fetch('/api/import/trades', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(await buildCsvImportAuthHeaders()),
                        },
                        body: JSON.stringify({
                            fields: results.meta.fields,
                            rows: results.data,
                            brokerId: csvImportAccountRef.current!.sourceBroker,
                            timeZone: sourceTimeZone,
                            accountTag: csvImportAccountRef.current!.accountName,
                            tags: [],
                            existingExecutionKeys: Array.from(existingExecutionKeys),
                        }),
                    });

                    if (!importResp.ok) {
                        const text = await importResp.text();
                        throw new Error(text || 'Import API request failed');
                    }
                    const importOut: any = await importResp.json();
                    const trades = Array.isArray(importOut?.trades) ? importOut.trades : [];
                    const backendDuplicateExecutionCount = Number(importOut?.duplicateExecutionCount) || 0;
                    const backendDuplicateTradeCount = Number(importOut?.duplicateTradeCount) || 0;
                    const backendDuplicateTradeDiagnostics = Array.isArray(importOut?.duplicateTradeDiagnostics) ? importOut.duplicateTradeDiagnostics : [];
                    const backendRejectionDiagnostics = Array.isArray(importOut?.rejectionDiagnostics) ? importOut.rejectionDiagnostics : [];
                    const backendOverlapDiagnostics = backendRejectionDiagnostics.filter((diagnostic: any) => diagnostic?.code === 'PARTIAL_EXECUTION_OVERLAP_REQUIRES_REVIEW');
                    const backendTenantDiagnostics = backendRejectionDiagnostics.filter((diagnostic: any) => diagnostic?.code === 'ASSET_CLASS_NOT_ENABLED');
                    const backendOverlapLifecycleCount = Number(importOut?.rejectedOverlapLifecycleCount) || backendOverlapDiagnostics.length;
                    const backendTenantBlockedCount = Number(importOut?.tenantBlockedCount) || backendTenantDiagnostics.length;
                    rejectionDiagnostics = applyImportOverlapDiagnostics(rejectionDiagnostics, [...backendOverlapDiagnostics, ...backendTenantDiagnostics], results.data || []);
                    let rejectedCount = rejectionDiagnostics.filter((diagnostic: any) => !diagnostic.accepted).length;
                    const hasDuplicateOnlyResponse = backendDuplicateExecutionCount > 0 || backendDuplicateTradeCount > 0;
                    let resolvedCsvAccount: CsvSourceAccountOption | null = null;
                    if (trades.length > 0 || hasDuplicateOnlyResponse) {
                        const selectedAccount = csvImportAccountRef.current;
                        if (!selectedAccount) throw new Error('Choose a supported broker account before importing this CSV.');
                        const resolutionInput = buildCsvSourceAccountResolutionInput({
                            sourceBroker: selectedAccount.sourceBroker,
                            accountName: selectedAccount.accountName,
                            sourceAccountId: selectedAccount.sourceAccountId,
                            rawRows: results.data || [],
                            trades,
                        });
                        const resolved = await resolveCsvSourceAccount(resolutionInput);
                        resolvedCsvAccount = requireResolvedCsvSourceAccount(resolved, selectedAccount.sourceBroker);
                    }

                    clearInterval(progressInterval);
                    setProgress(96);

                    if (trades.length === 0 && !hasDuplicateOnlyResponse && backendOverlapLifecycleCount === 0 && backendTenantBlockedCount === 0 && initialInvalidRowCount === 0) {
                        setTimeout(() => {
                            showImportBanner({
                                success: true,
                                count: 0,
                                executionCount: 0,
                                rejectedCount: 0,
                                rowsRead: parsedRowCount,
                            });
                            setImporting(false);
                            setProgress(0);
                        }, 600);
                        return;
                    }

                    // attempt Firestore save
                    if (trades.length > 0 && typeof (window as any).__saveTradesToFirestore === 'function') {
                        try { await (window as any).__saveTradesToFirestore(trades); } catch (err) { console.warn('[TradesPage] __saveTradesToFirestore failed', err); }
                    }

                    // persist locally with dedupe against existing imported executions
                    let importedExecutionCount = 0;
                    let duplicateExecutionCount = backendDuplicateExecutionCount;
                    let skippedInvalidCount = initialInvalidRowCount;
                    const clientDuplicateTradeDiagnostics: any[] = [];
                    const clientOverlapDiagnostics: any[] = [];
                    try {
                        const batchId = `import-${Date.now()}`;
                        const mappedIncoming = trades.map((t: any) => {
                            const openTs = t.openTsSec ?? null;
                            const closeTs = t.closeTsSec ?? null;
                            const normalizedExecTimesMs = (Array.isArray(t.executionsList) ? t.executionsList : [])
                                .map((e: any) => {
                                    const raw = e?.timestamp;
                                    const num = Number(raw);
                                    if (Number.isFinite(num) && num > 0) return num < 1e11 ? num * 1000 : num;
                                    return null;
                                })
                                .filter((ts: number | null): ts is number => ts != null);
                            const executedAtMs = openTs
                                ? openTs * 1000
                                : (normalizedExecTimesMs.length > 0 ? Math.min(...normalizedExecTimesMs) : null);
                            const openTimestampMs = executedAtMs;
                            const closeTimestampMs = closeTs
                                ? closeTs * 1000
                                : (normalizedExecTimesMs.length > 0 ? Math.max(...normalizedExecTimesMs) : null);
                            const calculatedPnlRaw = t.calculatedPnl ?? t.pnl;
                            const calculatedPnl = Number.isFinite(Number(calculatedPnlRaw)) ? Number(calculatedPnlRaw) : null;
                            const brokerReportedPnl = Number.isFinite(Number(t.brokerReportedPnl))
                                ? Number(t.brokerReportedPnl)
                                : null;
                            const pnlDifference = Number.isFinite(Number(t.pnlDifference))
                                ? Number(t.pnlDifference)
                                : (brokerReportedPnl != null && calculatedPnl != null ? brokerReportedPnl - calculatedPnl : null);
                            const csvTags = Array.isArray(t.tags) && t.tags.length > 0 ? t.tags : undefined;

                            return normalizeTradeAssetClassification({
                                storageId: `imported-${t.id}`,
                                importBatchId: batchId,
                                importedAt: Date.now(),
                                executedAt: executedAtMs,
                                tradeId: t.tradeId ?? String(t.id),
                                originalId: t.tradeId ?? t.id,
                                sourceOrderIds: Array.isArray(t.sourceOrderIds) ? t.sourceOrderIds : [],
                                isReversal: Boolean(t.isReversal),
                                reversalOf: t.reversalOf ?? undefined,
                                reversalChainId: t.reversalChainId ?? undefined,
                                symbol: t.symbol,
                                side: t.side,
                                timestamp: executedAtMs,
                                openTimestampMs,
                                closeTimestampMs,
                                openTime: openTimestampMs,
                                closeTime: closeTimestampMs,
                                quantity: t.quantity,
                                volume: t.quantity,
                                shares: t.quantity,
                                executionCount: t.executionCount,
                                executionsList: Array.isArray(t.executionsList) ? t.executionsList : [],
                                entryPrice: t.entryPrice,
                                exitPrice: t.exitPrice ?? null,
                                pnl: calculatedPnl,
                                calculatedPnl,
                                brokerReportedPnl,
                                pnlDifference,
                                durationMinutes:
                                  openTimestampMs != null && closeTimestampMs != null && closeTimestampMs >= openTimestampMs
                                    ? Math.max(0, Math.round((closeTimestampMs - openTimestampMs) / 60000))
                                    : undefined,
                                commission: Number(t.commission ?? 0),
                                notes: String(t.notes ?? ''),
                                tags: csvTags ?? [],
                                assetClass: typeof t.assetClass === 'string' ? t.assetClass : undefined,
                                secType: typeof t.secType === 'string' ? t.secType : undefined,
                                instrumentType: typeof t.instrumentType === 'string' ? t.instrumentType : undefined,
                                account: resolvedCsvAccount?.accountName,
                                accountName: resolvedCsvAccount?.accountName,
                                sourceAccountId: resolvedCsvAccount?.id,
                                sourceAccountRef: resolvedCsvAccount?.sourceAccountRef,
                                sourceBroker: resolvedCsvAccount?.sourceBroker || csvImportAccountRef.current?.sourceBroker,
                                broker: resolvedCsvAccount?.sourceBroker || csvImportAccountRef.current?.sourceBroker,
                                timeZone: sourceTimeZone,
                                source: 'csv',
                                sourceType: 'csv',
                            });
                        });

                        const dedupedIncoming: any[] = [];

                        mappedIncoming.forEach((record: any) => {
                            const keys = collectExecutionDedupeKeys(record);
                            if (keys.length === 0) {
                                dedupedIncoming.push(record);
                                importedExecutionCount += Math.max(1, Number(record.executionCount) || 1);
                                return;
                            }

                            const filteredRecord = filterDuplicateExecutionsFromImportedTrade(record, existingExecutionKeys);
                            if (!filteredRecord) {
                                duplicateExecutionCount += keys.length;
                                clientDuplicateTradeDiagnostics.push({
                                    code: 'DUPLICATE_TRADE_SKIPPED',
                                    symbol: record.symbol,
                                    assetClass: record.assetClass,
                                    rowOrdinals: getTradeSourceRowOrdinals(record, results.data || []),
                                    reasons: ['All executions in this trade were already imported.'],
                                });
                                return;
                            }

                            const keptKeys = collectExecutionDedupeKeys(filteredRecord);
                            dedupedIncoming.push(filteredRecord);
                            importedExecutionCount += keptKeys.length;
                            duplicateExecutionCount += Math.max(0, keys.length - keptKeys.length);
                        });

                        rejectionDiagnostics = applyImportOverlapDiagnostics(rejectionDiagnostics, clientOverlapDiagnostics, results.data || []);

                        const summaryCounts = reconcileCsvImportSummaryCounts(
                            results.data || [],
                            importedExecutionCount,
                            initialInvalidRowCount,
                            duplicateExecutionCount,
                        );

                        importedExecutionCount = summaryCounts.importedExecutionCount;
                        duplicateExecutionCount = summaryCounts.duplicateExecutionCount;
                        skippedInvalidCount = summaryCounts.skippedInvalidCount;

                        // Allocate globally-unique visible Trade IDs (T{max+1}..T{max+n})
                        // ONLY for the trades the existing duplicate rules accepted. A
                        // duplicate-only reimport therefore consumes no IDs.
                        const allocatedIncoming = allocateVisibleTradeIds(existingImported, dedupedIncoming);
                        const toPersist = [...existingImported, ...allocatedIncoming];
                        if ((allocatedIncoming.length > 0 || hasDuplicateOnlyResponse) && !resolvedCsvAccount) {
                            throw new Error('A stable broker account could not be confirmed. No trades were saved.');
                        }
                        if (resolvedCsvAccount) {
                            for (const trade of allocatedIncoming) {
                                trade.source = 'csv';
                                trade.sourceType = 'csv';
                                trade.sourceBroker = resolvedCsvAccount.sourceBroker;
                                trade.broker = resolvedCsvAccount.sourceBroker;
                                trade.sourceAccountId = resolvedCsvAccount.id;
                                trade.sourceAccountRef = resolvedCsvAccount.sourceAccountRef;
                                trade.account = resolvedCsvAccount.accountName;
                                trade.accountName = resolvedCsvAccount.accountName;
                            }
                        }

                        const persistRequest = buildCsvImportPersistRequest({
                            rawRows: results.data || [],
                            filename: f.name,
                            timeZone: sourceTimeZone,
                            brokerId: resolvedCsvAccount?.sourceBroker,
                            sourceAccountId: resolvedCsvAccount?.id,
                            accountTag: resolvedCsvAccount?.accountName,
                            sourceAccountRef: resolvedCsvAccount?.sourceAccountRef,
                            tags: [],
                            clientAcceptedCount: summaryCounts.importedExecutionCount,
                            clientDuplicateCount: summaryCounts.duplicateExecutionCount,
                        });

                        const saveLocalAfterVerification = () => allocatedIncoming.length === 0 || saveImportedTrades(toPersist);
                        const persistenceResult = await persistCsvImportAfterLocalSave({
                            saveLocal: saveLocalAfterVerification,
                            requestBody: persistRequest,
                            verifyRemote: verifyPersistedCsvImport,
                            onSuccess: (result) => {
                                console.info('[TradesPage] persisted CSV backend sync completed', result);
                            },
                            onFailure: (error) => {
                                console.warn('[TradesPage] persisted CSV backend sync failed', error);
                            },
                        });
                        if (!persistenceResult.serverVerified) {
                            if (isCsvImportVerificationRetryable(persistenceResult.remoteResult)) {
                                setPendingCsvVerification({ remoteResult: persistenceResult.remoteResult, saveLocal: saveLocalAfterVerification });
                            }
                            throw persistenceResult.failure || new Error('Server persistence was not confirmed; no trades were saved.');
                        }
                        setPendingCsvVerification(null);
                        if (String(persistenceResult.remoteResult?.status || '').toLowerCase() === 'duplicate_exact_file') {
                            dedupedIncoming.splice(0, dedupedIncoming.length);
                            allocatedIncoming.splice(0, allocatedIncoming.length);
                            importedExecutionCount = 0;
                            duplicateExecutionCount = Math.max(
                                duplicateExecutionCount,
                                Number(persistenceResult.remoteResult?.batchCounts?.acceptedExecutionCount) || 0,
                            );
                        }
                        const reloaded = await loadTrades();
                        setTrades(reloaded);
                        try { removePrivateTradingValue('trades-cache'); } catch {}

                        rejectedCount = rejectionDiagnostics.filter((diagnostic) => !diagnostic.accepted).length;
                        const rejectedTradeLifecycles = backendOverlapLifecycleCount + clientOverlapDiagnostics.length;
                        const tenantRestrictedTradesExcluded = backendTenantBlockedCount;
                        const summary = buildCsvImportSummary({
                            rows: results.data || [],
                            rowDiagnostics: rejectionDiagnostics,
                            newTradesImported: dedupedIncoming.length,
                            duplicateTradesSkipped: backendDuplicateTradeCount + clientDuplicateTradeDiagnostics.length,
                            duplicateExecutionsSkipped: duplicateExecutionCount,
                            invalidRowsSkipped: initialInvalidRowCount,
                            rejectedTradeLifecycles,
                            tenantRestrictedTradesExcluded,
                            importedTrades: dedupedIncoming,
                            duplicateTradeDiagnostics: [...backendDuplicateTradeDiagnostics, ...clientDuplicateTradeDiagnostics],
                            rejectedLifecycleDiagnostics: [...backendOverlapDiagnostics, ...clientOverlapDiagnostics],
                            tenantRestrictedDiagnostics: backendTenantDiagnostics,
                        });

                        const hasReviewDetails = summary.invalidRowsSkipped > 0 || summary.rejectedTradeLifecycles > 0 || summary.tenantRestrictedTradesExcluded > 0;
                        if (hasReviewDetails) {
                            const rejectionLog = {
                                id: `rejections-${Date.now()}`,
                                timestamp: Date.now(),
                                sourceFile: f.name,
                                parsedRowCount,
                                acceptedExecutionCount: importedExecutionCount,
                                rejectedCount,
                                sourceTimezone: sourceTimeZone || null,
                                summary,
                                rows: rejectionDiagnostics,
                                lifecycleDiagnostics: [...backendOverlapDiagnostics, ...clientOverlapDiagnostics],
                                tenantRestrictedTrades: backendTenantDiagnostics,
                            };
                            try { sessionStorage.removeItem('tt_rejected_inspected'); } catch {}
                            try { writePrivateTradingJson('import-rejections', JSON.stringify(rejectionLog), 'session'); } catch {}
                            try { writePrivateTradingJson('import-rejections', JSON.stringify(rejectionLog)); } catch {}
                        }

                        showImportBanner({
                            success: true,
                            count: dedupedIncoming.length,
                            executionCount: importedExecutionCount,
                            duplicateCount: duplicateExecutionCount,
                            skippedInvalidCount: summaryCounts.skippedInvalidCount,
                            rejectedCount,
                            rowsRead: parsedRowCount,
                            summary,
                            message: 'CSV imported successfully.',
                        });
                    } catch (err: any) {
                        console.warn('[TradesPage] Failed to persist imported records locally', err);
                        const summary = buildCsvImportSummary({
                            rows: results.data || [],
                            rowDiagnostics: rejectionDiagnostics,
                            newTradesImported: 0,
                            duplicateTradesSkipped: backendDuplicateTradeCount + clientDuplicateTradeDiagnostics.length,
                            duplicateExecutionsSkipped: duplicateExecutionCount,
                            invalidRowsSkipped: initialInvalidRowCount,
                            rejectedTradeLifecycles: backendOverlapLifecycleCount + clientOverlapDiagnostics.length,
                            tenantRestrictedTradesExcluded: backendTenantBlockedCount,
                            duplicateTradeDiagnostics: [...backendDuplicateTradeDiagnostics, ...clientDuplicateTradeDiagnostics],
                            rejectedLifecycleDiagnostics: [...backendOverlapDiagnostics, ...clientOverlapDiagnostics],
                            tenantRestrictedDiagnostics: backendTenantDiagnostics,
                        });
                        showImportBanner({
                            success: false,
                            count: 0,
                            executionCount: importedExecutionCount,
                            duplicateCount: duplicateExecutionCount,
                            skippedInvalidCount,
                            rejectedCount,
                            rowsRead: parsedRowCount,
                            summary,
                            message: err?.message || 'The import was analyzed, but server persistence could not be confirmed; no trade was confirmed saved.',
                        });
                    }

                    setTimeout(() => {
                        setImporting(false);
                        setProgress(0);
                    }, 800);
                } catch (err: any) {
                    console.error('[TradesPage] import error', err);
                    showImportBanner({
                        success: false,
                        message: err?.message || 'Failed to import CSV.',
                    });
                    setImporting(false);
                    setProgress(0);
                }
            };
            reader.readAsText(f);
        } catch (err) {
            console.error(err);
            showImportBanner({ success: false, message: 'Import failed due to an unexpected error.' });
            setImporting(false);
            setProgress(0);
        }
    };

    const resizeHandle = (key: ColKey) => {
        const visible = orderedVisibleKeys;
        const isLast = visible[visible.length - 1] === key;
        const hasPair = isLast ? visible.length > 1 : visible.includes(key);
        if (!hasPair) return null;

        return (
            <div
                className={`absolute ${isLast ? 'left-0' : 'right-0'} top-0 h-full w-4 cursor-col-resize opacity-100`}
                onPointerDown={(e) => {
                    e.stopPropagation();
                    startResize(e, key);
                }}
                onClick={(e) => {
                    // clicking the resize area shouldn't trigger header click/sort
                    e.stopPropagation();
                }}
            >
                <div className={`absolute ${isLast ? 'left-0' : 'right-0'} top-0 h-full w-px bg-zinc-300 dark:bg-zinc-700`} />
            </div>
        );
    };
    // Only consider trades visible on the current page for "select all" behavior
    const visibleTrades = pagedTrades;
    const tradeKey = getTradeStorageKey;
    const allSelected = visibleTrades.length > 0 && visibleTrades.every(t => selectedTrades.has(tradeKey(t)));

    const toggleSelectAll = () => {
        const newSelection = new Set<string>(selectedTrades);
        if (allSelected) {
            // remove visible page ids
            for (const t of visibleTrades) newSelection.delete(tradeKey(t));
        } else {
            // add visible page ids
            for (const t of visibleTrades) newSelection.add(tradeKey(t));
        }
        setSelectedTrades(newSelection);
    };

    const toggleTradeSelection = (idOrTrade: number | string) => {
        const key = String(idOrTrade);
        const newSelection = new Set<string>(selectedTrades);
        if (newSelection.has(key)) {
            newSelection.delete(key);
        } else {
            newSelection.add(key);
        }
        setSelectedTrades(newSelection);
    };

    const selectedTags = useMemo(() => {
        const tagSet = new Set<string>();
        for (const t of groupedTrades) {
            if (!selectedTrades.has(tradeKey(t))) continue;
            const tags = Array.isArray((t as any).tags) ? (t as any).tags : [];
            for (const tag of tags) tagSet.add(String(tag));
        }
        // Sort tags alphabetically (case-insensitive)
        return Array.from(tagSet).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    }, [groupedTrades, selectedTrades]);

    const selectedExecutionIds = useMemo(() => {
        const ids = new Set<number>();
        for (const t of groupedTrades) {
            if (!selectedTrades.has(tradeKey(t))) continue;
            const src = (t as any).sourceIds as number[] | undefined;
            if (Array.isArray(src)) for (const id of src) ids.add(id);
        }
        return ids;
    }, [groupedTrades, selectedTrades]);

    const isRawTradeSelected = (t: Trade): boolean => {
        if (selectedTrades.has(tradeKey(t as any))) return true;
        const idNum = Number((t as any)?.id);
        return Number.isFinite(idNum) && selectedExecutionIds.has(idNum);
    };

    const getPersistedDeleteTargets = (rows: Trade[]) => {
        const targets: Array<{ tradeId: string; accountId: string; sourceType: 'manual' | 'csv' }> = [];
        for (const trade of rows as any[]) {
            const tradeId = String(trade?.persistedTradeId ?? trade?.persisted_trade_id ?? '').trim();
            if (!tradeId) {
                if (String(trade?.persistedSource ?? '').trim().toLowerCase() === 'persisted') {
                    throw new Error('A persisted trade is missing its server identity. Refresh the Trades page and try again.');
                }
                continue;
            }
            const sourceType = String(trade?.sourceType ?? trade?.source_type ?? trade?.source ?? '').trim().toLowerCase();
            if (sourceType !== 'manual' && sourceType !== 'csv') {
                throw new Error('A selected trade has an unsupported or unknown persisted source. No trades were deleted.');
            }
            const accountId = String(trade?.sourceAccountId ?? trade?.source_account_id ?? '').trim();
            if (!accountId) throw new Error('A selected persisted trade is missing its account identity. No trades were deleted.');
            targets.push({ tradeId, accountId, sourceType });
        }
        const unique = new Map(targets.map((target) => [`${target.accountId}\u0000${target.tradeId}`, target]));
        return [...unique.values()];
    };

    const refreshAfterConfirmedDeletion = async (deletedIds: Set<string>, selectedKeys: Set<string>) => {
        const generation = ++tradeRefreshGenerationRef.current;
        const request = ++tradeRefreshRequestRef.current;
        const refreshed = await loadTrades();
        if (generation !== tradeRefreshGenerationRef.current || request !== tradeRefreshRequestRef.current) return;
        const visible = (Array.isArray(refreshed) ? refreshed : []).filter((trade: any) => {
            const persistedId = String(trade?.persistedTradeId ?? trade?.persisted_trade_id ?? '').trim();
            return !(persistedId && deletedIds.has(persistedId)) && !selectedKeys.has(tradeKey(trade));
        });
        lastKnownTradesSnapshot = visible;
        setTrades(visible);
    };

    const confirmDeleteSelectedTrades = async () => {
        if (deletingSelectedTrades) return;
        setDeleteSelectedError('');
        const selectedRows = groupedTrades.filter((trade) => selectedTrades.has(tradeKey(trade)));
        let persistedTargets: Array<{ tradeId: string; accountId: string; sourceType: 'manual' | 'csv' }>;
        try { persistedTargets = getPersistedDeleteTargets(selectedRows); }
        catch (error) {
            setDeleteSelectedError(error instanceof Error ? error.message : 'The selected trades could not be validated.');
            return;
        }

        setDeletingSelectedTrades(true);
        try {
            if (persistedTargets.length > 0) await deleteSelectedPersistedTrades(persistedTargets);

            const selectedKeys = new Set(selectedRows.map((trade) => tradeKey(trade)));
            const confirmedDeletedIds = new Set(persistedTargets.map((target) => target.tradeId));
            const remaining = trades.filter((trade) => !isRawTradeSelected(trade));
            try {
                const remainingImported = loadImportedTrades().filter((trade: any) => {
                    const persistedId = String(trade?.persistedTradeId ?? trade?.persisted_trade_id ?? '').trim();
                    if (persistedId && confirmedDeletedIds.has(persistedId)) return false;
                    return !isRawTradeSelected(trade);
                });
                saveImportedTrades(remainingImported, { source: TRADES_PAGE_DELETE_MUTATION_SOURCE });
                const remainingCustom = loadCustomTrades().filter((trade: any) => {
                    const persistedId = String(trade?.persistedTradeId ?? trade?.persisted_trade_id ?? '').trim();
                    return !(persistedId && confirmedDeletedIds.has(persistedId)) && !isRawTradeSelected(trade);
                });
                saveCustomTrades(remainingCustom, { source: TRADES_PAGE_DELETE_MUTATION_SOURCE });
            } catch {}
            try { removePrivateTradingValue('trades-cache'); } catch {}
            lastKnownTradesSnapshot = remaining;
            setTrades(remaining);
            setSelectedTrades(new Set());
            setConfirmDeleteSelectedOpen(false);

            if (persistedTargets.length > 0) {
                try {
                    await refreshAfterConfirmedDeletion(confirmedDeletedIds, selectedKeys);
                } catch (error) {
                    console.error('[TradesPage] trades deleted but authoritative refresh failed', error);
                }
            }

            window.dispatchEvent(new CustomEvent('tt:toast', {
                detail: {
                    title: 'Trades Deleted',
                    message: persistedTargets.length > 0
                        ? `${persistedTargets.length} persisted ${persistedTargets.length === 1 ? 'trade was' : 'trades were'} deleted from the selected account(s).`
                        : `${selectedRows.length} local ${selectedRows.length === 1 ? 'trade was' : 'trades were'} removed from this browser.`,
                    variant: 'success',
                    duration: 5000,
                },
            }));
        } catch (error) {
            const message = error instanceof Error ? error.message : 'The selected trades could not be deleted.';
            setDeleteSelectedError(message);
            window.dispatchEvent(new CustomEvent('tt:toast', {
                detail: { title: 'Delete Failed', message, variant: 'error', duration: 7000 },
            }));
        } finally {
            setDeletingSelectedTrades(false);
        }
    };

    const confirmDeleteAllTrades = async () => {
        if (deletingAllTrades) return;
        setDeleteAllError('');
        let persistedTargets: Array<{ tradeId: string; accountId: string; sourceType: 'manual' | 'csv' }>;
        try { persistedTargets = getPersistedDeleteTargets(trades); }
        catch (error) {
            setDeleteAllError(error instanceof Error ? error.message : 'The trades could not be validated.');
            return;
        }

        setDeletingAllTrades(true);
        try {
            if (persistedTargets.length > 0) await deleteSelectedPersistedTrades(persistedTargets);
            const deletedIds = new Set(persistedTargets.map((target) => target.tradeId));
            const selectedKeys = new Set(trades.map((trade) => tradeKey(trade)));
            const remainingImported = loadImportedTrades().filter((trade: any) => {
                const persistedId = String(trade?.persistedTradeId ?? trade?.persisted_trade_id ?? '').trim();
                return !(persistedId && deletedIds.has(persistedId)) && !selectedKeys.has(tradeKey(trade));
            });
            saveImportedTrades(remainingImported, { source: TRADES_PAGE_DELETE_MUTATION_SOURCE });
            const remainingCustom = loadCustomTrades().filter((trade: any) => {
                const persistedId = String(trade?.persistedTradeId ?? trade?.persisted_trade_id ?? '').trim();
                return !(persistedId && deletedIds.has(persistedId)) && !selectedKeys.has(tradeKey(trade));
            });
            saveCustomTrades(remainingCustom, { source: TRADES_PAGE_DELETE_MUTATION_SOURCE });
            lastKnownTradesSnapshot = [];
            setTrades([]);
            try { removePrivateTradingValue('trades-cache'); } catch {}
            setSelectedTrades(new Set());
            setConfirmDeleteAllOpen(false);
            if (persistedTargets.length > 0) {
                try { await refreshAfterConfirmedDeletion(deletedIds, selectedKeys); }
                catch (error) { console.error('[TradesPage] trades deleted but authoritative refresh failed', error); }
            }
            window.dispatchEvent(new CustomEvent('tt:toast', {
                detail: {
                    title: 'Trades Deleted',
                    message: persistedTargets.length > 0
                        ? `${persistedTargets.length} persisted ${persistedTargets.length === 1 ? 'trade was' : 'trades were'} deleted from the server.`
                        : 'Locally stored trades were cleared.',
                    variant: 'success',
                    duration: 5000,
                },
            }));
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Trades could not be deleted.';
            setDeleteAllError(message);
            window.dispatchEvent(new CustomEvent('tt:toast', {
                detail: { title: 'Delete Failed', message, variant: 'error', duration: 7000 },
            }));
        } finally {
            setDeletingAllTrades(false);
        }
    };

    const getInitialRiskTargetKeys = (trade: Trade): Set<string> => {
        const keys = new Set<string>([tradeKey(trade as any)]);
        const executionRows = Array.isArray((trade as any)?.executionsList)
            ? (trade as any).executionsList
            : (Array.isArray((trade as any)?.executions) ? (trade as any).executions : []);
        for (const row of executionRows) keys.add(tradeKey(row));
        return keys;
    };

    const persistInitialRiskForTrade = (
        trade: Trade,
        payload: { amount: number; method: InitialRiskMethod; stopPrice: number | null }
    ) => {
        const amount = Number(payload.amount);
        if (!Number.isFinite(amount) || amount <= 0) return;
        const targetKeys = getInitialRiskTargetKeys(trade);

        setTrades((prev) => {
            const next = prev.map((row) => {
                if (!targetKeys.has(tradeKey(row as any))) return row;
                const updated = {
                    ...(row as any),
                    initialRisk: amount,
                    initialRiskAmount: amount,
                    initial_risk: amount,
                    riskAmount: amount,
                    initialRiskMethod: payload.method,
                    initialStopPrice: payload.method === 'stopPrice' ? payload.stopPrice : null,
                };
                return updated as Trade;
            });
            try { saveImportedTrades(next as any[]); } catch {}
            try { removePrivateTradingValue('trades-cache'); } catch {}
            return next;
        });
    };

    const openInitialRiskModalForTrade = (trade: Trade) => {
        const initialRisk = getTradeInitialRiskValue(trade);
        const existingMethodRaw = String((trade as any)?.initialRiskMethod ?? '').trim();
        const existingMethod: InitialRiskMethod = existingMethodRaw === 'stopPrice' ? 'stopPrice' : 'amount';
        const existingStop = Number((trade as any)?.initialStopPrice);

        setRiskModalTrade(trade);
        setRiskModalMethod(existingMethod);
        setRiskAmountInput(initialRisk != null ? String(initialRisk) : '');
        setStopPriceInput(Number.isFinite(existingStop) && existingStop > 0 ? String(existingStop) : '');
    };

    const openInitialRiskModalForSelection = () => {
        const selected = groupedTrades.filter((trade) => selectedTrades.has(tradeKey(trade)));
        if (selected.length !== 1) {
            try {
                window.dispatchEvent(new CustomEvent('tt:toast', {
                    detail: {
                        message: 'Select exactly one trade to set Initial Risk.',
                        variant: 'warning',
                        duration: 2600,
                    },
                }));
            } catch {}
            return;
        }
        openInitialRiskModalForTrade(selected[0]);
    };

    const stopRiskPreview = useMemo(() => {
        if (!riskModalTrade) {
            return {
                market: 'STOCKS' as MarketCategory,
                assetClass: 'STOCKS' as StopRiskAssetClass,
                basis: null as ({ side: 'buy' | 'sell'; entryPrice: number; quantity: number } | null),
                multiplier: null as number | null,
                blockedReason: 'Select a trade.',
                result: null as ReturnType<typeof computeInitialRiskFromStop> | null,
            };
        }

        const market = getTradeMarketCategory(riskModalTrade as any);
        const assetClass = mapMarketToStopRiskAssetClass(market);
        const basisResult = deriveOpeningRiskBasis(riskModalTrade);
        if (!basisResult.ok) {
            return {
                market,
                assetClass,
                basis: null,
                multiplier: null,
                blockedReason: basisResult.reason,
                result: null,
            };
        }

        if (assetClass === 'FOREX') {
            return {
                market,
                assetClass,
                basis: basisResult,
                multiplier: null,
                blockedReason: 'Stop Price mode is unavailable for Forex due to unit/model ambiguity. Use Risk Amount ($).',
                result: null,
            };
        }

        if (assetClass === 'CRYPTO') {
            return {
                market,
                assetClass,
                basis: basisResult,
                multiplier: null,
                blockedReason: 'Stop Price mode is unavailable for Crypto in the current risk model. Use Risk Amount ($).',
                result: null,
            };
        }

        if (assetClass === 'OTHER') {
            return {
                market,
                assetClass,
                basis: basisResult,
                multiplier: null,
                blockedReason: 'Stop Price mode is unavailable for this instrument type. Use Risk Amount ($).',
                result: null,
            };
        }

        const parsedStopPrice = Number(stopPriceInput);
        const multiplier = assetClass === 'FUTURES' ? resolveFuturesMultiplierForRisk(riskModalTrade) : null;

        if (assetClass === 'FUTURES' && (multiplier == null || multiplier <= 0)) {
            return {
                market,
                assetClass,
                basis: basisResult,
                multiplier,
                blockedReason: 'Futures contract multiplier is unavailable for this symbol. Use Risk Amount ($).',
                result: null,
            };
        }

        if (!Number.isFinite(parsedStopPrice) || parsedStopPrice <= 0) {
            return {
                market,
                assetClass,
                basis: basisResult,
                multiplier,
                blockedReason: null,
                result: null,
            };
        }

        const result = computeInitialRiskFromStop({
            assetClass,
            side: basisResult.side,
            entryPrice: basisResult.entryPrice,
            quantity: basisResult.quantity,
            stopPrice: parsedStopPrice,
            futuresMultiplier: multiplier,
        });

        return {
            market,
            assetClass,
            basis: basisResult,
            multiplier,
            blockedReason: null,
            result,
        };
    }, [riskModalTrade, stopPriceInput]);

    const parsedRiskAmount = Number(riskAmountInput);
    const canSaveRiskAmount = Number.isFinite(parsedRiskAmount) && parsedRiskAmount > 0;
    const canSaveStopPrice = !!stopRiskPreview.result && stopRiskPreview.result.ok;

    const saveInitialRiskFromModal = () => {
        if (!riskModalTrade) return;
        if (riskModalMethod === 'amount') {
            if (!canSaveRiskAmount) return;
            persistInitialRiskForTrade(riskModalTrade, {
                amount: parsedRiskAmount,
                method: 'amount',
                stopPrice: null,
            });
            setRiskModalTrade(null);
            return;
        }

        if (!stopRiskPreview.result || !stopRiskPreview.result.ok) return;
        const parsedStop = Number(stopPriceInput);
        persistInitialRiskForTrade(riskModalTrade, {
            amount: stopRiskPreview.result.totalRisk,
            method: 'stopPrice',
            stopPrice: Number.isFinite(parsedStop) ? parsedStop : null,
        });
        setRiskModalTrade(null);
    };

    const applyTagToSelected = (tag: string) => {
        const trimmed = tag.trim();
        if (!trimmed) return;
        setTrades((prev) => {
            const next = prev.map((t) => {
                if (!isRawTradeSelected(t)) return t;
                const current = Array.isArray((t as any).tags) ? (t as any).tags : [];
                if (current.includes(trimmed)) return t;
                return { ...t, tags: [...current, trimmed] } as Trade;
            });
            try { saveImportedTrades(next as any[], { source: TRADES_PAGE_TAG_MUTATION_SOURCE }); } catch {}
            try { removePrivateTradingValue('trades-cache'); } catch {}
            return next;
        });
    };

    const removeTagFromSelected = (tag: string) => {
        const trimmed = tag.trim();
        if (!trimmed) return;
        setTrades((prev) => {
            const next = removeTagFromTrades(prev, isRawTradeSelected, trimmed);
            try { saveImportedTrades(next as any[], { source: TRADES_PAGE_TAG_MUTATION_SOURCE }); } catch {}
            try { removePrivateTradingValue('trades-cache'); } catch {}
            return next;
        });
    };

    // Apply/remove tag for a single trade identified by its tradeKey
    const applyTagToTrade = (targetKey: string, tag: string) => {
        const trimmed = tag.trim();
        if (!trimmed) return;
        setTrades(prev => {
            const next = prev.map(t => {
                try {
                    const k = String(t.storageId ?? t.id ?? `${t.symbol}-${t.timestamp}`);
                    if (k !== targetKey) return t;
                    const current = Array.isArray((t as any).tags) ? (t as any).tags : [];
                    if (current.includes(trimmed)) return t;
                    return { ...t, tags: [...current, trimmed] } as Trade;
                } catch { return t; }
            });
            try { saveImportedTrades(next as any[], { source: TRADES_PAGE_TAG_MUTATION_SOURCE }); } catch {}
            try { removePrivateTradingValue('trades-cache'); } catch {}
            return next;
        });
    };

    const removeTagFromTrade = (targetKey: string, tag: string) => {
        const trimmed = tag.trim();
        if (!trimmed) return;
        setTrades(prev => {
            const next = removeTagFromTrades(prev, (trade) => {
                try { return String(trade.storageId ?? trade.id ?? `${trade.symbol}-${trade.timestamp}`) === targetKey; }
                catch { return false; }
            }, trimmed);
            try { saveImportedTrades(next as any[], { source: TRADES_PAGE_TAG_MUTATION_SOURCE }); } catch {}
            try { removePrivateTradingValue('trades-cache'); } catch {}
            return next;
        });
    };

    const renameTagForTrade = (targetKey: string, originalTag: string, nextTag: string) => {
        const replacement = nextTag.trim();
        if (!replacement) return;
        setTrades(prev => {
            const next = prev.map(t => {
                const k = String(t.storageId ?? t.id ?? `${t.symbol}-${t.timestamp}`);
                if (k !== targetKey) return t;
                return { ...t, tags: renameTradeTag((t as any).tags, originalTag, replacement) } as Trade;
            });
            try { saveImportedTrades(next as any[], { source: TRADES_PAGE_TAG_MUTATION_SOURCE }); } catch {}
            try { removePrivateTradingValue('trades-cache'); } catch {}
            return next;
        });
        setEditingTag(null);
    };

    // Other actions implementations

    /* removed unused setSharedForSelected to avoid declared-but-unused warning */

    const mergeSelectedTrades = () => {
        const selected = trades.filter(t => isRawTradeSelected(t));
        if (selected.length < 2) return;
        const base = selected[0];
        const others = selected.slice(1);
        const merged: Trade = { ...base } as Trade;
        for (const o of others) {
            merged.volume = (merged.volume || 0) + (o.volume || 0);
            merged.executions = (merged.executions || 0) + (o.executions || 0);
            merged.pnl = (Number(merged.pnl) || 0) + (Number(o.pnl) || 0);
            merged.tags = Array.from(new Set([...(merged.tags || []), ...(o.tags || [])]));
            merged.notes = [merged.notes || '', o.notes || ''].filter(Boolean).join('\n');
        }
        setTrades(prev => {
            const remaining = prev.filter(t => !isRawTradeSelected(t));
            return [merged, ...remaining];
        });
        setSelectedTrades(new Set());
    };

    // splitSelectedTrades removed (unused)

    // Manual split: apply explicit allocations (allocations array sums to total selected shares)
    const applyManualSplit = (allocations: number[]) => {
        setTrades(prev => {
            const next: Trade[] = [];
            const selected = prev.filter(t => isRawTradeSelected(t));
            const totalSelectedShares = selected.reduce((s, t) => s + (Number((t as any).volume) || 0), 0) || 0;

            for (const t of prev) {
                if (!isRawTradeSelected(t)) {
                    next.push(t);
                    continue;
                }

                const vol = Math.max(0, Math.floor(Number(t.volume) || 0));
                const pnl = Number(t.pnl) || 0;

                if (vol === 0 || allocations.length === 0 || totalSelectedShares === 0) {
                    next.push(t);
                    continue;
                }

                const piecesForTrade: Trade[] = [];
                let acc = 0;
                for (let i = 0; i < allocations.length; i++) {
                    const alloc = Math.max(0, Math.floor(allocations[i] || 0));
                    // proportional share of this allocation for this trade
                    const pieceVol = Math.floor((alloc * vol) / totalSelectedShares);
                    acc += pieceVol;
                    const piecePnl = vol > 0 ? +(pnl * (pieceVol / vol)).toFixed(2) : 0;
                    const id = hashStringToInt(`${t.id}-${i}-${Date.now()}`);
                    piecesForTrade.push({ ...t, id, volume: pieceVol, pnl: piecePnl } as Trade);
                }

                // distribute any leftover due to rounding into the first piece
                const remainder = vol - acc;
                if (remainder > 0 && piecesForTrade.length > 0) {
                    piecesForTrade[0] = { ...piecesForTrade[0], volume: piecesForTrade[0].volume + remainder } as Trade;
                    // recompute PnL for first piece
                    piecesForTrade[0] = { ...piecesForTrade[0], pnl: vol > 0 ? +(pnl * (piecesForTrade[0].volume / vol)).toFixed(2) : piecesForTrade[0].pnl } as Trade;
                }

                // If allocations produce zero-volume pieces for tiny trades, filter them out
                for (const p of piecesForTrade) {
                    if ((p as any).volume > 0) next.push(p);
                }
            }

            return next;
        });
        setSelectedTrades(new Set());
    };

    // Totals per asset class (Stocks, Futures, Forex, Crypto) — used in footer breakdown
    const totalsByMarket = useMemo(() => {
        const map: Record<string, number> = { STOCKS: 0, FUTURES: 0, FOREX: 0, CRYPTO: 0, OTHER: 0 };
        for (const t of groupedTrades) {
            try {
                if (isOpenLikeTradeForRender(t)) continue;
                const cat = detectMarketCategory({
                    symbol: (t as any).symbol,
                    market: (t as any).market,
                    marketType: (t as any).marketType,
                    assetClass: (t as any).assetClass,
                    category: (t as any).category,
                    instrumentType: (t as any).instrumentType,
                    instrument: (t as any).instrument,
                    securityType: (t as any).securityType,
                }) || 'STOCKS';
                const v = getTradeDisplayPnl(t);
                map[cat] = (map[cat] || 0) + v;
            } catch {}
        }
        return map;
    }, [groupedTrades, activeView]);

    // Reference `totalsByMarket` so linters/compilers don't mark it unused — keep for footer breakdown.
    void totalsByMarket;

    // Keep per-market totals in `totalsByMarket` for the footer breakdown.

    // Total for the FULL filtered dataset (all pages), not just the current page.
    // The canonical display filter drops invalid/rejected import rows so the
    // total is computed over the SAME canonical trade set as the Dashboard.
    // Each trade's P&L comes from the same canonical resolver (getTradePnl) as the
    // Dashboard, so the Trades page total and the Dashboard total can never
    // disagree for the same closed trades in the same window.
    const displayedTotal = useMemo(() => {
        try {
            const canonical = filterCanonicalDisplayTrades(filteredTrades as any[]);
            return canonical.reduce((sum, t) => {
                if (isOpenLikeTradeForRender(t)) return sum;
                const v = getTradeDisplayPnl(t as Trade);
                return sum + (Number.isFinite(Number(v)) ? Number(v) : 0);
            }, 0);
        } catch {
            return 0;
        }
    }, [filteredTrades, activeView]);

    // Split modal calculation (uses total shares across selected trades)
    const totalSelectedShares = groupedTrades.filter(t => selectedTrades.has(tradeKey(t))).reduce((s, t) => s + (Number((t as any).volume) || 0), 0);

    // --- Manual Split Modal Component ---
    function SplitTradesModal({
        onClose,
        onConfirm,
        totalShares = 450,
        selectedTradesCount = 1,
    }: {
        onClose: () => void;
        onConfirm: (result: { allocations: number[] }) => void;
        totalShares?: number;
        selectedTradesCount?: number;
    }) {
        // keep as string so user can clear / edit freely
        const [tradeCountInput, setTradeCountInput] = useState<string>('2');

        const tradeCount = Number(tradeCountInput);
        const validTradeCount = Number.isInteger(tradeCount) && tradeCount >= 2;

        // allocations start empty
        const [allocations, setAllocations] = useState<Array<number | ''>>(['', '']);

        // rebuild allocations ONLY when trade count is valid
        useEffect(() => {
            if (!validTradeCount) return;
            setAllocations(Array.from({ length: tradeCount }, () => ''));
        }, [tradeCount, validTradeCount]);

        const { remaining } = useMemo(() => {
            const used = allocations.reduce<number>((sum, v) => sum + (typeof v === 'number' ? v : 0), 0);
            const rem = totalShares - used;
            return { used, remaining: rem };
        }, [allocations, totalShares]);

        const allFilled = allocations.every((v) => v !== '');
        const isValid = validTradeCount && remaining === 0 && allFilled;

        return (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="w-[460px] rounded-xl border border-zinc-200 bg-white p-6 text-zinc-900 shadow-2xl dark:border-neutral-800 dark:bg-neutral-900 dark:text-white space-y-5">
                    {/* Header */}
                    <div className="flex justify-between items-start">
                        <div className="space-y-1">
                            <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Split Trade</h2>
                            <p className="text-sm text-zinc-500 dark:text-neutral-400 mt-0.5">Split {selectedTradesCount > 1 ? 'selected trades' : 'this trade'} into custom-sized parts.</p>
                        </div>
                        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 dark:text-neutral-500 dark:hover:text-white" aria-label="Close">
                            <X size={20} />
                        </button>
                    </div>

                    {/* Trade count */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-zinc-600 dark:text-neutral-300 -mt-1">Number of trades</label>
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={tradeCountInput}
                            onChange={(e) => setTradeCountInput(e.target.value)}
                            className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 text-zinc-900 appearance-none mt-1 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 dark:border-neutral-700 dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-500"
                            placeholder="2"
                        />
                        {!validTradeCount && <div className="text-xs text-amber-500 dark:text-amber-400">Enter a number ≥ 2</div>}
                    </div>

                    {/* Allocation list */}
                    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 space-y-3 dark:border-neutral-800/50 dark:bg-neutral-950/50">
                        {allocations.map((qty, idx) => (
                            <div key={idx} className="flex items-center justify-between gap-3">
                                <span className="text-sm text-zinc-500 dark:text-neutral-400">Trade {idx + 1}</span>

                                <div className="relative">
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        aria-label={`Shares for trade ${idx + 1}`}
                                        value={qty === '' ? '' : qty}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            const next = [...allocations];

                                            if (raw === '') {
                                                next[idx] = '';
                                                setAllocations(next as any);
                                                return;
                                            }

                                            const parsed = Number(raw);
                                            if (!Number.isFinite(parsed)) return;

                                            // clamp so user cannot exceed totalShares
                                            const usedWithoutCurrent = allocations.reduce<number>((sum, v, i) => {
                                                if (i === idx) return sum;
                                                return sum + (typeof v === 'number' ? v : 0);
                                            }, 0);
                                            const maxForThisField = Math.max(0, totalShares - usedWithoutCurrent);
                                            const clamped = Math.min(Math.max(0, parsed), maxForThisField);
                                            next[idx] = clamped;
                                            setAllocations(next as any);
                                        }}
                                        className="w-28 rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-right pr-14 text-sm text-zinc-900 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 dark:border-neutral-700 dark:bg-neutral-800 dark:text-white"
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-500 pointer-events-none dark:text-neutral-500">shares</span>
                                </div>
                            </div>
                        ))}

                        <div className="pt-2 border-t border-zinc-200 text-sm flex justify-between dark:border-neutral-800">
                            <span className="text-zinc-500 dark:text-neutral-400">Shares remaining</span>
                            <span className={remaining === 0 ? 'text-green-600 dark:text-green-400' : remaining < 0 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}>{remaining}</span>
                        </div>
                    </div>

                    {/* Footer info */}
                    <div className="text-xs text-zinc-500 flex items-start gap-2 rounded bg-zinc-50 p-3 dark:bg-neutral-800/30 dark:text-neutral-500">
                        <AlertCircle className="w-4 h-4 text-zinc-400 shrink-0 dark:text-neutral-400" />
                        <p>Total quantity ({totalShares}) must be fully distributed. This action cannot be undone.</p>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-4 py-2 rounded-lg bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700">Cancel</button>
                        <button
                            disabled={!isValid}
                            onClick={() => {
                                const normalized = allocations.map((v) => (v === '' ? 0 : (v as number))) as number[];
                                onConfirm({ allocations: normalized });
                            }}
                            className={`px-6 py-2 rounded-lg flex items-center gap-2 text-sm font-medium ${isValid ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-zinc-200 text-zinc-500 cursor-not-allowed dark:bg-neutral-800 dark:text-neutral-500'}`}
                        >
                            <span>Split Trade</span>
                            {isValid && <ArrowRight size={14} />}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // load saved layout (if any)
    useEffect(() => {
        try {
            const raw = localStorage.getItem(LAYOUT_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed) {
                    if (parsed.colWidths) {
                        const next = {} as Partial<Record<ColKey, number>>;
                        for (const key of Object.keys(parsed.colWidths as Record<string, unknown>)) {
                            const mapped = migrateSettingKeyToColKey(key);
                            if (!mapped) continue;
                            next[mapped] = normalizeColumnWidthValue(mapped, (parsed.colWidths as Record<string, unknown>)[key]);
                        }
                        setManualColWidthOverrides(next);
                    }
                    // The table is constrained to container width (no horizontal scrolling), so ignore persisted tableWidthPx.
                    if (parsed.pageSize) setPageSize(Number(parsed.pageSize));
                }
            }
        } catch {
        } finally {
            localLayoutHydratedRef.current = true;
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const saved = await fetchTradesTableLayoutPreference();
                if (cancelled || !saved) return;

                if (saved.colWidths) {
                    const next = {} as Partial<Record<ColKey, number>>;
                    for (const key of Object.keys(saved.colWidths as Record<string, unknown>)) {
                        const mapped = migrateSettingKeyToColKey(key);
                        if (!mapped) continue;
                        next[mapped] = normalizeColumnWidthValue(mapped, (saved.colWidths as Record<string, unknown>)[key]);
                    }
                    setManualColWidthOverrides(next);
                }

                if (saved.pageSize) setPageSize(Number(saved.pageSize));

                try {
                    const raw = localStorage.getItem(LAYOUT_KEY);
                    const parsed = raw ? JSON.parse(raw) : {};
                    localStorage.setItem(LAYOUT_KEY, JSON.stringify({
                        ...(parsed && typeof parsed === 'object' ? parsed : {}),
                        pageSize: saved.pageSize ?? pageSize,
                        colWidths: saved.colWidths ?? {},
                    }));
                } catch {}
            } catch {
                // Local layout remains the fallback when the shared preference cannot be reached.
            } finally {
                if (!cancelled) remoteLayoutHydratedRef.current = true;
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!localLayoutHydratedRef.current) return;
        if (!layoutPersistenceReadyRef.current) {
            layoutPersistenceReadyRef.current = true;
            return;
        }
        try {
            const raw = localStorage.getItem(LAYOUT_KEY);
            const parsed = raw ? JSON.parse(raw) : {};
            localStorage.setItem(LAYOUT_KEY, JSON.stringify({
                ...(parsed && typeof parsed === 'object' ? parsed : {}),
                pageSize,
                colWidths: manualColWidthOverrides,
            }));
        } catch {}

        if (!remoteLayoutHydratedRef.current) return;
        if (remoteLayoutSaveTimerRef.current != null) {
            window.clearTimeout(remoteLayoutSaveTimerRef.current);
        }
        remoteLayoutSaveTimerRef.current = window.setTimeout(() => {
            remoteLayoutSaveTimerRef.current = null;
            void saveTradesTableLayoutPreference({
                pageSize,
                colWidths: manualColWidthOverrides as Record<string, number>,
            });
        }, 350);
    }, [manualColWidthOverrides, pageSize]);

    useEffect(() => {
        return () => {
            if (remoteLayoutSaveTimerRef.current != null) {
                window.clearTimeout(remoteLayoutSaveTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (resizingRef.current.key) return;
        const plan = buildWidthPlan(responsiveVisibleCols, manualColWidthOverrides, availableTableWidthPx);
        setColWidths(plan.widths);
        setTableWidthPx(plan.visibleKeys.length ? Math.max(200, plan.total) : null);
    }, [responsiveVisibleCols, manualColWidthOverrides, colOrder, availableTableWidthPx]);

    // When explicit px widths are present, keep table width in sync with visible columns.
    useEffect(() => {
        const visibleKeys = orderedVisibleKeys;
        if (!visibleKeys.length) return;

        const hasAnyPx = visibleKeys.some((k) => typeof colWidths[k] === 'number');
        if (!hasAnyPx) {
            setTableWidthPx(null);
            return;
        }

        const total = visibleKeys.reduce((sum, k) => {
            const clamped = normalizeColumnWidthValue(k, colWidths[k] ?? getColumnMinWidth(k));
            return sum + clamped;
        }, 0);
        setTableWidthPx(Math.max(200, total));
    }, [colWidths, orderedVisibleKeys]);

    // persist trades to localStorage whenever they change so deletions are permanent
    useEffect(() => {
        if (demoTradesActive || !hasHydratedTradesRef.current) return;
        if (!hasHydratedTradesRef.current) return;
        if (skipFirstPersistRef.current) {
            skipFirstPersistRef.current = false;
            return;
        }
        try {
            // The authoritative imported/custom stores own persistence. The old
            // unscoped tt_trades cache is intentionally no longer written.
        } catch {}
    }, [demoTradesActive, trades]);

    return (
        <div className="space-y-3 -mt-0 pb-6" data-tour-target="trades-page">
            {/* Main file input pulled out to the root so it isn't unmounted when trades exist */}
            <input ref={fileInputRef} type="file" accept=".csv" aria-label="Import trades from CSV" title="Import trades from CSV" className="hidden" onChange={handleFileSelected} />

            {showCsvAccountPicker && (
                <div className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="trades-csv-account-title">
                    <div className="w-full max-w-lg rounded-2xl border border-zinc-700 bg-zinc-900 p-6 text-white shadow-2xl">
                        <div className="mb-4 flex items-start justify-between gap-3">
                            <div>
                                <h2 id="trades-csv-account-title" className="text-lg font-semibold">Choose the CSV broker account</h2>
                                <p className="mt-1 text-sm text-zinc-400">Trades are saved to this account on the server.</p>
                            </div>
                            <button type="button" aria-label="Close broker account selection" className="rounded-lg p-1 text-zinc-400 hover:bg-zinc-800 hover:text-white" onClick={() => setShowCsvAccountPicker(false)}><X className="h-5 w-5" /></button>
                        </div>
                        <label className="mb-1 block text-sm font-medium" htmlFor="trades-csv-broker">Broker</label>
                        <select id="trades-csv-broker" aria-label="CSV broker" value={csvSourceBroker} onChange={(event) => { setCsvSourceBroker(event.target.value); setCsvSourceAccountId(''); setCsvSourceAccountName(''); setCsvAccountsError(''); }} className="mb-4 h-11 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 text-sm">
                            <option value="">Select a supported broker</option>
                            {brokerOptions.filter((option) => option.id !== 'csv_generic' && option.id !== 'other').map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}
                        </select>
                        <label className="mb-1 block text-sm font-medium" htmlFor="trades-csv-account">Account</label>
                        <select id="trades-csv-account" aria-label="CSV broker account" value={csvSourceAccountId} onChange={(event) => { setCsvSourceAccountId(event.target.value); setCsvSourceAccountName(''); setCsvAccountsError(''); }} disabled={!csvSourceBroker || csvAccountsLoading} className="h-11 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 text-sm disabled:opacity-60">
                            <option value="">{csvAccountsLoading ? 'Loading saved accounts…' : 'Select an account action'}</option>
                            {csvSourceAccounts.filter((account) => account.sourceBroker === csvSourceBroker).map((account) => <option key={account.id} value={account.id}>{account.accountName}</option>)}
                            <option value="__create__">Create a new account</option>
                        </select>
                        {csvSourceAccountId === '__create__' && (
                            <div className="mt-3">
                                <label className="mb-1 block text-sm font-medium" htmlFor="trades-csv-account-name">New account name</label>
                                <input id="trades-csv-account-name" aria-label="New broker account name" value={csvSourceAccountName} onChange={(event) => { setCsvSourceAccountName(event.target.value); setCsvAccountsError(''); }} maxLength={120} className="h-11 w-full rounded-lg border border-zinc-700 bg-zinc-950 px-3 text-sm" placeholder="e.g. Primary account" />
                            </div>
                        )}
                        {csvAccountsLoading && <p className="mt-2 text-xs text-zinc-400">Loading your saved accounts…</p>}
                        {csvAccountsError && <p role="alert" className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{csvAccountsError}</p>}
                        <div className="mt-6 flex justify-end gap-2">
                            <button type="button" className="rounded-lg border border-zinc-700 px-4 py-2 text-sm hover:bg-zinc-800" onClick={() => setShowCsvAccountPicker(false)}>Cancel</button>
                            <button type="button" disabled={csvAccountsLoading || Boolean(csvAccountsError)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50" onClick={continueCsvImport}>Choose CSV</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="relative">
                <StandardPageHeader title="Trades" subtitle="Manage and analyze your trading history." showDivider={false} />
                <div className="relative z-50 mt-3 flex flex-wrap items-center justify-end gap-3 lg:absolute lg:right-0 lg:top-4 lg:mt-0">
                {shouldShowTradingAccountSelector(manualTradeAccounts, manualTradeAccountsLoading) && <div className="relative flex w-full justify-center sm:w-auto">
                    <div className="w-[min(260px,calc(100vw-2rem))]">
                        <Listbox
                            value={selectedTradingAccountId}
                            onChange={(accountId: string) => {
                                setSelectedTradingAccountId(accountId);
                                writePrivateTradingValue('trades-selected-account', accountId);
                                setSelectedTrades(new Set());
                                setPage(1);
                            }}
                        >
                            <div className="relative">
                                <Listbox.Button
                                    aria-label="Trading Account"
                                    className="group flex h-10 w-full items-center gap-2 rounded-xl border border-slate-300 bg-white px-2.5 text-left shadow-sm transition-all hover:border-blue-400/50 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/30 dark:border-slate-800 dark:bg-[#050a14] dark:hover:border-blue-400/40"
                                >
                                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-blue-400/30 bg-blue-400/10 text-blue-400 dark:text-blue-400">
                                        <Wallet className="h-3 w-3" />
                                    </span>
                                    <span className={`min-w-0 flex-1 truncate text-xs ${selectedTradingAccountId ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}>
                                        {manualTradeAccounts.find((account) => account.id === selectedTradingAccountId)?.accountName || 'Select a trading account'}
                                    </span>
                                    <ChevronDown className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-slate-500" />
                                </Listbox.Button>
                                <Listbox.Options className="scrollable absolute z-30 mt-1.5 max-h-64 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-2xl ring-1 ring-black/5 focus:outline-none dark:border-slate-700/80 dark:bg-[#0b1322] dark:shadow-black/50 dark:ring-white/5">
                                    <Listbox.Option value="" className={({ active, selected }) => `mb-1 flex cursor-pointer items-center gap-2 rounded-lg border px-2 py-2 text-left text-xs transition-colors ${selected ? 'border-blue-400/50 bg-blue-400/10 font-semibold text-blue-400 dark:text-blue-400' : 'border-transparent text-slate-600 dark:text-slate-300'} ${active ? 'bg-slate-100 dark:bg-white/5' : ''}`}>
                                        {({ selected }) => <>
                                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-blue-400/30 bg-blue-400/10 text-blue-400 dark:text-blue-400"><Wallet className="h-3 w-3" /></span>
                                            <span className="min-w-0 flex-1">All Trading Accounts</span>
                                            {selected && <Check className="h-3.5 w-3.5 shrink-0 text-blue-400" />}
                                        </>}
                                    </Listbox.Option>
                                    {manualTradeAccounts.map((account) => (
                                        <Listbox.Option key={account.id} value={account.id} className={({ active, selected }) => `mb-1 flex cursor-pointer items-center gap-2 rounded-lg border px-2 py-2 text-left transition-colors ${selected ? 'border-blue-400/50 bg-blue-400/10' : 'border-transparent'} ${active ? 'bg-slate-100 dark:bg-white/5' : ''}`}>
                                            {({ selected }) => <>
                                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-blue-400/30 bg-blue-400/10 text-blue-400 dark:text-blue-400"><Wallet className="h-3 w-3" /></span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate text-xs font-semibold text-slate-900 dark:text-white">{account.accountName}</span>
                                                    <span className="block truncate text-[10px] text-slate-500 dark:text-slate-400">{account.sourceBroker.replace(/_/g, ' ')}</span>
                                                </span>
                                                {selected && <Check className="h-3.5 w-3.5 shrink-0 text-blue-400" />}
                                            </>}
                                        </Listbox.Option>
                                    ))}
                                    {manualTradeAccountsLoading && <div className="px-2 py-2 text-[11px] text-slate-500 dark:text-slate-400">Loading saved accounts…</div>}
                                    {!manualTradeAccountsLoading && manualTradeAccountsError && <div role="status" className="px-2 py-2 text-[11px] text-rose-600 dark:text-rose-300">Saved accounts could not be loaded.</div>}
                                    {!manualTradeAccountsLoading && !manualTradeAccountsError && manualTradeAccounts.length === 0 && <div className="px-2 py-2 text-[11px] text-slate-500 dark:text-slate-400">No saved accounts available.</div>}
                                </Listbox.Options>
                            </div>
                        </Listbox>
                    </div>
                </div>}
                <div className="flex flex-wrap items-center justify-end gap-3">
                    <button
                        type="button"
                        className="inline-flex h-[39px] items-center gap-2 border-0 px-4 rounded-xl bg-blue-600 text-sm text-white hover:bg-blue-700"
                        onClick={() => setCreateOpen(true)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                        <span>Manual Entry</span>
                    </button>

                                        {showRejectedTradesButton && (
                                            <button
                                                type="button"
                                                title="View rejected import rows & download diagnostics (one-time)"
                                                aria-label="View rejected trades"
                                                className="inline-flex items-center gap-2 px-3 h-9 rounded-xl bg-red-600 text-sm text-white hover:bg-red-700"
                                                onClick={() => {
                                                    try {
                                                        sessionStorage.setItem('tt_rejected_inspected', '1');
                                                    } catch {}
                                                    try {
                                                        const nav = (window as any).__TT_navigate;
                                                        if (typeof nav === 'function') nav('Import Rejections');
                                                        else window.location.href = '/import/rejections';
                                                    } catch {
                                                        try { window.location.href = '/import/rejections'; } catch {}
                                                    }
                                                    setShowRejectedTradesButton(false);
                                                }}
                                            >
                                                <XCircle size={14} />
                                                <span>Rejected Trades</span>
                                            </button>
                                        )}

                    <button
                        type="button"
                        className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-600 shadow-sm transition-colors hover:border-rose-900/50 hover:bg-rose-950/20 hover:text-rose-400 focus-visible:ring-2 focus-visible:ring-rose-500/40 dark:border-slate-800 dark:bg-[#0b1220] dark:text-slate-300 dark:hover:border-rose-900/50 dark:hover:bg-rose-950/20 dark:hover:text-rose-400"
                        onClick={() => setShowClearModal(true)}
                    >
                        <Trash2 size={14} />
                        <span>Clear Imported Trades</span>
                    </button>

                    <CreateCustomEntryModal
                        open={createOpen}
                        onClose={() => setCreateOpen(false)}
                        onCreate={handleCreateManualTrade}
                        accounts={manualTradeAccounts.map((account) => ({ id: account.id, accountName: account.accountName }))}
                        accountsLoading={manualTradeAccountsLoading}
                        accountsError={manualTradeAccountsError}
                        requireAccount
                        timeZone={displayTimeZone}
                    />
                </div>
                </div>
                <div className="mb-6 mt-2 h-px w-full bg-zinc-600/60 dark:bg-zinc-500/30" />
            </div>

            {/* Import success banner (transient) - placed below the divider line */}
            {importResult && (
                <div className={`w-full flex flex-col items-center transition-opacity duration-500 ${importBannerFading ? 'opacity-0' : 'opacity-100'}`}>
                  <div
                      className={`inline-flex items-center gap-3 rounded-xl px-4 py-2 shadow-sm mt-2 border ${
                          importResult.success
                              ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-900/50'
                              : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-900/50'
                      }`}
                  >
                      {importResult.success ? (
                          <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      ) : (
                          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0" />
                      )}

                      <p className={`${importResult.success ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'} whitespace-nowrap`}>
                          {(() => {
                              const content = buildImportBannerContent(importResult);
                              return (
                                  <>
                                      {content.title && <><span className={`font-semibold ${importResult.success ? 'text-emerald-900 dark:text-emerald-200' : 'text-red-900 dark:text-red-200'}`}>{content.title}</span>{' '}</>}
                                      {content.body}
                                  </>
                              );
                          })()}
                      </p>

                      {pendingCsvVerification && <button
                          type="button"
                          disabled={retryingCsvVerification}
                          className="shrink-0 rounded-md border border-red-300 px-2.5 py-1 text-xs font-medium text-red-800 hover:bg-red-100 disabled:opacity-60 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-900/30"
                          onClick={async () => {
                              setRetryingCsvVerification(true);
                              try {
                                  const verified = await retryCsvImportVerification(pendingCsvVerification.remoteResult);
                                  if (!verified) {
                                      showImportBanner({ success: false, message: 'The server has not confirmed this import yet. Retry verification; do not import the CSV again.' });
                                      return;
                                  }
                                  const isExactDuplicate = String((pendingCsvVerification.remoteResult as any)?.status || '').trim().toLowerCase() === 'duplicate_exact_file';
                                  if (!isExactDuplicate) {
                                      try { await pendingCsvVerification.saveLocal(); } catch {}
                                  }
                                  setPendingCsvVerification(null);
                                  setTrades(await loadTrades());
                                  try { removePrivateTradingValue('trades-cache'); } catch {}
                                  showImportBanner({ success: true, message: 'The server confirmed the saved trades and the table has been refreshed.' });
                              } catch {
                                  showImportBanner({ success: false, message: 'Verification could not complete. Retry verification when ready; the CSV will not be submitted again.' });
                              } finally {
                                  setRetryingCsvVerification(false);
                              }
                          }}
                      >
                          {retryingCsvVerification ? 'Checking…' : 'Retry verification'}
                      </button>}

                      <button
                          type="button"
                          aria-label="Dismiss"
                          className={`ml-2 shrink-0 ${importResult.success ? 'text-emerald-700 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-200' : 'text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-200'}`}
                          onClick={() => {
                              if (pendingCsvVerification) return;
                              setImportBannerFading(false);
                              setImportResult(null);
                          }}
                      >
                          <X className="w-5 h-5" />
                      </button>
                  </div>
                </div>
            )}

            {/* Clear imported cache modal */}
            <ClearImportedTradesDialog
                open={showClearModal}
                onClose={() => setShowClearModal(false)}
                accountScopedOnly
                onCleared={(account) => {
                    if (!account) return;
                    const belongsToClearedAccount = (trade: any) => isImportedTradeForAccount(trade, {
                        sourceBroker: account.sourceBroker,
                        sourceAccountRef: account.sourceAccountRef || '',
                        sourceAccountId: account.id,
                    });
                    lastKnownTradesSnapshot = lastKnownTradesSnapshot.filter((trade: any) => !belongsToClearedAccount(trade));
                    setTrades((current) => current.filter((trade: any) => !belongsToClearedAccount(trade)));
                    setSelectedTrades(new Set());
                    setPage(1);
                }}
            />
            {importing && (
                <div className="w-full mt-2 px-4">
                    <div className="w-full bg-zinc-200 h-2 rounded overflow-hidden">
                        <div className="bg-blue-600 h-2 transition-all" style={{ width: `${progress}%` }} />
                    </div>
                </div>
            )}

            <div className="mt-2">
                <TopFilters
                    marketFilter={marketFilter}
                    onChangeMarket={setMarketFilter}
                    initialTimeframe={liveFilters.timeframe}
                    initialFromDate={liveFilters.fromDate}
                    initialToDate={liveFilters.toDate}
                    initialSideFilter={liveFilters.sideFilter}
                    initialSearchTerm={liveFilters.searchTerm}
                    initialAdvancedFilters={liveFilters.advancedFilters}
                    showApplyButton={false}
                    onChange={handleTopFiltersChange}
                />
            </div>

                        <ActiveFilterChips
              liveFilters={liveFilters}
              marketFilter={marketFilter}
              onClearSearch={() => setLiveFilters((p) => ({ ...p, searchTerm: '' }))}
              onClearMarket={() => setMarketFilter('ALL')}
              onClearSide={() => setLiveFilters((p) => ({ ...p, sideFilter: 'All' }))}
              onClearTimeframe={() => setLiveFilters((p) => ({ ...p, timeframe: 'alltime', fromDate: undefined, toDate: undefined }))}
              onClearAll={() => { setLiveFilters(buildDefaultTradesPageFilters()); setMarketFilter('ALL'); }}
            />

            <div className="sticky top-0 z-30 mt-2">
                <div className="h-[55px] rounded-2xl border border-zinc-300/90 bg-zinc-50 dark:border-zinc-700/90 dark:bg-zinc-900/95 backdrop-blur">
                    <div className="relative flex h-full items-center justify-between px-2">
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1 rounded-lg border border-border bg-zinc-50/80 p-1 shadow-[inset_0_2px_5px_rgba(15,23,42,0.14)] dark:border-zinc-700/80 dark:bg-zinc-800/90 dark:shadow-[inset_0_2px_6px_rgba(0,0,0,0.55)]">
                                    {['Gross', 'Net'].map((t) => (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => setActiveView(t as any)}
                                            className={
                                                activeView === t
                                                    ? 'compact-segment-button px-3 h-8 rounded-md bg-blue-600 text-white text-sm shadow-[0_10px_24px_-16px_rgba(37,99,235,0.95)]'
                                                    : 'compact-segment-button px-3 h-8 text-sm text-zinc-600 hover:bg-white dark:text-zinc-300 dark:hover:bg-zinc-700/90 dark:hover:text-white'
                                            }
                                        >
                                            {t}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {activeView === 'Net' && (
                                <div className="inline-flex items-center gap-2 px-3 h-8 rounded-md border border-amber-200 bg-amber-50 text-amber-700 text-sm dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                                    <Info className="w-4 h-4" />
                                    <span>Net P&amp;L (after fees)</span>
                                </div>
                            )}
                            <div className="ml-4 flex items-center gap-2">
                                <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input aria-label="Select all trades" type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="h-4 w-4 rounded border-gray-300 dark:border-zinc-600 text-blue-600 focus:ring-blue-500 bg-transparent dark:bg-zinc-700" />
                            <span className="text-sm text-zinc-700 dark:text-zinc-300">Select All</span>
                        </label>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="relative z-[100000] inline-flex items-center gap-2 pr-2 border-r border-zinc-200 dark:border-zinc-700">
                                <span className="text-sm text-zinc-700 dark:text-zinc-300">Time:</span>
                                <div className="relative z-[100000]">
                                    <button
                                        ref={timeButtonRef}
                                        type="button"
                                        aria-haspopup="listbox"
                                        aria-expanded={timeMenuOpen}
                                        onClick={() => setTimeMenuOpen((open) => !open)}
                                        className="inline-flex h-8 items-center gap-2 rounded-md border border-zinc-200 bg-white pl-2 pr-3 text-sm text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/90 dark:text-zinc-200 dark:hover:bg-zinc-800"
                                        title="Trades time display mode"
                                    >
                                        <span className="truncate">
                                            {tradesTimeMode === 'local' ? 'Local' : tradesTimeMode === 'market' ? 'Market (NY)' : 'UTC'}
                                        </span>
                                        <ChevronDown size={16} className={`ml-2 text-zinc-500 transition-transform ${timeMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {timeMenuOpen && timeMenuPosition && createPortal(
                                        <div ref={timeMenuRef} role="listbox" aria-label="Trade time display mode" style={{ top: timeMenuPosition.top, left: timeMenuPosition.left }} className="fixed z-[2147483647] w-[130px] rounded-md border border-zinc-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
                                            {([
                                                ['local', 'Local'],
                                                ['market', 'Market (NY)'],
                                                ['utc', 'UTC'],
                                            ] as const).map(([mode, label]) => (
                                                <button
                                                    key={mode}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={tradesTimeMode === mode}
                                                    onClick={() => { setTradesTimeMode(mode); setTimeMenuOpen(false); }}
                                                    className="w-full px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800"
                                                >
                                                    {label}
                                                </button>
                                            ))}
                                        </div>,
                                        document.body,
                                    )}
                                </div>
                            </div>
                            <div className="relative">
                                <button
                                    ref={settingsButtonRef}
                                    type="button"
                                    onClick={() => {
                                        setSettingsOpen(s => {
                                            const next = !s;
                                            if (next) {
                                                try {
                                                    const presetRaw = localStorage.getItem(PRESET_KEY);
                                                    setPendingPresetIntent(
                                                        presetRaw === 'Default' || presetRaw === 'Advanced' || presetRaw === 'Futures-Focused'
                                                            ? presetRaw
                                                            : null,
                                                    );
                                                    const raw = localStorage.getItem(SETTINGS_KEY);
                                                    if (raw) {
                                                        const parsed = normalizeStoredSettings(JSON.parse(raw));
                                                        setPendingSettings(parsed);
                                                    } else {
                                                        setPendingSettings(computeSettingsDefaults());
                                                    }
                                                } catch {
                                                    setPendingPresetIntent(null);
                                                    setPendingSettings(computeSettingsDefaults());
                                                }
                                            } else {
                                                setPendingPresetIntent(null);
                                                setPendingSettings(null);
                                            }
                                            return next;
                                        });
                                    }}
                                    title="Settings"
                                    className={`px-3 h-8 py-0 rounded-md text-sm flex items-center gap-2 ${settingsOpen ? 'bg-zinc-100 dark:bg-zinc-800/90' : 'hover:bg-zinc-100 dark:bg-zinc-800/90 dark:hover:bg-zinc-700/90'} border border-zinc-200 dark:border-zinc-700 pl-3`}
                                >
                                    <Settings className="w-4 h-4" />
                                    <span className="leading-none">Settings</span>
                                </button>
                            </div>
                        </div>
                        {settingsOpen && typeof document !== 'undefined' ?
                            createPortal(
                                <TableColumnsManager
                                    savedColumns={pendingSettings}
                                    initialPreset={pendingPresetIntent}
                                    assetClassVisibility={assetVisibility}
                                    validateSelection={validateSettingsSelectionForFit}
                                    autoHideSelection={autoHideSettingsSelection}
                                    onCancel={() => { setSettingsOpen(false); setPendingSettings(null); setPendingPresetIntent(null); }}
                                    onSave={(sel, presetIntent) => {
                                        try {
                                            const normalized = normalizeStoredSettings(sel);
                                            const next = settingsSelectionToVisibleCols(normalized);
                                            const plan = buildWidthPlan(next, manualColWidthOverrides, availableTableWidthPx);
                                            if (!plan.fits) {
                                                return false;
                                            }
                                            const serial = JSON.stringify(normalized);
                                            try { localStorage.setItem(SETTINGS_KEY, serial); } catch {}

                                            try {
                                                if (presetIntent) {
                                                    try { localStorage.setItem(PRESET_KEY, presetIntent); } catch {}
                                                } else {
                                                    try { localStorage.removeItem(PRESET_KEY); } catch {}
                                                }
                                            } catch {}

                                            applyVisibleLayout(next, { widthOverrides: manualColWidthOverrides, silent: true });
                                        } catch {}
                                        setSettingsOpen(false);
                                        setPendingSettings(null);
                                        setPendingPresetIntent(null);
                                        return true;
                                    }}
                                />,
                                document.body
                            )
                            : null}

                        {/* Centered page-size control */}
                        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center gap-2">
                            <div className="relative inline-block">
                                <label className="sr-only" htmlFor="pageSizeSelect">Trades per page</label>
                                <select
                                    id="pageSizeSelect"
                                    value={pageSize}
                                    onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                                    className="appearance-none text-sm rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/90 px-3 h-9 pr-8"
                                >
                                    <option value={30}>30</option>
                                    <option value={60}>60</option>
                                    <option value={100}>100</option>
                                    <option value={200}>200</option>
                                </select>
                                <ChevronDown size={16} className="absolute right-5 top-1/2 transform -translate-y-1/2 text-zinc-500 pointer-events-none" />
                            </div>
                            <span className="text-sm text-zinc-600 dark:text-zinc-400">Trades / Page</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="rounded-[8px] overflow-clip border border-[#27272A] bg-white dark:bg-zinc-900">
                {columnFitMessage ? (
                    <div className="border-b border-amber-200/60 bg-amber-50 px-4 py-2 text-sm text-amber-700 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
                        {columnFitMessage}
                    </div>
                ) : null}
                {sortedTrades.length > 0 && hasInitialTradesLoaded && (
                    <DndContext
                        sensors={settingsOpen ? noSensors : sensors}
                        onDragStart={handleColumnDragStart}
                        onDragMove={handleColumnDragMove}
                        onDragEnd={handleColumnDragEnd}
                        onDragCancel={handleColumnDragCancel}
                    >
                        <div
                            className="sticky z-40 w-full overflow-clip bg-zinc-200 dark:bg-zinc-800"
                            style={{ top: `${stickyTradesHeaderTop}px` }}
                        >
                            <table
                                ref={stickyHeaderTableRef}
                                className="text-sm table-fixed border-separate border-spacing-0"
                                style={{ width: tableWidthPx ? `${tableWidthPx}px` : 'auto', minWidth: tableWidthPx ? `${tableWidthPx}px` : undefined }}
                            >
                                <colgroup>
                                    {orderedVisibleKeys.map((k) => <col key={k} style={colStyle(k)} />)}
                                </colgroup>
                                <thead>
                                    <SortableContext items={orderedVisibleKeys} strategy={horizontalListSortingStrategy}>
                                        <tr className="border-b border-border bg-zinc-200 dark:bg-zinc-800">
                                            {orderedVisibleKeys.map((k) => <SortableHeader key={k} k={k} />)}
                                        </tr>
                                    </SortableContext>
                                </thead>
                            </table>
                        </div>
                        <DragOverlay>
                            {activeColumnId ? (
                                <div style={{ width: dragMeasuredWidthsRef.current[activeColumnId] || 180, opacity: 0.45 }}>
                                    <div className="rounded border border-border shadow-lg bg-white dark:bg-zinc-900 overflow-hidden">
                                        <div className="px-4 py-3 text-center font-bold text-zinc-700 dark:text-zinc-200 border-b border-border">{dragOverlayLabel}</div>
                                        <div className="max-h-[60vh] overflow-y-auto no-scrollbar">
                                            {(overlayRows.length ? overlayRows : pagedTrades).map((trade) => (
                                                <div key={tradeKey(trade)} className="px-4 py-3 text-center text-sm border-b border-border">
                                                    {renderCell(activeColumnId, trade)}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                )}
                <div
                    className={`relative ${isColumnDragging ? 'no-scrollbar' : ''}`}
                    ref={containerRef}
                    data-auto-hidden-columns={responsiveColumnFit.hiddenKeys.join(',')}
                    onScroll={(event) => {
                        if (stickyHeaderTableRef.current) {
                            stickyHeaderTableRef.current.style.transform = `translateX(-${event.currentTarget.scrollLeft}px)`;
                        }
                    }}
                    style={{ overflowX: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' }}
                >
                    {!hasInitialTradesLoaded && trades.length === 0 ? (
                        <div className="p-12 flex flex-col items-center justify-center gap-3">
                            <RefreshCcw className="w-8 h-8 text-blue-500 animate-spin" />
                            <div className="flex flex-col items-center gap-2 text-center">
                                <div className="font-semibold text-[20px] text-zinc-900 dark:text-zinc-100">Loading trades…</div>
                                <div className="text-[10pt] text-zinc-600 dark:text-zinc-400 mt-1">Loading your saved trades before the first table paint.</div>
                            </div>
                        </div>
                    ) : sortedTrades.length === 0 ? (
                        <div className="p-12 flex flex-col items-center justify-center gap-2">
                            <svg width="96" height="80" viewBox="0 0 96 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-24 h-20 text-blue-100">
                                <path d="M48 8C34.745 8 24.5 17.745 24.5 31c0 .99.065 1.964.193 2.92C18.548 35.83 14 41.848 14 48.5 14 58.165 22.335 66.5 32 66.5h32c11.046 0 20-8.954 20-20 0-10.493-8.033-19.078-18.246-19.928A16.5 16.5 0 0 0 48 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M48 32v18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M36 44l12-12 12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <div className="flex flex-col items-center gap-6">
                                <div className="mb-3 text-center">
                                    <div className="font-semibold text-[20px] text-zinc-900 dark:text-zinc-100">No trades yet</div>
                                    <div className="text-[10pt] text-zinc-600 dark:text-zinc-400 mt-2">Import your trading history from a CSV file to get started</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleImportClick}
                                    disabled={importing}
                                    className="inline-flex items-center gap-2 px-5 h-11 rounded-sm bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold hover:from-blue-700 hover:to-blue-600 shadow-lg transition-transform transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Cloud className="w-4 h-4 text-white" />
                                    <span>{importing ? 'Importing…' : 'Import Trades'}</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div ref={tableAreaRef} className="relative w-max min-w-full">
                            <table className="text-sm table-fixed border-separate border-spacing-0" style={{ width: tableWidthPx ? `${tableWidthPx}px` : 'auto', minWidth: tableWidthPx ? `${tableWidthPx}px` : undefined }}>
                                <colgroup>
                                    {orderedVisibleKeys.map((k) => (
                                        <col key={k} style={colStyle(k)} />
                                    ))}
                                </colgroup>
                                <thead>
                                    {selectedTrades.size > 0 && (
                                        <tr className="border-b border-border bg-blue-50 dark:bg-blue-900/30">
                                            <td colSpan={orderedVisibleKeys.length || 1} className="px-4 py-2">
                                                <div className="flex items-center gap-4">
                                                    <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">{selectedTrades.size} trade(s) selected</span>
                                                                            <ActionMenu
                                                                                selectedIds={Array.from(selectedTrades)}
                                                                                onAction={(action) => {
                                                                                    if (action === 'Select All') toggleSelectAll();
                                                                                    else if (action === 'Set Initial Risk') openInitialRiskModalForSelection();
                                                                                    else if (action === 'Merge Trades') setIsMergeOpen(true);
                                                                                    else if (action === 'Split Trade') setIsSplitOpen(true);
                                                                                    else if (action === 'Delete Selected') { setDeleteSelectedError(''); setConfirmDeleteSelectedOpen(true); }
                                                                                    else if (action === 'Delete All') { setDeleteAllError(''); setConfirmDeleteAllOpen(true); }
                                                                                    else console.log(action, Array.from(selectedTrades));
                                                                                }}
                                                                            />
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </thead>
                                <tbody>
                                    {pagedTrades.map((trade) => (
                                        <tr
                                            key={tradeKey(trade)}
                                            ref={(el) => { rowRefs.current[tradeKey(trade)] = el; }}
                                            onMouseEnter={() => setHoveredRowKey(tradeKey(trade))}
                                            onMouseLeave={() => setHoveredRowKey(null)}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => {
                                                openTradeDetails(trade);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    openTradeDetails(trade);
                                                }
                                            }}
                                            className={`relative cursor-pointer border-b border-border hover:bg-zinc-50 dark:hover:bg-zinc-800/50 ${isOpenLikeTradeForRender(trade) ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''}`}
                                        >
                                            {orderedVisibleKeys.map((k) => {
                                                const tdOnClick = k === 'initialRisk'
                                                    ? (e: ReactMouseEvent<HTMLTableCellElement>) => { e.stopPropagation(); openInitialRiskModalForTrade(trade); }
                                                    : k === 'tags'
                                                        ? (e: ReactMouseEvent<HTMLTableCellElement>) => {
                                                            e.stopPropagation();
                                                            setTagModalTargetKey(tradeKey(trade));
                                                            setEditingTag(null);
                                                            setIsTagsModalOpen(true);
                                                        }
                                                    : undefined;

                                                return (
                                                    <td key={k} onClick={tdOnClick} style={colStyle(k)} className={`px-4 py-3 text-center font-light border-r border-b border-border last:border-r-0 bg-zinc-50 dark:bg-blue-900/10 ${k === 'notes' ? 'overflow-hidden' : ''} ${k === 'initialRisk' ? 'relative group' : (k === 'opened' ? 'relative' : '')}`}>
                                                        {renderCell(k, trade)}
                                                    </td>
                                                );
                                            })}
                                            {Boolean((trade as any).isReversal) && reversalDividerLeftPx != null && (
                                                <span
                                                    className="pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 inline-flex items-center justify-center px-1 py-0.5 rounded text-[10px] leading-none font-medium bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                                                    style={{ left: `${reversalDividerLeftPx}px` }}
                                                >
                                                    R
                                                </span>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {isColumnDragging && dropIndicator ? (
                                <div
                                    className="pointer-events-none absolute inset-y-0"
                                    style={{
                                        left: dropIndicator.left,
                                        width: 2,
                                        background: 'rgba(59,130,246,0.9)',
                                        transition: 'left 120ms linear',
                                        zIndex: 60,
                                    }}
                                />
                            ) : null}
                        </div>
                    )}

                    <div className="relative w-full border-t border-border bg-[#daedfd] dark:bg-zinc-800/50">
                        <div className="relative px-8 py-8">
                            <div className="absolute left-8 top-1/2 transform -translate-y-1/2 text-base">
                                <div className="flex items-center gap-3">
                                    <span className="text-zinc-700 dark:text-white font-semibold">
                                        {marketFilter === 'ALL'
                                            ? (activeView === 'Net' ? 'Total Profit/Loss (Net):' : 'Total Profit/Loss (Gross):')
                                            : `${marketFilter === 'STOCKS' ? 'Stocks' : marketFilter === 'FUTURES' ? 'Futures' : marketFilter === 'FOREX' ? 'Forex' : 'Crypto'} — ${activeView === 'Net' ? 'Total (Net):' : 'Total (Gross):'}`}
                                    </span>
                                    <span className={`${displayedTotal >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'} font-semibold ml-0`}>
                                        {fmtMoney(displayedTotal)}
                                        {historyStatus.phase !== 'complete' && (
                                            <span className="text-xs font-normal text-zinc-500 dark:text-zinc-400 ml-1.5">(syncing...)</span>
                                        )}
                                    </span>
                                </div>
                            </div>
                            <div className="absolute right-8 top-1/2 transform -translate-y-1/2 text-base text-zinc-600 dark:text-zinc-400">
                                {(() => {
                                    const totalCount = filteredTrades.length;
                                    const start = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
                                    const end = Math.min(page * pageSize, totalCount);
                                    const isFilterActive = marketFilter !== 'ALL'
                                        || liveFilters.timeframe !== 'alltime'
                                        || Boolean(liveFilters.fromDate || liveFilters.toDate || liveFilters.searchTerm.trim())
                                        || liveFilters.sideFilter !== 'All'
                                        || (Boolean(selectedTradingAccountId) && totalCount < trades.length);
                                    const suffix = isFilterActive ? 'matching trades' : 'trades';
                                    if (historyStatus.phase === 'complete') {
                                        return `Showing ${start}–${end} of ${totalCount.toLocaleString()} ${suffix}`;
                                    }
                                    return `Showing ${start}–${end} of ${totalCount.toLocaleString()} loaded ${suffix} (syncing...)`;
                                })()}
                            </div>
                            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center gap-4 w-96 justify-center">
                                <button
                                    type="button"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="px-4 h-9 rounded-md border border-border bg-white text-sm text-zinc-700 disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-700 dark:border-[#3b3b3f] dark:bg-zinc-900 dark:text-zinc-200"
                                >
                                    Prev
                                </button>
                                <div className="text-sm text-zinc-600 dark:text-zinc-400">Page {page} / {totalPages}</div>
                                <button
                                    type="button"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="px-4 h-9 rounded-md border border-border bg-white text-sm text-zinc-700 disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-700 dark:border-[#3b3b3f] dark:bg-zinc-900 dark:text-zinc-200"
                                >
                                    Next
                                </button>
                            </div>
                            {currentPageHasManualEntry && <>
                                <div className="absolute left-[calc(50%+4.5rem)] right-60 top-1/2 hidden -translate-y-1/2 justify-center xl:flex">
                                    <span className="inline-flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-zinc-600 dark:text-zinc-400" aria-label="M means Manual Entry">
                                        <span aria-hidden="true" className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border border-sky-400/50 bg-sky-500/15 text-[10px] font-bold leading-none text-sky-700 dark:text-sky-300">M</span>
                                        <span>= Manual Entry</span>
                                    </span>
                                </div>
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 xl:hidden">
                                    <span className="inline-flex shrink-0 items-center gap-1.5 text-[13px] font-medium text-zinc-600 dark:text-zinc-400" aria-label="M means Manual Entry">
                                        <span aria-hidden="true" className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border border-sky-400/50 bg-sky-500/15 text-[10px] font-bold leading-none text-sky-700 dark:text-sky-300">M</span>
                                        <span>= Manual Entry</span>
                                    </span>
                                </div>
                            </>}
                        </div>
                    </div>

                                {confirmDeleteSelectedOpen && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center">
                                        <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmDeleteSelectedOpen(false)} />
                                        <div className="relative bg-white dark:bg-zinc-900 rounded-lg p-6 w-[420px] shadow-lg border border-zinc-200 dark:border-zinc-800">
                                            <h3 className="text-lg font-semibold">Confirm Delete</h3>
                                            <p className="mt-3 text-sm text-zinc-700 dark:text-zinc-300">Are you sure you want to delete <span className="font-semibold">{selectedTrades.size}</span> selected {selectedTrades.size === 1 ? 'trade' : 'trades'}?</p>
                                            {deleteSelectedError && <p role="alert" className="mt-3 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">{deleteSelectedError}</p>}
                                            <div className="mt-5 flex justify-end gap-3">
                                                <button type="button" disabled={deletingSelectedTrades} className="px-4 h-9 rounded-md text-sm border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 disabled:opacity-50" onClick={() => setConfirmDeleteSelectedOpen(false)}>No</button>
                                                <button type="button" disabled={deletingSelectedTrades} className="px-4 h-9 rounded-md text-sm bg-red-600 text-white hover:bg-red-700 disabled:cursor-wait disabled:opacity-60" onClick={() => void confirmDeleteSelectedTrades()}>{deletingSelectedTrades ? 'Deleting…' : 'Yes'}</button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {confirmDeleteAllOpen && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center">
                                        <div className="absolute inset-0 bg-black/40" onClick={() => { if (!deletingAllTrades) setConfirmDeleteAllOpen(false); }} />
                                        <div className="relative bg-white dark:bg-zinc-900 rounded-lg p-6 w-[420px] shadow-lg border border-zinc-200 dark:border-zinc-800">
                                            <h3 className="text-lg font-semibold">Confirm Delete All</h3>
                                            <p className="mt-3 text-sm text-zinc-700 dark:text-zinc-300">Are you sure you want to delete <span className="font-semibold">ALL</span> trades? This cannot be undone.</p>
                                            {deleteAllError && <p role="alert" className="mt-3 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">{deleteAllError}</p>}
                                            <div className="mt-5 flex justify-end gap-3">
                                                <button type="button" disabled={deletingAllTrades} className="px-4 h-9 rounded-md text-sm border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 disabled:opacity-50" onClick={() => setConfirmDeleteAllOpen(false)}>No</button>
                                                <button type="button" disabled={deletingAllTrades} className="px-4 h-9 rounded-md text-sm bg-red-600 text-white hover:bg-red-700 disabled:cursor-wait disabled:opacity-60" onClick={() => void confirmDeleteAllTrades()}>{deletingAllTrades ? 'Deleting…' : 'Yes'}</button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {
                                    /* Choose tags/handlers based on whether the modal targets a single trade */
                                }
                                {(() => {
                                    const modalTags = tagModalTargetKey
                                        ? (groupedTrades.find(g => String((g as any).storageId ?? g.id ?? `${g.symbol}-${g.timestamp}`) === tagModalTargetKey)?.tags ?? [])
                                        : selectedTags;
                                    const handleAdd = (tag: string) => {
                                        if (tagModalTargetKey) applyTagToTrade(tagModalTargetKey, tag);
                                        else applyTagToSelected(tag);
                                    };
                                    const handleRemove = (tag: string) => {
                                        if (tagModalTargetKey) removeTagFromTrade(tagModalTargetKey, tag);
                                        else removeTagFromSelected(tag);
                                    };
                                    const handleRename = (original: string, next: string) => {
                                        if (tagModalTargetKey) renameTagForTrade(tagModalTargetKey, original, next);
                                    };
                                    return (
                                        <TagsModal
                                            isOpen={isTagsModalOpen}
                                            onClose={() => { setIsTagsModalOpen(false); setTagModalTargetKey(null); setEditingTag(null); }}
                                            tags={modalTags}
                                            onAdd={handleAdd}
                                            onRemove={handleRemove}
                                            editingTag={editingTag}
                                            onRename={handleRename}
                                        />
                                    );
                                })()}
                                {/* Delete Tag Modal */}
                                {isDeleteTagOpen && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center">
                                        <div className="absolute inset-0 bg-black/40" onClick={() => setIsDeleteTagOpen(false)} />
                                        <div className="relative bg-white dark:bg-zinc-900 rounded-lg p-6 w-[420px] shadow-lg border border-zinc-200 dark:border-zinc-800">
                                            <h3 className="text-lg font-semibold">Remove Tag from Selected</h3>
                                            <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">Remove a tag from all selected trades.</p>
                                            <div className="mt-4">
                                                <select aria-label="Choose tag to remove" className="w-full p-2 border rounded-md bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800" onChange={(e) => { const v = e.target.value; if (v) removeTagFromSelected(v); setIsDeleteTagOpen(false); }}>
                                                    <option value="">-- choose tag --</option>
                                                    {selectedTags.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                            </div>
                                            <div className="mt-5 flex justify-end gap-3">
                                                <button className="px-4 h-9 rounded-md text-sm border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900" onClick={() => setIsDeleteTagOpen(false)}>Cancel</button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Set Initial Risk Modal (single-trade, compact) */}
                                {riskModalTrade && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center">
                                        <div className="absolute inset-0 bg-[#030712]/70 backdrop-blur-sm" onClick={() => setRiskModalTrade(null)} />
                                        <div className="relative w-[420px] overflow-hidden rounded-[12px] border border-zinc-200 bg-white text-zinc-900 shadow-lg dark:border-zinc-800 dark:bg-zinc-900 dark:text-white">
                                            <div className="flex items-start justify-between border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
                                                <div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-500/12 text-amber-400 border border-amber-400/20">
                                                            <AlertCircle className="h-4 w-4" />
                                                        </span>
                                                        <h3 className="text-[18px] font-semibold tracking-[0.01em] text-zinc-900 dark:text-white">Set Initial Risk</h3>
                                                    </div>
                                                    <p className="mt-2 text-sm text-zinc-500 dark:text-white/55">
                                                        Trade {getTradeDisplayId(riskModalTrade)} • {String((riskModalTrade as any)?.symbol ?? '—')}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setRiskModalTrade(null)}
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-900 dark:text-white/55 dark:hover:bg-white/6 dark:hover:text-white"
                                                    aria-label="Close initial risk modal"
                                                >
                                                    <X className="h-4 w-4" />
                                                </button>
                                            </div>

                                            <div className="px-5 py-6">
                                                <p className="text-[15px] leading-6 text-zinc-600 dark:text-white/72">Required to calculate R-Multiple metrics.</p>

                                                <div className="mt-4 flex gap-1 rounded-md border border-zinc-200 bg-zinc-50 p-1 dark:border-zinc-800 dark:bg-zinc-900">
                                                    <button
                                                        type="button"
                                                        onClick={() => setRiskModalMethod('stopPrice')}
                                                        className={`flex-1 h-9 rounded-md border text-sm font-semibold transition ${riskModalMethod === 'stopPrice' ? 'bg-blue-600 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03)] border border-gray-300/20' : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-100 dark:border-gray-300/10 dark:bg-zinc-800 dark:text-white/60 dark:hover:bg-zinc-700'}`}
                                                    >
                                                        Stop Price
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setRiskModalMethod('amount')}
                                                        className={`flex-1 h-9 rounded-md border text-sm font-semibold transition ${riskModalMethod === 'amount' ? 'bg-blue-600 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.03)] border border-gray-300/20' : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-100 dark:border-gray-300/10 dark:bg-zinc-800 dark:text-white/60 dark:hover:bg-zinc-700'}`}
                                                    >
                                                        Risk Amount ($)
                                                    </button>
                                                </div>

                                                {riskModalMethod === 'amount' ? (
                                                    <div className="mt-6 space-y-3">
                                                        <label className="block text-sm font-semibold text-zinc-700 dark:text-white/78">Total Risk Amount (USD)</label>
                                                        <div className="flex h-10 items-center rounded-lg border border-zinc-300 bg-white px-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] dark:border-zinc-800 dark:bg-zinc-950">
                                                            <span className="mr-3 text-sm leading-none text-zinc-400 dark:text-white/32">$</span>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={riskAmountInput}
                                                                onChange={(e) => setRiskAmountInput(e.target.value)}
                                                                className="h-full w-full bg-transparent text-sm font-medium tracking-[0.01em] text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-white dark:placeholder:text-white/20"
                                                                placeholder="0.00"
                                                            />
                                                        </div>
                                                        {!canSaveRiskAmount && riskAmountInput.trim() !== '' ? (
                                                            <p className="text-xs text-red-600 dark:text-[#ff8b8b]">Enter a numeric value greater than 0.</p>
                                                        ) : null}
                                                    </div>
                                                ) : (
                                                    <div className="mt-6 space-y-3">
                                                        <label className="block text-sm font-semibold text-zinc-700 dark:text-white/78">Stop Price</label>
                                                        <div className="flex h-10 items-center rounded-lg border border-zinc-300 bg-white px-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] dark:border-zinc-800 dark:bg-zinc-950">
                                                            <input
                                                                type="number"
                                                                step="0.0001"
                                                                value={stopPriceInput}
                                                                onChange={(e) => setStopPriceInput(e.target.value)}
                                                                className="h-full w-full bg-transparent text-sm font-medium tracking-[0.01em] text-zinc-900 outline-none placeholder:text-zinc-400 dark:text-white dark:placeholder:text-white/20"
                                                                placeholder="0.00"
                                                            />
                                                        </div>

                                                        {stopRiskPreview.basis ? (
                                                            <p className="text-xs leading-5 text-zinc-500 dark:text-white/52">
                                                                Side: {stopRiskPreview.basis.side === 'buy' ? 'Long' : 'Short'} • Entry: {formatTradePriceValue(stopRiskPreview.basis.entryPrice, riskModalTrade)} • Opening Qty: {formatTradeSizeValue(stopRiskPreview.basis.quantity)}
                                                                {stopRiskPreview.assetClass === 'FUTURES' && stopRiskPreview.multiplier != null ? ` • Multiplier: ${stopRiskPreview.multiplier}` : ''}
                                                            </p>
                                                        ) : null}

                                                        {stopRiskPreview.blockedReason ? (
                                                            <p className="text-xs leading-5 text-amber-600 dark:text-amber-300">{stopRiskPreview.blockedReason}</p>
                                                        ) : null}

                                                        {!stopRiskPreview.blockedReason && stopRiskPreview.result && stopRiskPreview.result.ok ? (
                                                            <div className="rounded-xl border border-emerald-400/15 bg-emerald-500/8 px-4 py-3">
                                                                <p className="text-xs uppercase tracking-[0.08em] text-emerald-700 dark:text-emerald-300/75">Calculated Initial Risk</p>
                                                                <p className="mt-1 text-lg font-semibold text-emerald-700 dark:text-emerald-300">{fmtMoney(stopRiskPreview.result.totalRisk)}</p>
                                                            </div>
                                                        ) : null}

                                                        {!stopRiskPreview.blockedReason && stopRiskPreview.result && !stopRiskPreview.result.ok ? (
                                                            <p className="text-xs leading-5 text-red-600 dark:text-[#ff8b8b]">{stopRiskPreview.result.reason}</p>
                                                        ) : null}
                                                    </div>
                                                )}

                                                <button
                                                    className="mt-6 flex h-10 w-full items-center justify-center gap-2 rounded-md bg-gradient-to-r from-blue-600 to-blue-500 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-45"
                                                    disabled={riskModalMethod === 'amount' ? !canSaveRiskAmount : !canSaveStopPrice}
                                                    onClick={saveInitialRiskFromModal}
                                                >
                                                    <Check className="h-4 w-4" />
                                                    <span>Save &amp; Calculate</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Share / Unshare Modals */}
                                {/* Share Trades removed from menu — modal intentionally omitted */}

                                {/* Unshare Trades removed from menu — modal intentionally omitted */}

                                {/* Merge Modal */}
                                {isMergeOpen && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center">
                                        <div className="absolute inset-0 bg-black/40" onClick={() => setIsMergeOpen(false)} />
                                        <div className="relative bg-white dark:bg-zinc-900 rounded-lg p-6 w-[480px] shadow-lg border border-zinc-200 dark:border-zinc-800">
                                            <h3 className="text-lg font-semibold">Merge Trades</h3>
                                            <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">This will merge selected trades into a single trade (first selected becomes the merged record).</p>
                                            <div className="mt-5 flex justify-end gap-3">
                                                <button className="px-4 h-9 rounded-md text-sm border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900" onClick={() => setIsMergeOpen(false)}>Cancel</button>
                                                <button className="px-4 h-9 rounded-md text-sm bg-blue-600 text-white" onClick={() => { mergeSelectedTrades(); setIsMergeOpen(false); }}>Merge</button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Split Modal */}
                                {isSplitOpen && (
                                    <SplitTradesModal
                                            totalShares={totalSelectedShares}
                                            selectedTradesCount={selectedTrades.size}
                                            onClose={() => setIsSplitOpen(false)}
                                            onConfirm={({ allocations }) => { applyManualSplit(allocations); setIsSplitOpen(false); }}
                                        />
                                )}

                </div>
            </div>
        </div>
    );
}
export default TradesPage;
